// src/pages/POs.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";
import * as XLSX from "xlsx";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import DateRangeFilter from "../components/common/DateRangeFilter";
import ManualCloseFollowupDialog, {
  manualCloseReasonError,
} from "../components/po/ManualCloseFollowupDialog";
import { filterRowsByAccess, getUserAccessInfo, ALL_ACCESS_TOKEN } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { shouldApplyMrpPoBaseFilter } from "../utils/mrpDrilldown";
import { saveImportedPoDetails } from "../utils/poDetailsImport";
import {
  describeCorrectionPreview,
  getManualCloseDefaultStatus,
} from "../utils/cancelFollowupCorrection";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Info,
  RotateCw,
  Stamp,
  X,
} from "lucide-react";

const STORAGE_DATA_KEY = "pp_po_tracker_dataset";
const STORAGE_META_KEY = "pp_po_tracker_meta";
const STORAGE_WIDTHS_KEY = "pp_po_tracker_column_widths";
const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const PRICING_VALIDATE_ENDPOINT = "/api/pricing/validate";
const PRICING_OVERRIDE_ENDPOINT = "/api/pricing/override";
const PRICING_OVERRIDE_REVOKE_ENDPOINT = "/api/pricing/override/revoke";
const CANCEL_FOLLOWUP_UPDATE_ENDPOINT = "/api/po/cancel-followups/update";
const CANCEL_FOLLOWUP_ILS_UPDATE_ENDPOINT = "/api/po/cancel-followups/ils-update";
const CANCEL_FOLLOWUP_ILS_CANCEL_ENDPOINT = "/api/po/cancel-followups/ils-cancel";
const CANCEL_FOLLOWUP_CORRECTION_PREVIEW_ENDPOINT = "/api/po/cancel-followups/correction-preview";
const CANCEL_FOLLOWUP_CORRECTION_ACTIVATE_ENDPOINT = "/api/po/cancel-followups/correction-activate";
const CANCEL_FOLLOWUP_CORRECTION_ORCHESTRATE_ENDPOINT = "/api/po/cancel-followups/correction-ils-orchestrate";
const PO_DETAILS_LABEL = "PO Details";
const PREFERRED_SHEET_NAMES = ["PO Details", "PO Tracker"];
const PAGE_OPTIONS = [25, 50, 100, "All"];
const MIN_COL_WIDTH = 80;
const MAX_COL_WIDTH = 300;
const DEFAULT_COL_WIDTH = 140;
const SELECT_COLUMN_KEY = "__row_select__";
const SELECT_COLUMN_DEFAULT_WIDTH = 56;
const EXPORT_SHEET_NAME = "PO Details";
const TSCP_TEMPLATE_BUTTON_LABEL = "TSCP Template";
const TSCP_TEMPLATE_FILENAME_PREFIX = "TSCP Template";

const STATUS_LABEL = "Status";
const ON_HOLD_LABEL = "On Hold";
const FIRST_UPDATE_LABEL = "FirstUpdatePST";
const LAST_UPDATE_LABEL = "LastUpdatePST";
const CUST_CODE_LABEL = "Cust Code";
const TSCP_LABEL = "TSCP";
const PRODUCT_CODE_LABEL = "Product Code";
const LINE_STATUS_LABEL = "Line status";
const CANCELLED_DATE_LABEL = "Cancelled Date";
const CANCEL_FOLLOWUP_STATUS_LABEL = "Cancel Follow-up";
const CANCEL_FOLLOWUP_OWNER_LABEL = "Cancel Follow-up Owner";
const CANCEL_OUTBOUND_CODE_LABEL = "Cancel Outbound Code";
const CANCEL_LOAD_ID_LABEL = "Cancel Load ID";
const CANCEL_WMS_LABEL = "Cancel WMS";
const CANCEL_SHIPPED_DATE_LABEL = "Cancel Shipped Date";
const CANCEL_CARRIER_PU_DATE_LABEL = "Cancel Carrier PU Date";
const WHSE_CODE_LABEL = "WHSE Code";
const SHIP_POINT_LABEL = "Ship Point";
const STATUS_COLUMN = { key: "status", label: STATUS_LABEL, type: "text" };
const ON_HOLD_COLUMN = { key: "__onHold", label: ON_HOLD_LABEL, type: "text" };
const FIRST_UPDATE_COLUMN = { key: "firstupdatedt", label: FIRST_UPDATE_LABEL, type: "text" };
const LAST_UPDATE_COLUMN = { key: "lastupdatedt", label: LAST_UPDATE_LABEL, type: "text" };
const CUST_CODE_COLUMN = { key: "cust-code", label: CUST_CODE_LABEL, type: "text" };
const TSCP_COLUMN = { key: "__tscp", label: TSCP_LABEL, type: "text" };
const AUDIT_COLUMNS = [STATUS_COLUMN, ON_HOLD_COLUMN, FIRST_UPDATE_COLUMN, LAST_UPDATE_COLUMN];
const LEADING_DERIVED_COLUMNS = [TSCP_COLUMN];
const TRAILING_DERIVED_COLUMNS = [...AUDIT_COLUMNS, CUST_CODE_COLUMN];
const DERIVED_COLUMNS = [...LEADING_DERIVED_COLUMNS, ...TRAILING_DERIVED_COLUMNS];
const DERIVED_KEY_SET = new Set(DERIVED_COLUMNS.map((col) => col.key));
const DERIVED_LABEL_MAP = new Map(DERIVED_COLUMNS.map((column) => [column.key, column.label]));
const STATUS_KEY = STATUS_COLUMN.key;
const ON_HOLD_KEY = ON_HOLD_COLUMN.key;
const FIRST_UPDATE_KEY = FIRST_UPDATE_COLUMN.key;
const LAST_UPDATE_KEY = LAST_UPDATE_COLUMN.key;
const CUST_CODE_KEY = CUST_CODE_COLUMN.key;
const TSCP_KEY = TSCP_COLUMN.key;
const STATUS_NOT_CLOSED_TOKEN = "__not_closed__";
const BLANK_SUPPLIER_TOKEN = "__blank_supplier__";
const PO_NUMBER_LABEL = "PO#";
const STATIC_MULTI_SELECT_OPTIONS = {
  [ON_HOLD_KEY]: [
    { value: "yes", label: "On Hold" },
    { value: "", label: "Blank" },
  ],
  [TSCP_KEY]: [
    { value: "yes", label: "TSCP" },
    { value: "", label: "Blank" },
  ],
};

const numberFormatter = new Intl.NumberFormat();

const formatPrecheckNumber = (value) => {
  if (value === null || value === undefined) return "0";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }
  return numberFormatter.format(numericValue);
};

const formatPrecheckTimestamp = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatAgeSeconds = (value) => {
  if (!Number.isFinite(value) || value < 0) return "";
  if (value < 60) return `${Math.round(value)}s`;
  if (value < 3600) {
    const minutes = Math.floor(value / 60);
    const seconds = Math.round(value % 60);
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const formatPricingNumber = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return numericValue.toFixed(2);
};

const formatPricingRange = (from, to) => {
  const start = from ? String(from) : "Unknown";
  const end = to ? String(to) : "No expiration";
  return `${start} -> ${end}`;
};

const buildPricingTooltip = (result) => {
  if (!result) return "";
  const lines = [
    `Expected price: ${formatPricingNumber(result.expected_price)}`,
    `Actual price: ${formatPricingNumber(result.actual_price)}`,
    `Delta: ${formatPricingNumber(result.delta)}`,
    `Pricing effective: ${formatPricingRange(result.pricing_effective_from, result.pricing_effective_to)}`,
  ];
  if (result.reason_code) {
    lines.push(`Reason: ${result.reason_code}`);
  }
  if (result.override_applied) {
    lines.push("Override applied.");
  }
  return lines.join("\n");
};

const isPricingBlockedResult = (result) =>
  Boolean(result && String(result.status || "").toUpperCase() === "MISMATCH" && !result.override_applied);

const normalizePricingFlag = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  const text = String(value).trim().toLowerCase();
  if (!text) return 0;
  if (text === "yes" || text === "true" || text === "y" || text === "1") return 1;
  if (text === "no" || text === "false" || text === "0") return 0;
  const parsed = Number.parseInt(text, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isPricingEligibleRow = (row) => {
  if (!row) return false;
  const statusValue = String(row[STATUS_KEY] ?? row.status ?? "").trim().toLowerCase();
  if (statusValue === "closed") return false;
  const tscpValue = row[TSCP_KEY] ?? row.tscp ?? row.TSCP;
  const tscpFlag = normalizePricingFlag(tscpValue);
  return tscpFlag === 0;
};

const extractPoDetailId = (record) => {
  if (!record) return null;
  const toInt = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number" && Number.isFinite(value)) {
      return Number.isInteger(value) ? value : Math.trunc(value);
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^\d+$/.test(trimmed)) return Number.parseInt(trimmed, 10);
    }
    return null;
  };

  const directCandidates = [
    record.po_detail_id,
    record.poDetailId,
    record.id,
    record.po_detail,
    record.poDetail,
  ];
  for (const candidate of directCandidates) {
    const parsed = toInt(candidate);
    if (parsed !== null) return parsed;
  }

  const rawId = String(record._id || "");
  if (!rawId.includes("::")) return null;
  const lastSegment = rawId.split("::").pop() || "";
  const digitGroups = lastSegment.match(/\d+/g);
  if (!digitGroups || digitGroups.length === 0) return null;
  const parsed = Number.parseInt(digitGroups[digitGroups.length - 1], 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const STATUS_ROW_CLASSES = {
  New: "bg-green-200",
  Closed: "bg-amber-200",
};
const CHANGED_CELL_CLASS = "bg-red-300 text-red-900 font-semibold";

const canonicalizeHeaderLabel = (value) =>
  (value === undefined || value === null ? "" : String(value))
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const STICKY_HEADER_SET = new Set([
  canonicalizeHeaderLabel(TSCP_LABEL),
  canonicalizeHeaderLabel(PO_NUMBER_LABEL),
  canonicalizeHeaderLabel(STATUS_LABEL),
]);
const COLUMN_WIDTH_HINTS = new Map([
  [canonicalizeHeaderLabel(PO_NUMBER_LABEL), 190],
  [canonicalizeHeaderLabel("2-Total VNPK order qty"), 90],
]);
const DEFAULT_VISIBLE_COLUMN_LABELS = [
  TSCP_LABEL,
  PO_NUMBER_LABEL,
  STATUS_LABEL,
  ON_HOLD_LABEL,
  LAST_UPDATE_LABEL,
  CUST_CODE_LABEL,
  PRODUCT_CODE_LABEL,
  LINE_STATUS_LABEL,
  CANCELLED_DATE_LABEL,
  CANCEL_FOLLOWUP_STATUS_LABEL,
  CANCEL_FOLLOWUP_OWNER_LABEL,
  CANCEL_OUTBOUND_CODE_LABEL,
  CANCEL_LOAD_ID_LABEL,
  CANCEL_WMS_LABEL,
  CANCEL_SHIPPED_DATE_LABEL,
  CANCEL_CARRIER_PU_DATE_LABEL,
  "MABD",
  "Create Date",
  "Event Code",
  "Walmart item No.",
  "VNPK cost",
  "2-Total VNPK order qty",
];
const DEFAULT_VISIBLE_LABELS = new Set(DEFAULT_VISIBLE_COLUMN_LABELS.map(canonicalizeHeaderLabel));
const HEADER_LABEL_OVERRIDES = new Map([
  [canonicalizeHeaderLabel("2-Total VNPK order qty"), "2-Total VNPK\norder qty"],
]);

const renderHeaderLabel = (header) => {
  const canonical = canonicalizeHeaderLabel(header.label);
  const override = HEADER_LABEL_OVERRIDES.get(canonical);
  if (override) {
    return <span className="whitespace-pre-line leading-tight">{override}</span>;
  }
  if (canonical === canonicalizeHeaderLabel(PO_NUMBER_LABEL)) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span>{PO_NUMBER_LABEL}</span>
        <span
          className="text-indigo-200/70 hover:text-indigo-100"
          title="Tip: Drag-select PO# and copy — paste will contain PO numbers only."
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </span>
    );
  }
  return header.label;
};

const FORCE_TEXT_LABELS = new Set(
  [
    "Supplier number",
    "Destination node",
    "Destination node contact number",
    "Destination node address: zipcode",
    "Walmart item No.",
    PRODUCT_CODE_LABEL,
    CUST_CODE_LABEL,
    TSCP_LABEL,
    WHSE_CODE_LABEL,
    SHIP_POINT_LABEL,
  ].map(canonicalizeHeaderLabel)
);

const FORCE_DATE_LABELS = new Set(
  ["DNSA", "DNSB", "MABD", "Original MABD", "Create date", CANCELLED_DATE_LABEL].map(
    canonicalizeHeaderLabel
  )
);
const FORCE_TYPE_MAP = new Map();
FORCE_TEXT_LABELS.forEach((label) => FORCE_TYPE_MAP.set(label, "text"));
FORCE_DATE_LABELS.forEach((label) => FORCE_TYPE_MAP.set(label, "date"));
const MULTI_SELECT_LABELS = new Set(
  [
    STATUS_LABEL,
    ON_HOLD_LABEL,
    TSCP_LABEL,
    CANCEL_FOLLOWUP_STATUS_LABEL,
    LINE_STATUS_LABEL,
    "Event code",
    "Item description",
    "Walmart item No.",
    CUST_CODE_LABEL,
    PRODUCT_CODE_LABEL,
    WHSE_CODE_LABEL,
    SHIP_POINT_LABEL,
  ].map(canonicalizeHeaderLabel)
);
const MULTI_VALUE_TEXT_LABELS = new Set([canonicalizeHeaderLabel(PO_NUMBER_LABEL)]);
const FILENAME_DATE_REGEX = /_(\d{1,2})(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(\d{4})_/i;
const FILENAME_MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};
const VERSION_MS_PER_DAY = 24 * 60 * 60 * 1000;
const VERSION_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const EXPECTED_PO_HEADERS = [
  "PO#",
  "Supply chain status",
  "Supplier number",
  "Supplier description",
  "Freight",
  "MABD",
  "Original MABD",
  "Creator",
  "Create date",
  "DNSA",
  "DNSB",
  "DNRB",
  "Destination node",
  "Event code",
  "Entry type",
  "Total VNPK order qty",
  "Total WHPK order qty",
  "PO total each order qty",
  "Total unit cost",
  "Distribution",
  "Promo ID",
  "Tot. VNPK rec qty",
  "Tot. WHPK rec qty",
  "Order type",
  "Departments",
  "Reason",
  "Channel method",
  "Buyer ID",
  "Total cube",
  "PO total weight (lbs)",
  "Destination node contact name",
  "Destination node contact number",
  "Destination node address:  state, city",
  "Destination node address:  zipcode",
  "Destination node division",
  "PO type",
  "Assortment indicator",
  "OMS status",
  "Item description",
  "Walmart item No.",
  "Line number",
  "Tot. ordered weight (lbs)",
  "Total VNPK order qty",
  "Total WHPK order qty",
  "VNPK cost",
  "VNPK order cost",
  "Line VNPK rec qty",
  "Line WHPK rec qty",
  "Stores",
  "Line status",
];

const SUPPLIER_NUMBER_LABEL = "Supplier number";
const SUPPLIER_NUMBER_CANON = canonicalizeHeaderLabel(SUPPLIER_NUMBER_LABEL);

const DUPLICATE_HEADER_RENAMES = {
  "total vnpk order qty": "2-Total VNPK order qty",
  "total whpk order qty": "2-Total WHPK order qty",
};

const FILTER_MODES = {
  text: "text",
  date: "date",
  multiSelect: "multiSelect",
  multiText: "multiText",
};

const getFilterModeForHeader = (header) => {
  if (!header) return FILTER_MODES.text;
  const canonical = canonicalizeHeaderLabel(header.label);
  if (header.type === "date") return FILTER_MODES.date;
  if (MULTI_SELECT_LABELS.has(canonical)) return FILTER_MODES.multiSelect;
  if (MULTI_VALUE_TEXT_LABELS.has(canonical)) return FILTER_MODES.multiText;
  return FILTER_MODES.text;
};

const normaliseTextValue = (value) => String(value ?? "").trim().toLowerCase();

const parseMultiValueQuery = (value) =>
  String(value || "")
    .split(/[\s,]+/)
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

const parsePoFilterTokens = (value) => {
  const tokens = String(value || "")
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  const seen = new Set();
  const ordered = [];
  tokens.forEach((token) => {
    const normalized = token.toLowerCase();
    if (seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push({ raw: token, normalized });
  });
  return ordered;
};

const normalizeDrilldownIsoDate = (value) => {
  const text = String(value || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
};

const DRILLDOWN_PO_DATE_FIELD_TO_LABEL = {
  create_date: "Create Date",
  mabd: "MABD",
};

const parsePoDrilldownParams = (searchText = "") => {
  const params = new URLSearchParams(searchText);
  const dateFieldRaw = String(params.get("date_field") || "").trim().toLowerCase();
  const poBaseFilterToken = String(params.get("po_base_filter") || "").trim().toLowerCase();
  const walmartItemNo = String(params.get("walmart_item_no") || "").trim();
  const whseCode = String(params.get("whse_code") || "").trim();
  const shipPoint = String(params.get("ship_point") || "").trim();
  const dateFrom = normalizeDrilldownIsoDate(params.get("date_from"));
  const dateTo = normalizeDrilldownIsoDate(params.get("date_to"));
  const dateField = Object.prototype.hasOwnProperty.call(DRILLDOWN_PO_DATE_FIELD_TO_LABEL, dateFieldRaw)
    ? dateFieldRaw
    : "";
  if (!dateField && !walmartItemNo && !whseCode && !shipPoint) return null;
  return {
    dateField,
    poBaseFilterToken,
    dateFrom,
    dateTo,
    walmartItemNo,
    whseCode,
    shipPoint,
  };
};
const parseFilenameVersionDate = (filename = "") => {
  if (!filename) return null;
  const match = filename.match(FILENAME_DATE_REGEX);
  if (!match) return null;
  const day = Number(match[1]);
  const monthKey = match[2]?.toLowerCase();
  const year = Number(match[3]);
  if (!Number.isFinite(day) || !Number.isFinite(year)) return null;
  if (!Object.prototype.hasOwnProperty.call(FILENAME_MONTH_INDEX, monthKey)) return null;
  const date = new Date(Date.UTC(year, FILENAME_MONTH_INDEX[monthKey], day));
  return Number.isNaN(date.getTime()) ? null : date;
};
const normalizeVersionDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};
const diffVersionDates = (incoming, current) => {
  const normalizedIncoming = normalizeVersionDate(incoming);
  const normalizedCurrent = normalizeVersionDate(current);
  if (!normalizedIncoming || !normalizedCurrent) return null;
  const diff = normalizedIncoming.getTime() - normalizedCurrent.getTime();
  return Math.round(diff / VERSION_MS_PER_DAY);
};
const formatVersionDate = (date) => {
  const normalized = normalizeVersionDate(date);
  if (!normalized) return "";
  return VERSION_DATE_FORMATTER.format(normalized);
};

const DATE_LABEL_RE = /(date|ship|eta|etd|arrive|mabd|dnrb|delivery)/i;
const ALWAYS_TEXT_RE = /(po#|po no|overdue)/i;
const ISO_DATE_RE = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/;
const SHORT_DATE_RE = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s*$/;

const normaliseHeaderLabel = (value) => (value === undefined || value === null ? "" : String(value).trim());

const validateHeaderRow = (headerRow = []) => {
  const normalized = headerRow.map(normaliseHeaderLabel);
  while (normalized.length && normalized[normalized.length - 1] === "") {
    normalized.pop();
  }
  if (normalized.length !== EXPECTED_PO_HEADERS.length) {
    throw new Error(
      `Import failed: expected ${EXPECTED_PO_HEADERS.length} columns, but found ${normalized.length}.`
    );
  }
  for (let i = 0; i < EXPECTED_PO_HEADERS.length; i += 1) {
    if (normalized[i] !== EXPECTED_PO_HEADERS[i]) {
      const expected = EXPECTED_PO_HEADERS[i];
      const found = normalized[i] || "(blank)";
      throw new Error(`Import failed: column ${i + 1} should be "${expected}", but found "${found}".`);
    }
  }
  return normalized;
};

const renameDuplicateHeaders = (headers) => {
  const counts = {};
  return headers.map((label) => {
    const canonical = label.toLowerCase();
    if (!DUPLICATE_HEADER_RENAMES[canonical]) return label;
    counts[canonical] = (counts[canonical] || 0) + 1;
    if (counts[canonical] >= 2) {
      return DUPLICATE_HEADER_RENAMES[canonical];
    }
    return label;
  });
};

const prepareRowsForImport = (rows) => {
  if (!rows?.length) {
    throw new Error("Import failed: file does not contain any rows.");
  }
  const normalized = validateHeaderRow(rows[0] || []);
  const adjustedHeaders = renameDuplicateHeaders(normalized);
  const nextRows = [...rows];
  nextRows[0] = adjustedHeaders;
  return nextRows;
};

const normalizeLabelKey = (value) => canonicalizeHeaderLabel(value);

const insertAuditHeaders = (headers) => {
  const withoutAudit = headers.filter((header) => !DERIVED_KEY_SET.has(header.key));
  const nextHeaders = [...withoutAudit];
  const poIndex = nextHeaders.findIndex((header) => normalizeLabelKey(header.label) === "po#");
  const leadingInsertIndex = poIndex >= 0 ? poIndex : 0;
  if (LEADING_DERIVED_COLUMNS.length) {
    nextHeaders.splice(
      leadingInsertIndex,
      0,
      ...LEADING_DERIVED_COLUMNS.map((column) => ({ ...column }))
    );
  }
  const updatedPoIndex = nextHeaders.findIndex((header) => normalizeLabelKey(header.label) === "po#");
  const trailingInsertIndex = updatedPoIndex >= 0 ? updatedPoIndex + 1 : nextHeaders.length;
  if (TRAILING_DERIVED_COLUMNS.length) {
    nextHeaders.splice(
      trailingInsertIndex,
      0,
      ...TRAILING_DERIVED_COLUMNS.map((column) => ({ ...column }))
    );
  }
  return nextHeaders;
};

const formatLocalDateTime = () => new Date().toLocaleString();

const normaliseKeyPart = (value) =>
  value === undefined || value === null ? "" : String(value).trim().toLowerCase();

const buildCompositeKey = (record, poKey, lineKey, supplierKey) => {
  if (!poKey || !lineKey) return "";
  const poValue = normaliseKeyPart(record?.[poKey]);
  const lineValue = normaliseKeyPart(record?.[lineKey]);
  const supplierValue = supplierKey ? normalizeSupplierIdentifier(record?.[supplierKey]) : "";
  return `${supplierValue}::${poValue}::${lineValue}`;
};

const buildRecordMap = (dataset, keyConfig) => {
  if (!dataset?.data || !keyConfig?.poKey || !keyConfig?.lineKey) return new Map();
  const map = new Map();
  dataset.data.forEach((record) => {
    const compositeKey = buildCompositeKey(
      record,
      keyConfig.poKey,
      keyConfig.lineKey,
      keyConfig.supplierKey
    );
    if (!compositeKey) return;
    map.set(compositeKey, record);
  });
  return map;
};

const normalizeSupplierIdentifier = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[\s,]+/g, "")
    .toLowerCase();

const toSupplierToken = (value) => {
  const normalized = normalizeSupplierIdentifier(value);
  return normalized || BLANK_SUPPLIER_TOKEN;
};

const findHeaderKeyByLabel = (headers = [], targetLabel) => {
  const target = canonicalizeHeaderLabel(targetLabel);
  const match = headers.find((header) => canonicalizeHeaderLabel(header.label) === target);
  return match?.key || null;
};

const normalizeRuleOperand = (value, type) => {
  if (type === "number") {
    const num = Number(String(value ?? "").replace(/,/g, ""));
    return Number.isFinite(num) ? num : null;
  }
  if (type === "date") {
    return parseShortDate(value);
  }
  const text = normaliseTextValue(value);
  return text || "";
};

const buildPoToken = (row, poKey, custKey) => {
  if (!row || !poKey) return "";
  const poValue = String(row[poKey] ?? "").trim();
  if (!poValue) return "";
  const custValue = custKey ? String(row[custKey] ?? "").trim().toUpperCase() : "";
  return custValue ? `${custValue}::${poValue}` : poValue;
};

const normalizeHoldRulesForDataset = (headers = [], rules = []) => {
  const headerMap = new Map((headers || []).map((header) => [header.key, header]));
  return (rules || [])
    .map((rule) => {
      const columnKey = rule?.columnKey || rule?.column;
      if (!columnKey || !headerMap.has(columnKey)) return null;
      const header = headerMap.get(columnKey) || {};
      const columnType = header.type === "number" ? "number" : header.type === "date" ? "date" : "text";
      const operator = (rule?.operator || "=").trim();
      if (!["=", ">", "<"].includes(operator)) return null;
      const parsedValue = normalizeRuleOperand(rule?.value, columnType);
      if (columnType !== "text" && parsedValue === null) return null;
      if (columnType === "text" && !parsedValue) return null;
      return {
        columnKey,
        operator,
        type: columnType,
        parsedValue,
        applySamePo: Boolean(rule?.applySamePo),
      };
    })
    .filter(Boolean);
};

const ruleMatchesRow = (row, rule) => {
  if (!row || !rule) return false;
  if (rule.type === "text") {
    const left = normaliseTextValue(row[rule.columnKey]);
    return Boolean(left) && left === rule.parsedValue;
  }
  if (rule.type === "number") {
    const left = normalizeRuleOperand(row[rule.columnKey], "number");
    if (left === null) return false;
    if (rule.operator === ">") return left > rule.parsedValue;
    if (rule.operator === "<") return left < rule.parsedValue;
    return left === rule.parsedValue;
  }
  if (rule.type === "date") {
    const left = parseShortDate(row[rule.columnKey]);
    if (left === null) return false;
    if (rule.operator === ">") return left > rule.parsedValue;
    if (rule.operator === "<") return left < rule.parsedValue;
    return left === rule.parsedValue;
  }
  return false;
};

const computeHoldFlagsForDataset = (headers = [], rows = [], rules = []) => {
  const normalizedRules = normalizeHoldRulesForDataset(headers, rules);
  const rowFlags = rows.map(() => false);
  if (!normalizedRules.length) {
    const changed = rows.some((row) => row && row[ON_HOLD_KEY]);
    return { rowFlags, changed };
  }
  const poKey = findHeaderKeyByLabel(headers, PO_NUMBER_LABEL);
  const custKey = findHeaderKeyByLabel(headers, CUST_CODE_LABEL) || CUST_CODE_COLUMN.key;
  const heldPoTokens = new Set();
  rows.forEach((row, index) => {
    if (!row || typeof row !== "object") return;
    for (const rule of normalizedRules) {
      if (ruleMatchesRow(row, rule)) {
        rowFlags[index] = true;
        if (rule.applySamePo) {
          const token = buildPoToken(row, poKey, custKey);
          if (token) heldPoTokens.add(token);
        }
        break;
      }
    }
  });
  if (heldPoTokens.size && poKey) {
    rows.forEach((row, index) => {
      if (rowFlags[index] || !row || typeof row !== "object") return;
      const token = buildPoToken(row, poKey, custKey);
      if (token && heldPoTokens.has(token)) {
        rowFlags[index] = true;
      }
    });
  }
  const changed = rows.some((row, index) => {
    const prev = Boolean(row?.[ON_HOLD_KEY]);
    return prev !== rowFlags[index];
  });
  return { rowFlags, changed };
};

const ensureOnHoldHeader = (headers = []) => {
  const canonicalTarget = canonicalizeHeaderLabel(ON_HOLD_LABEL);
  let nextHeaders = headers;
  let changed = false;
  const existingIndex = headers.findIndex((header) => canonicalizeHeaderLabel(header.label) === canonicalTarget);
  if (existingIndex === -1) {
    changed = true;
    nextHeaders = [...headers];
    const statusIndex = headers.findIndex((header) => canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(STATUS_LABEL));
    const insertIndex = statusIndex === -1 ? 0 : statusIndex + 1;
    nextHeaders.splice(insertIndex, 0, { ...ON_HOLD_COLUMN });
  } else {
    const target = headers[existingIndex];
    if (target.key !== ON_HOLD_KEY || target.label !== ON_HOLD_LABEL || target.type !== "text") {
      nextHeaders = [...headers];
      nextHeaders[existingIndex] = { ...ON_HOLD_COLUMN };
      changed = true;
    }
  }
  return { headers: nextHeaders, changed };
};

const annotateDatasetWithHoldColumn = (dataset, rules = []) => {
  if (!dataset || !Array.isArray(dataset.headers)) return dataset;
  const { headers: nextHeaders, changed: headerChanged } = ensureOnHoldHeader(dataset.headers || []);
  const rows = Array.isArray(dataset.data) ? dataset.data : [];
  const { rowFlags, changed: holdChanged } = computeHoldFlagsForDataset(nextHeaders, rows, rules);
  if (!headerChanged && !holdChanged) return dataset;
  const nextData = holdChanged
    ? rows.map((row, index) => {
        if (!row || typeof row !== "object") return row;
        const nextValue = rowFlags[index] ? "Yes" : "";
        if ((row[ON_HOLD_KEY] || "") === nextValue) return row;
        return { ...row, [ON_HOLD_KEY]: nextValue };
      })
    : rows;
  return {
    ...dataset,
    headers: nextHeaders,
    data: nextData,
  };
};

const normalizeTscpToken = (token) => String(token ?? "").trim().toUpperCase();

const ensureTscpHeader = (headers = []) => {
  const canonicalTarget = canonicalizeHeaderLabel(TSCP_LABEL);
  const canonicalPo = canonicalizeHeaderLabel(PO_NUMBER_LABEL);
  let nextHeaders = headers;
  let changed = false;
  const existingIndex = headers.findIndex((header) => canonicalizeHeaderLabel(header.label) === canonicalTarget);
  if (existingIndex === -1) {
    nextHeaders = [...headers];
    const poIndex = headers.findIndex((header) => canonicalizeHeaderLabel(header.label) === canonicalPo);
    const insertIndex = poIndex === -1 ? 0 : poIndex;
    nextHeaders.splice(insertIndex, 0, { ...TSCP_COLUMN });
    changed = true;
  } else {
    const target = headers[existingIndex];
    if (target.key !== TSCP_KEY || target.label !== TSCP_LABEL || target.type !== "text") {
      nextHeaders = [...headers];
      nextHeaders[existingIndex] = { ...TSCP_COLUMN };
      changed = true;
    }
  }
  return { headers: nextHeaders, changed };
};

const annotateDatasetWithTscpColumn = (dataset, tokens = new Set()) => {
  if (!dataset?.headers) return dataset;
  const tokenSet =
    tokens instanceof Set
      ? tokens
      : new Set((tokens || []).map((token) => normalizeTscpToken(token)).filter(Boolean));
  const { headers: ensuredHeaders, changed: headerChanged } = ensureTscpHeader(dataset.headers);
  const poKey = findHeaderKeyByLabel(ensuredHeaders, PO_NUMBER_LABEL);
  const custKey = findHeaderKeyByLabel(ensuredHeaders, CUST_CODE_LABEL) || CUST_CODE_COLUMN.key;
  const rows = Array.isArray(dataset.data) ? dataset.data : [];
  if (!rows.length) {
    if (headerChanged) {
      return { ...dataset, headers: ensuredHeaders };
    }
    return dataset;
  }
  let changed = headerChanged;
  const nextData = rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    const custValue = custKey ? String(row[custKey] ?? "").trim().toUpperCase() : "";
    const poValue = poKey ? String(row[poKey] ?? "").trim() : "";
    const token = custValue && poValue ? `${custValue}::${poValue}` : "";
    const nextValue = token && tokenSet.has(token) ? "Yes" : "";
    if ((row[TSCP_KEY] || "") === nextValue) return row;
    changed = true;
    return { ...row, [TSCP_KEY]: nextValue };
  });
  if (!changed) return dataset;
  return {
    ...dataset,
    headers: ensuredHeaders,
    data: nextData,
  };
};

const normalizeTscpTokens = (tokens = []) =>
  new Set((tokens || []).map((token) => normalizeTscpToken(token)).filter(Boolean));

const applyCustCodeColumn = (dataset, lookupMap) => {
  if (!dataset?.headers || !dataset?.data) return dataset;
  const supplierKey = findHeaderKeyByLabel(dataset.headers, SUPPLIER_NUMBER_LABEL);
  if (!supplierKey) return dataset;
  const hasLookup = lookupMap instanceof Map && lookupMap.size > 0;
  if (!hasLookup) return dataset;
  let changed = false;
  const data = dataset.data.map((record) => {
    const existing = record[CUST_CODE_KEY] ?? "";
    let mapped = "";
    const normalized = normalizeSupplierIdentifier(record[supplierKey]);
    if (normalized) {
      mapped = lookupMap.get(normalized) || "";
    }
    if (existing === mapped) return record;
    changed = true;
    return { ...record, [CUST_CODE_KEY]: mapped };
  });
  return changed ? { ...dataset, data } : dataset;
};

const normaliseComparisonValue = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "number") return Number.isFinite(value) ? value : "";
  if (value instanceof Date) return value.getTime();
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const numericCandidate = Number(trimmed.replace(/,/g, ""));
  if (!Number.isNaN(numericCandidate) && /^-?[0-9.,]+$/.test(trimmed)) {
    return numericCandidate;
  }
  return trimmed;
};

const diffRecords = (newRecord, oldRecord, comparisonPlan) => {
  const diff = {};
  let hasChanges = false;
  comparisonPlan.forEach(({ newKey, oldKey }) => {
    const newValue = newRecord?.[newKey];
    const oldValue = oldRecord?.[oldKey];
    if (normaliseComparisonValue(newValue) !== normaliseComparisonValue(oldValue)) {
      hasChanges = true;
      diff[newKey] = { previous: oldValue };
    }
  });
  return { hasChanges, diff };
};

const mergeDatasetWithHistory = (sourceDataset, previousDataset) => {
  if (!sourceDataset?.headers || !sourceDataset?.data) return sourceDataset;

  const baseNewHeaders = sourceDataset.headers.filter((header) => !DERIVED_KEY_SET.has(header.key));
  const baseOldHeaders = (previousDataset?.headers || []).filter((header) => !DERIVED_KEY_SET.has(header.key));

  const poIndex = baseNewHeaders.findIndex((header) => normalizeLabelKey(header.label) === "po#");
  const lineIndex = baseNewHeaders.findIndex((header) => normalizeLabelKey(header.label) === "line number");
  const supplierIndex = baseNewHeaders.findIndex(
    (header) => canonicalizeHeaderLabel(header.label) === SUPPLIER_NUMBER_CANON
  );
  if (poIndex === -1 || lineIndex === -1) {
    throw new Error("Import failed: required PO# or Line number column is missing.");
  }

  const poKeyNew = baseNewHeaders[poIndex]?.key;
  const lineKeyNew = baseNewHeaders[lineIndex]?.key;
  const supplierKeyNew = supplierIndex >= 0 ? baseNewHeaders[supplierIndex]?.key : null;
  const resolveOldKeyByLabel = (label, fallbackKey) =>
    (label ? findHeaderKeyByLabel(baseOldHeaders, label) : null) || fallbackKey;
  const poKeyOld = resolveOldKeyByLabel(PO_NUMBER_LABEL, poKeyNew);
  const lineKeyOld = resolveOldKeyByLabel("Line number", lineKeyNew);
  const supplierKeyOld = supplierKeyNew
    ? resolveOldKeyByLabel(SUPPLIER_NUMBER_LABEL, supplierKeyNew)
    : null;
  const newSupplierSet = new Set();
  if (supplierKeyNew) {
    sourceDataset.data.forEach((record) => {
      newSupplierSet.add(toSupplierToken(record[supplierKeyNew]));
    });
  }

  const comparisonPlan = baseNewHeaders.map((header) => ({
    label: header.label,
    newKey: header.key,
    oldKey: findHeaderKeyByLabel(baseOldHeaders, header.label) || header.key,
  }));

  const previousMap = buildRecordMap(previousDataset, {
    poKey: poKeyOld,
    lineKey: lineKeyOld,
    supplierKey: supplierKeyOld,
  });
  const usedKeys = new Set();
  const timestamp = formatLocalDateTime();

  const mergedData = sourceDataset.data.map((record) => {
    const compositeKey = buildCompositeKey(record, poKeyNew, lineKeyNew, supplierKeyNew);
    const previousRecord = previousMap.get(compositeKey);
    const nextRecord = { ...record };

    if (!previousRecord) {
      nextRecord[STATUS_KEY] = "New";
      nextRecord[FIRST_UPDATE_KEY] = timestamp;
      nextRecord[LAST_UPDATE_KEY] = "";
      return nextRecord;
    }

    usedKeys.add(compositeKey);
    const { hasChanges, diff } = diffRecords(nextRecord, previousRecord, comparisonPlan);
    if (hasChanges) {
      nextRecord[STATUS_KEY] = "Update";
      nextRecord[FIRST_UPDATE_KEY] = previousRecord[FIRST_UPDATE_KEY] || "";
      nextRecord[LAST_UPDATE_KEY] = timestamp;
      nextRecord.__diff = diff;
    } else {
      nextRecord[STATUS_KEY] = "Open";
      nextRecord[FIRST_UPDATE_KEY] = previousRecord[FIRST_UPDATE_KEY] || "";
      nextRecord[LAST_UPDATE_KEY] = previousRecord[LAST_UPDATE_KEY] || "";
      if (nextRecord.__diff) delete nextRecord.__diff;
    }
    return nextRecord;
  });

  previousMap.forEach((record, compositeKey) => {
    if (usedKeys.has(compositeKey)) return;
    if (supplierKeyOld && newSupplierSet.size) {
      const supplierToken = toSupplierToken(record[supplierKeyOld]);
      if (!newSupplierSet.has(supplierToken)) {
        const preserved = { ...record };
        if (!preserved[CUST_CODE_KEY]) preserved[CUST_CODE_KEY] = record[CUST_CODE_KEY] || "";
        delete preserved.__diff;
        mergedData.push(preserved);
        return;
      }
    }
    const cloned = { ...record };
    cloned._id = record._id ? `${record._id}-closed` : `closed-${compositeKey}`;
    cloned[STATUS_KEY] = "Closed";
    cloned[FIRST_UPDATE_KEY] = record[FIRST_UPDATE_KEY] || "";
    cloned[LAST_UPDATE_KEY] = timestamp;
    if (!cloned[CUST_CODE_KEY]) cloned[CUST_CODE_KEY] = record[CUST_CODE_KEY] || "";
    delete cloned.__diff;
    mergedData.push(cloned);
  });

  const nextHeaders = insertAuditHeaders(baseNewHeaders);
  mergedData.forEach((record) => {
    if (!record[STATUS_KEY]) record[STATUS_KEY] = "";
    if (!record[FIRST_UPDATE_KEY]) record[FIRST_UPDATE_KEY] = "";
    if (!record[LAST_UPDATE_KEY]) record[LAST_UPDATE_KEY] = "";
    if (!record[CUST_CODE_KEY]) record[CUST_CODE_KEY] = "";
  });

  return enforceForcedColumnTypes({ headers: nextHeaders, data: mergedData });
};

const isBlankValue = (value) => value === undefined || value === null || value === "";

const ensureAuditColumnsForDisplay = (dataset, options = {}) => {
  if (!dataset?.headers) return dataset;
  const baseHeaders = insertAuditHeaders(dataset.headers);
  const headers = baseHeaders.map((header) => {
    const forcedLabel = DERIVED_LABEL_MAP.get(header.key);
    if (forcedLabel && header.label !== forcedLabel) {
      return { ...header, label: forcedLabel };
    }
    return header;
  });
  let fallbackTimestamp = "";
  const ensureFallbackTimestamp = () => {
    if (fallbackTimestamp) return fallbackTimestamp;
    fallbackTimestamp = formatLocalDateTime();
    return fallbackTimestamp;
  };
  const data = (dataset.data || []).map((record) => {
    const next = { ...record };
    DERIVED_COLUMNS.forEach((column) => {
      if (next[column.key] === undefined) next[column.key] = "";
    });
    if (isBlankValue(next[FIRST_UPDATE_KEY])) {
      next[FIRST_UPDATE_KEY] = ensureFallbackTimestamp();
    }
    return next;
  });
  const withTypes = enforceForcedColumnTypes({ ...dataset, headers, data });
  return applyCustCodeColumn(withTypes, options.customerMap);
};

const coerceValueToForcedType = (value, type) => {
  if (type === "date") return normaliseCell(value, "date");
  if (type === "text") return value === undefined || value === null ? "" : String(value);
  return value;
};

const enforceForcedColumnTypes = (dataset) => {
  if (!dataset?.headers) return dataset;
  let headersChanged = false;
  const headerForceMeta = dataset.headers.map((header) => {
    const canonical = canonicalizeHeaderLabel(header.label);
    const forcedType = FORCE_TYPE_MAP.get(canonical);
    let nextHeader = header;
    if (forcedType && header.type !== forcedType) {
      headersChanged = true;
      nextHeader = { ...header, type: forcedType };
    }
    return { header: nextHeader, forcedType };
  });
  const forcedTargets = headerForceMeta.filter((item) => item.forcedType);
  const headers = headerForceMeta.map((item) => item.header);
  if (!forcedTargets.length) {
    return headersChanged ? { ...dataset, headers } : dataset;
  }
  const originalData = Array.isArray(dataset.data) ? dataset.data : [];
  let dataChanged = false;
  const data = originalData.map((record) => {
    let mutated = false;
    const next = { ...record };
    forcedTargets.forEach(({ header, forcedType }) => {
      const currentValue = next[header.key];
      const coercedValue = coerceValueToForcedType(currentValue, forcedType);
      if (coercedValue !== currentValue) {
        next[header.key] = coercedValue;
        mutated = true;
      }
    });
    if (mutated) {
      dataChanged = true;
      return next;
    }
    return record;
  });
  if (!headersChanged && !dataChanged) return dataset;
  return {
    ...dataset,
    headers: headersChanged ? headers : dataset.headers,
    data: dataChanged ? data : dataset.data,
  };
};

const sanitizeKey = (value, index) =>
  (value && value.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")) || `column-${index}`;

const toShortDate = (date) =>
  date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });

const padNumber = (value) => String(value).padStart(2, "0");

const formatTimestampForFilename = (date) =>
  `${date.getFullYear()}${padNumber(date.getMonth() + 1)}${padNumber(date.getDate())}-${padNumber(
    date.getHours()
  )}${padNumber(date.getMinutes())}${padNumber(date.getSeconds())}`;

const formatTimestampForTscpFile = (date) =>
  `${date.getFullYear()}${padNumber(date.getMonth() + 1)}${padNumber(date.getDate())}_${padNumber(
    date.getHours()
  )}${padNumber(date.getMinutes())}${padNumber(date.getSeconds())}`;

const filenameFromDisposition = (value = "") => {
  if (!value) return "";
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (err) {
      return utf8Match[1];
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(value);
  return plainMatch && plainMatch[1] ? plainMatch[1] : "";
};

const normaliseValueForExport = (value, type) => {
  if (value === undefined || value === null) return "";
  if (type === "number") {
    if (typeof value === "number") return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : String(value);
  }
  return value;
};

const PST_TIME_ZONE = "America/Los_Angeles";
const PST_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: PST_TIME_ZONE,
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
});
const PST_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: PST_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const parseDateValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? null : new Date(timestamp);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return new Date(
        Date.UTC(
          parsed.y,
          (parsed.m || 1) - 1,
          parsed.d || 1,
          parsed.H || 0,
          parsed.M || 0,
          Math.floor(parsed.S || 0)
        )
      );
    }
    const asDate = new Date(value);
    return Number.isNaN(asDate.getTime()) ? null : asDate;
  }
  const normalized = String(value).trim();
  if (!normalized) return null;
  const timestamp = Date.parse(normalized.replace(/\u202f/g, " "));
  if (!Number.isNaN(timestamp)) return new Date(timestamp);
  const shortMatch = SHORT_DATE_RE.exec(normalized);
  if (shortMatch) {
    const [, mm, dd, yy] = shortMatch;
    const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
    const js = new Date(year, Number(mm) - 1, Number(dd));
    return Number.isNaN(js.getTime()) ? null : js;
  }
  const isoMatch = ISO_DATE_RE.exec(normalized);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    const js = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(js.getTime()) ? null : js;
  }
  return null;
};

const formatPstTimestamp = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    if (value === undefined || value === null) return "";
    return String(value);
  }
  const datePart = PST_DATE_FORMATTER.format(parsed);
  const timePart = PST_TIME_FORMATTER.format(parsed);
  return `${datePart} ${timePart}`;
};

const formatShortDate = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (value instanceof Date) return toShortDate(value);
  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      // 使用本地時區避免 UTC 產生的日期前移
      const js = new Date(parsed.y, parsed.m - 1, parsed.d);
      return toShortDate(js);
    }
  }
  const m = SHORT_DATE_RE.exec(String(value));
  if (m) {
    const [, mm, dd, yy] = m;
    const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
    const js = new Date(year, Number(mm) - 1, Number(dd));
    if (!Number.isNaN(js.getTime())) return toShortDate(js);
  }
  const iso = ISO_DATE_RE.exec(String(value).trim());
  if (iso) {
    const [, yyyy, mm, dd] = iso;
    const js = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!Number.isNaN(js.getTime())) return toShortDate(js);
  }
  const js = new Date(value);
  if (!Number.isNaN(js.getTime())) return toShortDate(js);
  return String(value);
};

const parseShortDate = (value) => {
  if (!value) return null;
  const isoMatch = ISO_DATE_RE.exec(String(value));
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    const js = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    const ts = js.getTime();
    return Number.isNaN(ts) ? null : ts;
  }
  const m = SHORT_DATE_RE.exec(String(value));
  if (!m) return null;
  const [, mm, dd, yy] = m;
  const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
  const js = new Date(year, Number(mm) - 1, Number(dd));
  const ts = js.getTime();
  return Number.isNaN(ts) ? null : ts;
};

const toISODateInputValue = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const isoMatch = ISO_DATE_RE.exec(trimmed);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  const shortMatch = SHORT_DATE_RE.exec(trimmed);
  if (!shortMatch) return "";
  const [, mm, dd, yy] = shortMatch;
  const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
  return `${year}-${padNumber(mm)}-${padNumber(dd)}`;
};

const guessType = (label, samples) => {
  const trimmed = (label || "").trim();
  if (DATE_LABEL_RE.test(trimmed)) return "date";
  if (ALWAYS_TEXT_RE.test(trimmed)) return "text";
  let numeric = true;
  for (const sample of samples) {
    if (sample === undefined || sample === null || sample === "") continue;
    const n = Number(String(sample).replace(/,/g, ""));
    if (Number.isNaN(n)) {
      numeric = false;
      break;
    }
  }
  return numeric ? "number" : "text";
};

const normaliseCell = (value, type) => {
  if (type === "date") return formatShortDate(value);
  if (type === "number") {
    if (value === undefined || value === null || value === "") return null;
    const n = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return value === undefined || value === null ? "" : String(value);
};

const renderCellValue = (value, header) => {
  if (header?.key === FIRST_UPDATE_KEY || header?.key === LAST_UPDATE_KEY) {
    return formatPstTimestamp(value);
  }
  if (value === undefined || value === null) return "";
  if (header?.type === "date") return formatShortDate(value);
  if (header?.type === "number") return Number.isFinite(value) ? value.toLocaleString() : "";
  return value;
};

const STATUS_BADGE_STYLES = {
  New: "bg-green-200 text-green-900 border border-green-300",
  Update: "bg-rose-100 text-rose-800 border border-rose-200",
  Closed: "bg-amber-200 text-amber-900 border border-amber-300",
  Open: "bg-slate-200 text-slate-800 border border-slate-300",
};

const StatusBadge = ({ value }) => {
  const label = String(value || "").trim() || "Open";
  const classes = STATUS_BADGE_STYLES[label] || STATUS_BADGE_STYLES.Open;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`}>
      {label}
    </span>
  );
};

const OnHoldBadge = ({ value }) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
      On Hold
    </span>
  );
};

const TscpBadge = ({ value }) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-800">
      <Stamp className="h-3.5 w-3.5 text-indigo-500" aria-hidden="true" />
      TSCP
    </span>
  );
};

const ACTIVE_CANCEL_FOLLOWUP_STATUSES = new Set(["PENDING", "IN_PROGRESS"]);
const CANCEL_FOLLOWUP_META_PREFIX = "[[PP_CANCEL_FOLLOWUP_META::";
const CANCEL_FOLLOWUP_META_SUFFIX = "]]";
const CANCEL_FOLLOWUP_REQUESTED_WMS_KEYS = new Set(["CREQC", "WREQC", "REQC", "CANCELLED"]);

const normalizeCancelFollowupStatus = (value) => {
  const text = String(value || "").trim().toUpperCase();
  if (["PENDING", "IN_PROGRESS", "DONE", "NOT_REQUIRED"].includes(text)) return text;
  return "";
};

const getActiveCancelFollowupProgressStatus = (value) => {
  const normalized = normalizeCancelFollowupStatus(value);
  return normalized === "DONE" || normalized === "NOT_REQUIRED" ? normalized : "IN_PROGRESS";
};

const emptyCancelFollowupWorkflowMeta = () => ({
  version: 1,
  steps: {},
  manualCloseReason: "",
  manualCloseStatus: "",
});

const sanitizeCancelFollowupWorkflowMeta = (meta) => {
  const input = meta && typeof meta === "object" ? meta : {};
  const steps = {};
  Object.entries(input.steps || {}).forEach(([key, value]) => {
    if (!key || !value || typeof value !== "object") return;
    const doneAt = firstText(value.doneAt);
    const doneBy = firstText(value.doneBy);
    if (!doneAt && !doneBy) return;
    steps[key] = { doneAt, doneBy };
  });
  return {
    version: 1,
    steps,
    manualCloseReason: firstText(input.manualCloseReason),
    manualCloseStatus: normalizeCancelFollowupStatus(input.manualCloseStatus),
  };
};

const cancelFollowupWorkflowMetaHasData = (meta) => {
  const sanitized = sanitizeCancelFollowupWorkflowMeta(meta);
  return Boolean(
    Object.keys(sanitized.steps || {}).length ||
      sanitized.manualCloseReason ||
      sanitized.manualCloseStatus
  );
};

const parseCancelFollowupNotes = (rawValue) => {
  const rawNotes = String(rawValue || "");
  const markerIndex = rawNotes.lastIndexOf(CANCEL_FOLLOWUP_META_PREFIX);
  if (markerIndex < 0) {
    return {
      operatorNotes: rawNotes.trim(),
      workflowMeta: emptyCancelFollowupWorkflowMeta(),
    };
  }
  const suffixIndex = rawNotes.indexOf(CANCEL_FOLLOWUP_META_SUFFIX, markerIndex);
  if (suffixIndex < 0) {
    return {
      operatorNotes: rawNotes.trim(),
      workflowMeta: emptyCancelFollowupWorkflowMeta(),
    };
  }
  const encoded = rawNotes
    .slice(markerIndex + CANCEL_FOLLOWUP_META_PREFIX.length, suffixIndex)
    .trim();
  let workflowMeta = emptyCancelFollowupWorkflowMeta();
  if (encoded) {
    try {
      workflowMeta = sanitizeCancelFollowupWorkflowMeta(JSON.parse(decodeURIComponent(encoded)));
    } catch (_err) {
      workflowMeta = emptyCancelFollowupWorkflowMeta();
    }
  }
  return {
    operatorNotes: rawNotes.slice(0, markerIndex).trim(),
    workflowMeta,
  };
};

const buildCancelFollowupNotes = (operatorNotes, workflowMeta) => {
  const text = String(operatorNotes || "").trim();
  const sanitizedMeta = sanitizeCancelFollowupWorkflowMeta(workflowMeta);
  if (!cancelFollowupWorkflowMetaHasData(sanitizedMeta)) {
    return text;
  }
  const encoded = encodeURIComponent(JSON.stringify(sanitizedMeta));
  return text
    ? `${text}\n\n${CANCEL_FOLLOWUP_META_PREFIX}${encoded}${CANCEL_FOLLOWUP_META_SUFFIX}`
    : `${CANCEL_FOLLOWUP_META_PREFIX}${encoded}${CANCEL_FOLLOWUP_META_SUFFIX}`;
};

const getCancelFollowupWorkflowStep = (workflowMeta, key) =>
  sanitizeCancelFollowupWorkflowMeta(workflowMeta).steps?.[key] || null;

const setCancelFollowupWorkflowStep = (workflowMeta, key, actorEmail, doneAt = new Date().toISOString()) => {
  const sanitized = sanitizeCancelFollowupWorkflowMeta(workflowMeta);
  sanitized.steps = {
    ...(sanitized.steps || {}),
    [key]: {
      doneAt: firstText(doneAt),
      doneBy: firstText(actorEmail),
    },
  };
  return sanitized;
};

const resolveCancelFollowupSequentialStepStates = (steps = []) => {
  const normalizedSteps = Array.isArray(steps) ? steps : [];
  const completedIndex = normalizedSteps.reduce((bestIndex, step, index) => {
    if (step?.completed) return index;
    return bestIndex;
  }, -1);
  const currentIndex = normalizedSteps.findIndex((step, index) => {
    if (index <= completedIndex) return false;
    return Boolean(step?.available);
  });
  return normalizedSteps.map((step, index) => {
    if (step?.completed) return "completed";
    if (step?.blocked && index === currentIndex) return "blocked";
    if (index === currentIndex) return "current";
    return "waiting";
  });
};

const splitCancelFollowupValueList = (value) =>
  String(value || "")
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);

const getCancelFollowupLiveWmsKeys = (followup) => {
  const originValues = splitCancelFollowupValueList(followup?.originLiveState?.currentWmsStatus);
  if (originValues.length) {
    return Array.from(
      new Set(originValues.map((value) => normalizeWmsStatusKey(value)).filter(Boolean))
    );
  }
  const fromLinks = asArray(followup?.routingLinks)
    .map((link) => firstText(link?.currentWms))
    .filter(Boolean);
  const sourceValues = fromLinks.length ? fromLinks : splitCancelFollowupValueList(followup?.currentWms);
  return Array.from(
    new Set(sourceValues.map((value) => normalizeWmsStatusKey(value)).filter(Boolean))
  );
};

const resolveCancelFollowupPath = (followup) => {
  const liveWmsKeys = getCancelFollowupLiveWmsKeys(followup);
  const liveAvailable =
    Boolean(followup?.originLiveState?.liveStateAvailable) ||
    Boolean(followup?.replacementLiveState?.liveStateAvailable) ||
    Boolean(followup?.currentLiveStateAvailable) ||
    asArray(followup?.routingLinks).some((link) => Boolean(link?.currentLiveStateAvailable)) ||
    liveWmsKeys.length > 0;
  const snapshotWmsKeys = splitCancelFollowupValueList(followup?.wms).map((value) =>
    normalizeWmsStatusKey(value)
  );
  const effectiveKeys = liveWmsKeys.length ? liveWmsKeys : snapshotWmsKeys;
  const isShippedPath = effectiveKeys.length > 0 && effectiveKeys.every((key) => key === "SHIPPED");
  return {
    type: isShippedPath ? "shipped" : "not_shipped",
    badgeLabel: isShippedPath ? "Shipped Order Follow-up" : "Not Shipped Cancellation Flow",
    usedSnapshotFallback: !liveWmsKeys.length && snapshotWmsKeys.length > 0,
    liveAvailable,
    liveWmsKeys,
  };
};

const CANCEL_FOLLOWUP_BADGE_STYLES = {
  PENDING: "bg-rose-100 text-rose-800 border border-rose-200",
  IN_PROGRESS: "bg-amber-100 text-amber-800 border border-amber-200",
  DONE: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  NOT_REQUIRED: "bg-slate-200 text-slate-700 border border-slate-300",
};

const CANCEL_FOLLOWUP_BADGE_LABELS = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  DONE: "Done",
  NOT_REQUIRED: "No Reminder",
};

const WMS_STATUS_STYLES = {
  NEW: "bg-sky-100 text-sky-900 border-sky-200",
  MSTCRTD: "bg-indigo-100 text-indigo-800 border-indigo-200",
  PICKING: "bg-blue-100 text-blue-900 border-blue-200",
  PICKED: "bg-cyan-100 text-cyan-900 border-cyan-200",
  PACKING: "bg-teal-100 text-teal-900 border-teal-200",
  PACKED: "bg-emerald-100 text-emerald-900 border-emerald-200",
  SHIPPED: "bg-green-600 text-white border-green-700",
  CREQC: "bg-amber-100 text-amber-900 border-amber-200",
  REQC: "bg-amber-100 text-amber-900 border-amber-200",
  CANCELLED: "bg-rose-100 text-rose-900 border-rose-200",
  WREQC: "bg-orange-100 text-orange-900 border-orange-200",
  HOLD: "bg-yellow-100 text-yellow-900 border-yellow-200",
  ADDFAULT: "bg-red-100 text-red-900 border-red-200",
  DRAFT: "bg-slate-200 text-slate-900 border-slate-300",
  AWLABEL: "bg-purple-100 text-purple-900 border-purple-200",
};

const normalizeWmsStatusKey = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();

const WmsBadge = ({ value }) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return <span className="text-slate-400">-</span>;
  }
  const styleKey = normalizeWmsStatusKey(normalized);
  const badgeClass =
    WMS_STATUS_STYLES[styleKey] || "bg-slate-100 text-slate-800 border-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${badgeClass}`}
      title={normalized}
    >
      {normalized}
    </span>
  );
};

const CancelFollowupBadge = ({ value }) => {
  const status = normalizeCancelFollowupStatus(value);
  if (!status) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
        CANCEL_FOLLOWUP_BADGE_STYLES[status] || CANCEL_FOLLOWUP_BADGE_STYLES.PENDING
      }`}
    >
      {CANCEL_FOLLOWUP_BADGE_LABELS[status] || status}
    </span>
  );
};

const buildCancelFollowupPanelState = (followup = null) => {
  const parsed = parseCancelFollowupNotes(followup?.notes || "");
  return {
    followupId: followup?.id || null,
    followup,
    status: normalizeCancelFollowupStatus(followup?.status) || "PENDING",
    notes: parsed.operatorNotes,
    workflowMeta: parsed.workflowMeta,
    assignToMe: false,
    error: "",
  };
};

const emptyCancelFollowupCorrectionState = () => ({
  loading: "",
  message: "",
  tone: "",
  preview: null,
  activation: null,
  orchestration: null,
});

const asArray = (value) => (Array.isArray(value) ? value : []);

const firstText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const formatCorrectionQty = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toLocaleString() : String(value);
};

const getCorrectionAllocationSku = (item) =>
  firstText(item?.sku, item?.walmartItemNo, item?.itemNo, item?.productCode, item?.retailSku);

const formatCorrectionMessage = (item) => {
  const text = firstText(item?.message, item?.code, typeof item === "string" ? item : "");
  if (text) return text;
  if (item && typeof item === "object") {
    try {
      return JSON.stringify(item);
    } catch (_err) {
      return "Unable to display message details.";
    }
  }
  return "";
};

const CorrectionMessages = ({ title, items = [], tone = "warning" }) => {
  const rows = asArray(items).filter(Boolean);
  if (!rows.length) return null;
  const toneClass =
    tone === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "success"
        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : "border-amber-200 bg-amber-50 text-amber-800";
  return (
    <div className={`rounded border px-3 py-2 text-xs ${toneClass}`}>
      <p className="font-semibold">{title}</p>
      <ul className="mt-1 space-y-1">
        {rows.map((item, index) => (
          <li key={`${title}-${index}`}>
            {formatCorrectionMessage(item) || "-"}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CorrectionAllocationTable = ({ title, rows = [], emptyText = "No allocation rows." }) => {
  const items = asArray(rows);
  return (
    <div className="rounded border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </div>
      {items.length ? (
        <div className="overflow-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">SKU / Item</th>
                <th className="px-3 py-2 text-right">PO Qty</th>
                <th className="px-3 py-2 text-right">Current Alloc</th>
                <th className="px-3 py-2 text-right">Proposed Alloc</th>
                <th className="px-3 py-2 text-left">Source</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={`${title}-${getCorrectionAllocationSku(item) || index}`} className="border-t border-slate-100 text-slate-700">
                  <td className="px-3 py-2 font-medium text-slate-900">{getCorrectionAllocationSku(item) || "-"}</td>
                  <td className="px-3 py-2 text-right">{formatCorrectionQty(item?.poQty)}</td>
                  <td className="px-3 py-2 text-right">{formatCorrectionQty(item?.allocQty ?? item?.originalAllocQty)}</td>
                  <td className="px-3 py-2 text-right">{formatCorrectionQty(item?.correctedAllocQty ?? item?.allocQty)}</td>
                  <td className="px-3 py-2">{firstText(item?.allocSource, item?.sourceContext) || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-3 py-3 text-xs text-slate-500">{emptyText}</p>
      )}
    </div>
  );
};

const CollapsibleSection = ({ title, subtitle = "", defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
        )}
      </button>
      {open ? <div className="border-t border-slate-200 px-4 py-4">{children}</div> : null}
    </div>
  );
};

const FOLLOWUP_STATION_VISUALS = {
  completed: {
    dotClass: "border-emerald-200 bg-emerald-100 text-emerald-700",
    cardClass: "border-emerald-200 bg-emerald-50/60",
    label: "Completed",
  },
  current: {
    dotClass: "border-blue-200 bg-blue-100 text-blue-700",
    cardClass: "border-blue-200 bg-blue-50/70",
    label: "Current",
  },
  waiting: {
    dotClass: "border-slate-200 bg-slate-100 text-slate-500",
    cardClass: "border-slate-200 bg-slate-50",
    label: "Waiting",
  },
  blocked: {
    dotClass: "border-rose-200 bg-rose-100 text-rose-700",
    cardClass: "border-rose-200 bg-rose-50/70",
    label: "Blocked",
  },
};

const FollowupStationFlow = ({ stations = [] }) => (
  <div className="space-y-3">
    {stations.map((station, index) => {
      const visuals =
        FOLLOWUP_STATION_VISUALS[station.state] || FOLLOWUP_STATION_VISUALS.blocked;
      const isLast = index === stations.length - 1;
      return (
        <div key={station.key || station.title || index} className="relative pl-12">
          {!isLast ? (
            <div className="absolute left-[18px] top-10 bottom-[-14px] w-px bg-slate-200" aria-hidden="true" />
          ) : null}
        <div
          className={`absolute left-0 top-1 flex h-9 w-9 items-center justify-center rounded-full border ${visuals.dotClass}`}
        >
          {station.state === "completed" ? (
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          ) : station.state === "current" ? (
            <RotateCw className="h-4 w-4" aria-hidden="true" />
          ) : station.state === "waiting" ? (
            <span className="text-xs font-semibold">{index + 1}</span>
          ) : (
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          )}
        </div>
          <div className={`rounded-xl border px-4 py-4 ${visuals.cardClass}`}>
            <div className="space-y-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">{station.title}</p>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    {visuals.label}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    {station.manual ? "Manual Step" : "System Step"}
                  </span>
                </div>
                {station.description ? (
                  <p className="mt-1 text-xs text-slate-600">{station.description}</p>
                ) : null}
              </div>
              {station.action ? (
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={station.action.onClick}
                    disabled={Boolean(station.action.disabled)}
                    className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-xs font-semibold ${
                      station.action.tone === "secondary"
                        ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                        : station.manual
                          ? "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                          : "border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
                    } disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400`}
                  >
                    {station.manual ? (
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px]">
                        {station.state === "completed" ? "✓" : ""}
                      </span>
                    ) : null}
                    {station.action.label}
                  </button>
                </div>
              ) : null}
              {station.meta ? (
                <p className="text-[11px] text-slate-500">{station.meta}</p>
              ) : null}
              {station.reason ? (
                <div className="rounded border border-rose-200 bg-white px-3 py-2 text-[11px] text-rose-700">
                  {station.reason}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const MultiSelectDropdown = ({
  values = [],
  options = [],
  placeholder = "Filter",
  onChange,
  buttonClassName = "",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const normalized = useMemo(() => new Set(values), [values]);
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, search]);

  const toggleValue = (next) => {
    const exists = normalized.has(next);
    if (exists) {
      onChange(values.filter((value) => value !== next));
    } else {
      onChange([...values, next]);
    }
  };

  const handleSelectAll = () => {
    onChange(options.map((opt) => opt.value));
  };

  const handleClear = () => {
    onChange([]);
  };

  const summary =
    values.length === 0
      ? placeholder
      : values.length === options.length
        ? "All selected"
        : `${values.length} selected`;
  const buttonClasses = [
    "w-full rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
    buttonClassName,
    disabled ? "cursor-not-allowed opacity-60" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="relative text-xs" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
        className={buttonClasses}
      >
        {summary}
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-1 w-52 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-200 p-2">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search..."
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <button type="button" onClick={handleSelectAll} className="text-blue-600 hover:underline">
                Select all
              </button>
              <button type="button" onClick={handleClear} className="text-slate-600 hover:underline">
                Clear
              </button>
            </div>
          </div>
          <div className="max-h-56 overflow-auto p-2">
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <label key={option.value} className="mb-1 flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    checked={normalized.has(option.value)}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              ))
            ) : (
              <div className="py-4 text-center text-xs text-slate-500">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const loadWorkbookFromFile = async (file) => {
  const name = file?.name || "";
  const extension = name.split(".").pop()?.toLowerCase() || "";
  const isCsv = extension === "csv";

  let workbook;
  if (isCsv) {
    const text = await file.text();
    workbook = XLSX.read(text, { type: "string" });
  } else {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: "array" });
  }

  const sheetName =
    PREFERRED_SHEET_NAMES.find((name) => workbook.Sheets[name]) || workbook.SheetNames?.[0];
  if (!sheetName) throw new Error("File is empty or has no sheets.");

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
  if (!rows.length) throw new Error("File does not contain any rows.");

  return { rows, sheetName };
};

const buildDataset = (rows) => {
  const headersRaw = rows[0] || [];
  const headers = headersRaw.map((label, index) => ({
    key: sanitizeKey(label || `column-${index}`, index),
    label: label || `Column ${index + 1}`,
    type: "text",
  }));

  const samples = headers.map((_, colIndex) => []);
  rows.slice(1, 21).forEach((row) => {
    headers.forEach((_, idx) => {
      samples[idx].push(row?.[idx]);
    });
  });

  headers.forEach((header, index) => {
    const guessedType = guessType(header.label, samples[index]);
    const canonical = canonicalizeHeaderLabel(header.label);
    if (FORCE_DATE_LABELS.has(canonical)) {
      header.type = "date";
    } else if (FORCE_TEXT_LABELS.has(canonical)) {
      header.type = "text";
    } else {
      header.type = guessedType;
    }
  });

  const data = rows.slice(1).map((row, rowIndex) => {
    const record = { _id: `${rowIndex}-${Date.now()}` };
    headers.forEach((header, idx) => {
      record[header.key] = normaliseCell(row?.[idx], header.type);
    });
    return record;
  });

  return enforceForcedColumnTypes({ headers, data });
};

const loadStoredDataset = (key = STORAGE_DATA_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.headers || !parsed?.data) return null;
    return ensureAuditColumnsForDisplay(parsed);
  } catch (err) {
    console.warn("Failed to restore cached PO details dataset", err);
    return null;
  }
};

const persistDataset = (key = STORAGE_DATA_KEY, dataset) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(dataset));
  } catch (err) {
    console.warn("Unable to cache PO details dataset", err);
  }
};

const persistMeta = (key = STORAGE_META_KEY, meta) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(meta));
  } catch (err) {
    console.warn("Unable to cache PO details metadata", err);
  }
};

const loadStoredWidths = (key = STORAGE_WIDTHS_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === "object") return parsed;
    return {};
  } catch (err) {
    console.warn("Failed to restore cached PO column widths", err);
    return {};
  }
};

const loadStoredMeta = (key = STORAGE_META_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed) return null;
    return toMetaState(parsed);
  } catch (err) {
    console.warn("Failed to restore cached PO details metadata", err);
    return null;
  }
};

const persistWidths = (key = STORAGE_WIDTHS_KEY, widths = {}) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(widths));
  } catch (err) {
    console.warn("Unable to persist PO column widths", err);
  }
};

const clearStoredMeta = (key = STORAGE_META_KEY) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn("Unable to clear PO details metadata cache", err);
  }
};

const clearStoredWidths = (key = STORAGE_WIDTHS_KEY) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn("Unable to clear PO column widths cache", err);
  }
};

const toMetaState = (meta, fallback = null) => {
  if (!meta && !fallback) return null;
  const merged = { ...(fallback || {}), ...(meta || {}) };
  const raw = merged.updatedAt;
  if (raw instanceof Date) return merged;
  if (typeof raw === "string") {
    const parsed = new Date(raw);
    merged.updatedAt = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    return merged;
  }
  if (fallback?.updatedAt instanceof Date) {
    merged.updatedAt = fallback.updatedAt;
  } else {
    merged.updatedAt = new Date();
  }
  return merged;
};

const cloneDataset = (data) => (data ? JSON.parse(JSON.stringify(data)) : null);
const cloneMeta = (meta) => {
  if (!meta) return null;
  const copy = { ...meta };
  if (copy.updatedAt instanceof Date) copy.updatedAt = new Date(copy.updatedAt);
  return copy;
};

const defaultFiltersFor = (headers) => {
  const filters = {};
  headers.forEach((header) => {
    if (header.key === TSCP_KEY) {
      filters[header.key] = [""];
      return;
    }
    if (header.key === STATUS_KEY) {
      filters[header.key] = [STATUS_NOT_CLOSED_TOKEN];
      return;
    }
    const mode = getFilterModeForHeader(header);
    if (mode === FILTER_MODES.date) {
      filters[header.key] = { from: "", to: "" };
    } else if (mode === FILTER_MODES.multiSelect) {
      filters[header.key] = [];
    } else {
      filters[header.key] = "";
    }
  });
  return filters;
};

const clearFiltersFor = (headers) => {
  const filters = {};
  headers.forEach((header) => {
    const mode = getFilterModeForHeader(header);
    if (mode === FILTER_MODES.date) {
      filters[header.key] = { from: "", to: "" };
    } else if (mode === FILTER_MODES.multiSelect) {
      filters[header.key] = [];
    } else {
      filters[header.key] = "";
    }
  });
  return filters;
};

const toggleSortState = (prev, key) => {
  if (prev.key !== key) return { key, direction: 1 };
  if (prev.direction === 1) return { key, direction: -1 };
  if (prev.direction === -1) return { key: null, direction: 0 };
  return { key, direction: 1 };
};

const applyFilters = (rows, headers, filters) =>
  rows.filter((row) =>
    headers.every((header) => {
      const filterValue = filters[header.key];
      const cell = row[header.key];
      const mode = getFilterModeForHeader(header);
      if (mode === FILTER_MODES.date) {
        const fromTs = parseShortDate(filterValue?.from);
        const toTs = parseShortDate(filterValue?.to);
        const cellTs = parseShortDate(cell);
        if (fromTs && (!cellTs || cellTs < fromTs)) return false;
        if (toTs && (!cellTs || cellTs > toTs)) return false;
        return true;
      }
      if (mode === FILTER_MODES.multiSelect) {
        const selected = Array.isArray(filterValue) ? filterValue : [];
        if (!selected.length) return true;
        const normalizedCell = normaliseTextValue(cell);
        if (header.key === STATUS_KEY && selected.includes(STATUS_NOT_CLOSED_TOKEN)) {
          if (normalizedCell !== "closed") return true;
          return selected.some((value) => value !== STATUS_NOT_CLOSED_TOKEN && value === normalizedCell);
        }
        return selected.some((value) => value === normalizedCell);
      }
      if (mode === FILTER_MODES.multiText) {
        const tokens = parseMultiValueQuery(filterValue);
        if (!tokens.length) return true;
        const normalizedCell = normaliseTextValue(cell);
        return tokens.some((token) => token === normalizedCell);
      }
      const term = String(filterValue || "").trim().toLowerCase();
      if (!term) return true;
      return String(cell ?? "").toLowerCase().includes(term);
    })
  );

const applySorting = (rows, headers, sortState) => {
  if (!sortState.key || sortState.direction === 0) return rows;
  const header = headers.find((h) => h.key === sortState.key);
  if (!header) return rows;
  const direction = sortState.direction;
  const type = header.type;
  return [...rows].sort((a, b) => {
    const av = a[header.key];
    const bv = b[header.key];
    if (type === "date") {
      const aTs = parseShortDate(av) ?? 0;
      const bTs = parseShortDate(bv) ?? 0;
      return direction * (aTs - bTs);
    }
    if (type === "number") {
      const aNum = typeof av === "number" ? av : Number(String(av).replace(/,/g, ""));
      const bNum = typeof bv === "number" ? bv : Number(String(bv).replace(/,/g, ""));
      return direction * ((aNum || 0) - (bNum || 0));
    }
    return direction * String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" });
  });
};

const paginate = (rows, page, pageSize) => {
  if (pageSize === "All") return rows;
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
};

export default function POs({ sandbox = false } = {}) {
  const location = useLocation();
  const storageSuffix = sandbox ? "_sandbox" : "";
  const dataStorageKey = `${STORAGE_DATA_KEY}${storageSuffix}`;
  const metaStorageKey = `${STORAGE_META_KEY}${storageSuffix}`;
  const widthsStorageKey = `${STORAGE_WIDTHS_KEY}${storageSuffix}`;
  const pageLabel = sandbox ? `${PO_DETAILS_LABEL}<Sandbox mode>` : PO_DETAILS_LABEL;
  const sandboxBanner = sandbox ? "Sandbox mode — changes stay in this browser only." : "";
  const exportSheetName = sandbox ? `${EXPORT_SHEET_NAME} (Sandbox)` : EXPORT_SHEET_NAME;
  const exportFilePrefix = sandbox ? "po-details-sandbox" : "po-details";
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const activeWhsTokens = useMemo(
    () => (Array.isArray(accessInfo?.activeWhsTokens) ? accessInfo.activeWhsTokens : []),
    [accessInfo?.activeWhsTokens]
  );
  const activeWhsTokenKey = activeWhsTokens.join("|");
  const shouldLimitWarehouses =
    Boolean(accessInfo?.restrictWhs) &&
    activeWhsTokens.length > 0 &&
    !activeWhsTokens.includes(ALL_ACCESS_TOKEN);
  const activeWhsTokenSet = useMemo(() => {
    if (!shouldLimitWarehouses) return null;
    return new Set(activeWhsTokens.map((token) => token.toUpperCase()));
  }, [shouldLimitWarehouses, activeWhsTokens]);

  const [dataset, setDataset] = useState(() => loadStoredDataset(dataStorageKey));
  const [fileMeta, setFileMeta] = useState(() => loadStoredMeta(metaStorageKey));
  const [versionWarning, setVersionWarning] = useState(null);
  const [error, setError] = useState("");
  const [importWarning, setImportWarning] = useState("");
  const [sortState, setSortState] = useState({ key: null, direction: 0 });
  const [filters, setFilters] = useState(() => defaultFiltersFor(dataset?.headers || []));
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [widths, setWidths] = useState(() => loadStoredWidths(widthsStorageKey));
  const [loadingRemote, setLoadingRemote] = useState(true);
  const [saving, setSaving] = useState(false);
  const customerLookupRef = useRef(new Map());
  const [customerLookupVersion, setCustomerLookupVersion] = useState(0);
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const undoDatasetRef = useRef(null);
  const undoMetaRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [holdRules, setHoldRules] = useState([]);
  const holdRulesRef = useRef([]);
  const [holdFields, setHoldFields] = useState([]);
  const tscpTokensRef = useRef(new Set());
  const [tscpTokenVersion, setTscpTokenVersion] = useState(0);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [holdRulesError, setHoldRulesError] = useState("");
  const [holdForm, setHoldForm] = useState({
    columnKey: "",
    operator: "=",
    value: "",
    type: "text",
    label: "",
    applySamePo: true,
  });
  const [holdFormError, setHoldFormError] = useState("");
  const [savingHoldRule, setSavingHoldRule] = useState(false);
  const [deletingHoldRuleId, setDeletingHoldRuleId] = useState(null);
  const [loadingHoldRules, setLoadingHoldRules] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [warehouseLoadError, setWarehouseLoadError] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseAssignModalOpen, setWarehouseAssignModalOpen] = useState(false);
  const [warehouseAssignCode, setWarehouseAssignCode] = useState("");
  const [warehouseAssignOverwrite, setWarehouseAssignOverwrite] = useState(false);
  const [warehouseAssignSaving, setWarehouseAssignSaving] = useState(false);
  const [warehouseAssignError, setWarehouseAssignError] = useState("");
  const [warehouseAssignConflicts, setWarehouseAssignConflicts] = useState([]);
  const [tscpModalOpen, setTscpModalOpen] = useState(false);
  const [tscpWarehouseCode, setTscpWarehouseCode] = useState("");
  const [tscpWarehouseOverwrite, setTscpWarehouseOverwrite] = useState(true);
  const [tscpError, setTscpError] = useState("");
  const [tscpResolvedShipPoint, setTscpResolvedShipPoint] = useState("");
  const [tscpResolveLoading, setTscpResolveLoading] = useState(false);
  const [tscpResolveError, setTscpResolveError] = useState("");
  const [tscpGuardrailError, setTscpGuardrailError] = useState("");
  const [tscpGenerating, setTscpGenerating] = useState(false);
  const [tscpSelectedCustCode, setTscpSelectedCustCode] = useState("");
  const [tscpPrecheckModalOpen, setTscpPrecheckModalOpen] = useState(false);
  const [tscpPrecheckRunning, setTscpPrecheckRunning] = useState(false);
  const [tscpPrecheckResult, setTscpPrecheckResult] = useState(null);
  const [tscpPrecheckSession, setTscpPrecheckSession] = useState(null);
  const tscpPrecheckRequestIdRef = useRef(0);
  const tscpExportRequestIdRef = useRef(0);
  const tscpPrecheckAbortRef = useRef(null);
  const [pricingResults, setPricingResults] = useState({});
  const [pricingSummary, setPricingSummary] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [pricingRefreshKey, setPricingRefreshKey] = useState(0);
  const pricingRequestKeyRef = useRef("");
  const pricingCompletedKeyRef = useRef("");
  const pricingAbortRef = useRef(null);
  const pricingRequestIdRef = useRef(0);
  const [overrideModal, setOverrideModal] = useState({
    open: false,
    poDetailId: null,
    reason: "",
    error: "",
  });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [revokeModal, setRevokeModal] = useState({
    open: false,
    poDetailId: null,
    reason: "",
    error: "",
  });
  const [revokeSaving, setRevokeSaving] = useState(false);
  const [cancelFollowupsByPoDetailId, setCancelFollowupsByPoDetailId] = useState({});
  const [cancelFollowupSummary, setCancelFollowupSummary] = useState(null);
  const [cancelFollowupPanel, setCancelFollowupPanel] = useState(() => buildCancelFollowupPanelState(null));
  const [cancelFollowupSaving, setCancelFollowupSaving] = useState(false);
  const [cancelFollowupProgressAction, setCancelFollowupProgressAction] = useState({
    loading: "",
    message: "",
    tone: "",
  });
  const [cancelFollowupIlsAction, setCancelFollowupIlsAction] = useState({
    loading: "",
    message: "",
    tone: "",
  });
  const [cancelFollowupCorrection, setCancelFollowupCorrection] = useState(emptyCancelFollowupCorrectionState);
  const [cancelFollowupReview, setCancelFollowupReview] = useState({
    open: false,
    action: "",
    followup: null,
    refreshing: false,
    error: "",
    refreshedAt: "",
  });
  const cancelFollowupAutoRefreshKeyRef = useRef("");
  const cancelFollowupManualCloseSubmittingRef = useRef(false);
  const [cancelFollowupCloseState, setCancelFollowupCloseState] = useState({
    open: false,
    status: "DONE",
    reason: "",
    error: "",
  });
  const selectionModeRef = useRef("NORMAL");
  const tableContainerRef = useRef(null);
  const [missingPoBannerDismissed, setMissingPoBannerDismissed] = useState(false);
  const currentVersionDate = useMemo(() => parseFilenameVersionDate(fileMeta?.name || ""), [fileMeta?.name]);
  const annotateDatasetWithHold = useCallback(
    (nextDataset, rules = holdRulesRef.current) => annotateDatasetWithHoldColumn(nextDataset, rules),
    []
  );
  const annotateDatasetWithTscp = useCallback(
    (nextDataset, tokens = tscpTokensRef.current) => annotateDatasetWithTscpColumn(nextDataset, tokens),
    []
  );

  useEffect(() => {
    holdRulesRef.current = holdRules;
    setDataset((prev) => {
      if (!prev) return prev;
      const next = annotateDatasetWithHold(prev, holdRules);
      return next === prev ? prev : next;
    });
  }, [holdRules, annotateDatasetWithHold]);

  useEffect(() => {
    setDataset((prev) => {
      if (!prev) return prev;
      const next = annotateDatasetWithTscp(prev);
      if (next === prev) return prev;
      const annotated = annotateDatasetWithHold(next);
      persistDataset(dataStorageKey, annotated);
      return annotated;
    });
  }, [annotateDatasetWithHold, annotateDatasetWithTscp, dataStorageKey, tscpTokenVersion]);

  useEffect(() => {
    if (!holdFields.length) return;
    setHoldForm((prev) => {
      const existing = holdFields.find((field) => field.columnKey === prev.columnKey);
      if (existing) {
        const nextOperator = existing.type === "text" ? "=" : prev.operator;
        return {
          ...prev,
          columnKey: existing.columnKey,
          type: existing.type,
          label: existing.label,
          operator: nextOperator,
        };
      }
      const first = holdFields[0];
      return {
        columnKey: first.columnKey,
        operator: "=",
        value: "",
        type: first.type,
        label: first.label,
        applySamePo: true,
      };
    });
  }, [holdFields]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(null);
  useEffect(() => {
    const handleCopy = (event) => {
      if (selectionModeRef.current !== "PO_ONLY") return;
      const container = tableContainerRef.current;
      if (!container) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const anchorNode = selection.anchorNode;
      if (anchorNode && !container.contains(anchorNode)) return;
      const poNodes = Array.from(container.querySelectorAll(".po-copy-source"));
      if (!poNodes.length) return;
      const values = [];
      const seen = new Set();
      poNodes.forEach((node) => {
        let intersects = false;
        try {
          intersects = range.intersectsNode(node);
        } catch (err) {
          intersects = false;
        }
        if (!intersects) return;
        const value = (node.getAttribute("data-po") || node.textContent || "").trim();
        if (!value || seen.has(value)) return;
        seen.add(value);
        values.push(value);
      });
      if (!values.length) return;
      event.preventDefault();
      event.clipboardData?.setData("text/plain", values.join("\n"));
    };
    document.addEventListener("copy", handleCopy);
    return () => {
      document.removeEventListener("copy", handleCopy);
    };
  }, []);
  useEffect(() => {
    if (!dataset?.headers?.length) {
      setVisibleColumnKeys(null);
      return;
    }
    const headerKeys = dataset.headers.map((header) => header.key);
    setVisibleColumnKeys((prev) => {
      if (Array.isArray(prev) && prev.length) {
        const filtered = prev.filter((key) => headerKeys.includes(key));
        if (filtered.length === prev.length) return prev;
        if (filtered.length) return filtered;
      }
      if (Array.isArray(prev) && prev.length === 0) return prev;
      const defaults = dataset.headers
        .filter((header) => DEFAULT_VISIBLE_LABELS.has(canonicalizeHeaderLabel(header.label)))
        .map((header) => header.key);
      if (defaults.length) return defaults;
      return headerKeys;
    });
  }, [dataset?.headers]);
  const visibleColumnSet = useMemo(
    () => (Array.isArray(visibleColumnKeys) ? new Set(visibleColumnKeys) : null),
    [visibleColumnKeys]
  );
  const visibleHeaders = useMemo(() => {
    if (!dataset?.headers) return [];
    if (!visibleColumnSet) return dataset.headers;
    return dataset.headers.filter((header) => visibleColumnSet.has(header.key));
  }, [dataset?.headers, visibleColumnSet]);
  useEffect(() => {
    if (!sortState.key) return;
    if (Array.isArray(visibleColumnKeys) && visibleColumnKeys.length && !visibleColumnKeys.includes(sortState.key)) {
      setSortState({ key: null, direction: 0 });
    }
  }, [visibleColumnKeys, sortState.key]);
  const columnOptions = useMemo(() => {
    if (!dataset?.headers) return [];
    return dataset.headers.map((header) => ({
      value: header.key,
      label: header.label || header.key,
    }));
  }, [dataset?.headers]);
  const handleVisibleColumnsChange = useCallback(
    (nextValues = []) => {
      if (!dataset?.headers?.length) return;
      const headerKeys = new Set(dataset.headers.map((header) => header.key));
      const filtered = (nextValues || []).filter((key) => headerKeys.has(key));
      setVisibleColumnKeys(filtered);
    },
    [dataset?.headers]
  );
  const lineStatusKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], LINE_STATUS_LABEL),
    [dataset?.headers]
  );
  const createDateHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], "Create Date"),
    [dataset?.headers]
  );
  const mabdHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], "MABD"),
    [dataset?.headers]
  );
  const walmartItemHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], "Walmart item No."),
    [dataset?.headers]
  );
  const whseCodeHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], WHSE_CODE_LABEL),
    [dataset?.headers]
  );
  const shipPointHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], SHIP_POINT_LABEL),
    [dataset?.headers]
  );
  const drilldownParams = useMemo(
    () => parsePoDrilldownParams(location.search || ""),
    [location.search]
  );
  const productCodeHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], PRODUCT_CODE_LABEL),
    [dataset?.headers]
  );
  const poNumberKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], PO_NUMBER_LABEL),
    [dataset?.headers]
  );
  const cancelledDateHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], CANCELLED_DATE_LABEL),
    [dataset?.headers]
  );
  const custCodeHeaderKey = useMemo(
    () => findHeaderKeyByLabel(dataset?.headers || [], CUST_CODE_LABEL) || CUST_CODE_COLUMN.key,
    [dataset?.headers]
  );
  const multiSelectOptions = useMemo(() => {
    if (!dataset?.headers || !dataset.data) return {};
    const optionsByKey = {};
    dataset.headers.forEach((header) => {
      if (STATIC_MULTI_SELECT_OPTIONS[header.key]) {
        optionsByKey[header.key] = STATIC_MULTI_SELECT_OPTIONS[header.key];
        return;
      }
      const canonical = canonicalizeHeaderLabel(header.label);
      if (!MULTI_SELECT_LABELS.has(canonical)) return;
      const seen = new Set();
      const options = [];
      dataset.data.forEach((record) => {
        const raw = record?.[header.key];
        const label = String(raw ?? "").trim();
        if (!label) return;
        const normalized = label.toLowerCase();
        if (seen.has(normalized)) return;
        seen.add(normalized);
        options.push({ value: normalized, label });
      });
      options.sort((a, b) => a.label.localeCompare(b.label));
      if (header.key === STATUS_KEY) {
        options.unshift({ value: STATUS_NOT_CLOSED_TOKEN, label: "Not Closed" });
      }
      optionsByKey[header.key] = options;
    });
    return optionsByKey;
  }, [dataset]);
  const selectedPoNumbers = useMemo(() => {
    if (!dataset?.data || !poNumberKey) return [];
    const seen = new Set();
    const list = [];
    dataset.data.forEach((row) => {
      if (!row || typeof row !== "object") return;
      if (!selectedRows.has(row._id)) return;
      const value = String(row[poNumberKey] ?? "").trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      list.push(value);
    });
    return list;
  }, [dataset?.data, poNumberKey, selectedRows]);
  const selectedCustOptions = useMemo(() => {
    if (!dataset?.data || !custCodeHeaderKey) return [];
    const map = new Map();
    dataset.data.forEach((row) => {
      if (!row || typeof row !== "object") return;
      if (!selectedRows.has(row._id)) return;
      const raw = String(row[custCodeHeaderKey] ?? "").trim();
      if (!raw) return;
      const upper = raw.toUpperCase();
      if (!map.has(upper)) {
        map.set(upper, raw);
      }
    });
    const options = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [dataset?.data, custCodeHeaderKey, selectedRows]);
  const tscpSingleCustCode = selectedCustOptions.length === 1 ? selectedCustOptions[0].value : "";
  const tscpCustCodeRequired = selectedCustOptions.length > 1;
  const tscpFilteredPoNumbers = useMemo(() => {
    if (!tscpCustCodeRequired) return selectedPoNumbers;
    if (!dataset?.data || !poNumberKey || !custCodeHeaderKey) return [];
    if (!tscpSelectedCustCode) return [];
    const target = tscpSelectedCustCode.toUpperCase();
    const seen = new Set();
    const list = [];
    dataset.data.forEach((row) => {
      if (!row || typeof row !== "object") return;
      if (!selectedRows.has(row._id)) return;
      const poValue = String(row[poNumberKey] ?? "").trim();
      if (!poValue || seen.has(poValue)) return;
      const rawCust = String(row[custCodeHeaderKey] ?? "").trim().toUpperCase();
      if (rawCust !== target) return;
      seen.add(poValue);
      list.push(poValue);
    });
    return list;
  }, [
    dataset?.data,
    poNumberKey,
    custCodeHeaderKey,
    selectedRows,
    selectedPoNumbers,
    tscpCustCodeRequired,
    tscpSelectedCustCode,
  ]);
  const tscpSelectedCustLabel = useMemo(() => {
    if (!tscpSelectedCustCode) return "";
    const match = selectedCustOptions.find((option) => option.value === tscpSelectedCustCode);
    return match?.label || tscpSelectedCustCode;
  }, [tscpSelectedCustCode, selectedCustOptions]);
  const tscpSelectedWarehouseOption = useMemo(
    () => warehouseOptions.find((option) => option.code === tscpWarehouseCode),
    [warehouseOptions, tscpWarehouseCode]
  );
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    if (!tscpModalOpen || !tscpWarehouseCode || !tscpSingleCustCode) {
      setTscpResolvedShipPoint("");
      setTscpResolveError("");
      setTscpResolveLoading(false);
      return () => controller.abort();
    }

    const runResolve = async () => {
      setTscpResolveLoading(true);
      setTscpResolveError("");
      setTscpResolvedShipPoint("");
      try {
        const currentUser = getCurrentUser();
        const headers = {};
        if (currentUser?.email) headers["X-User-Email"] = currentUser.email;
        if (currentUser?.role) headers["X-User-Role"] = currentUser.role || "";
        const params = new URLSearchParams({
          cust_code: tscpSingleCustCode,
          whse_code: tscpWarehouseCode,
        });
        const resp = await fetch(`${API_BASE}/api/admin/ship-points/resolve?${params.toString()}`, {
          headers,
          signal: controller.signal,
        });
        const payload = await resp.json().catch(() => ({}));
        if (cancelled || controller.signal.aborted) return;
        if (!resp.ok || payload?.ok === false) {
          throw new Error(
            payload?.message ||
              payload?.error ||
              "Unable to resolve WM Ship Point for the selected customer and warehouse."
          );
        }
        setTscpResolvedShipPoint(String(payload?.ship_point || "").trim());
      } catch (err) {
        if (err?.name === "AbortError" || controller.signal.aborted) return;
        if (!cancelled) {
          setTscpResolvedShipPoint("");
          setTscpResolveError(
            err?.message || "Unable to resolve WM Ship Point for the selected customer and warehouse."
          );
        }
      } finally {
        if (!cancelled) {
          setTscpResolveLoading(false);
        }
      }
    };

    runResolve();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [tscpModalOpen, tscpSingleCustCode, tscpWarehouseCode]);
  const tscpSelectedPoCountLabel = tscpCustCodeRequired
    ? `${tscpFilteredPoNumbers.length} of ${selectedPoNumbers.length}`
    : `${tscpFilteredPoNumbers.length}`;
  const tscpPrecheckSummaryRows = Array.isArray(tscpPrecheckResult?.summary)
    ? tscpPrecheckResult.summary
    : [];
  const tscpPrecheckMappingErrors = Array.isArray(tscpPrecheckResult?.mapping_errors)
    ? tscpPrecheckResult.mapping_errors
    : [];
  const tscpPrecheckNoApproved = Array.isArray(tscpPrecheckResult?.po_numbers_with_no_approved_lines)
    ? tscpPrecheckResult.po_numbers_with_no_approved_lines
    : [];
  const tscpPrecheckStatus = (tscpPrecheckResult?.status || "").toLowerCase();
  const tscpPrecheckWarehouseLabel =
    tscpPrecheckResult?.warehouse_code || tscpPrecheckSession?.warehouseCode || tscpWarehouseCode || "";
  const tscpPrecheckInventorySource =
    tscpPrecheckResult?.inventory_source ||
    (tscpPrecheckResult?.inventory_refreshed ? "snapshot_fallback" : "snapshot");
  const tscpPrecheckSnapshotTimestamp =
    tscpPrecheckResult?.snapshot_generated_at || tscpPrecheckResult?.inventory_snapshot_generated_at || "";
  const tscpPrecheckSnapshotLabel = tscpPrecheckSnapshotTimestamp
    ? formatPrecheckTimestamp(tscpPrecheckSnapshotTimestamp)
    : "";
  const tscpPrecheckSnapshotAge = formatAgeSeconds(
    Number(tscpPrecheckResult?.snapshot_age_seconds ?? tscpPrecheckResult?.inventory_age_seconds)
  );
  const tscpPrecheckSnapshotIsStale = Boolean(tscpPrecheckResult?.snapshot_is_stale);
  const tscpPrecheckBackgroundRefreshStarted = Boolean(tscpPrecheckResult?.background_refresh_started);
  const tscpPrecheckInventorySourceLabel =
    tscpPrecheckInventorySource === "live"
      ? "Live inventory"
      : tscpPrecheckInventorySource === "snapshot_fallback"
        ? "New snapshot created for this check"
        : "Snapshot";
  const tscpPrecheckSnapshotMessage =
    typeof tscpPrecheckResult?.message === "string" && tscpPrecheckResult.message.trim()
      ? tscpPrecheckResult.message.trim()
      : tscpPrecheckSnapshotLabel
        ? `${tscpPrecheckInventorySourceLabel} generated: ${tscpPrecheckSnapshotLabel}${
            tscpPrecheckSnapshotAge ? ` (age ${tscpPrecheckSnapshotAge})` : ""
          }`
        : `Inventory source: ${tscpPrecheckInventorySourceLabel}`;
  useEffect(() => {
    if (tscpModalOpen && selectedPoNumbers.length === 0) {
      if (tscpPrecheckAbortRef.current) {
        console.debug("[TSCP] precheck abort", {
          requestId: tscpPrecheckRequestIdRef.current,
          sessionId: tscpPrecheckSession?.sessionId || null,
          reason: "selection-cleared",
        });
        tscpPrecheckAbortRef.current.abort();
        tscpPrecheckAbortRef.current = null;
      }
      tscpPrecheckRequestIdRef.current += 1;
      setTscpModalOpen(false);
      setTscpWarehouseCode("");
      setTscpError("");
      setTscpPrecheckModalOpen(false);
      setTscpPrecheckResult(null);
      setTscpPrecheckSession(null);
      setTscpPrecheckRunning(false);
    }
  }, [selectedPoNumbers.length, tscpModalOpen, tscpPrecheckSession]);
  useEffect(() => {
    if (!tscpModalOpen) return;
    if (selectedCustOptions.length === 1) {
      setTscpSelectedCustCode(selectedCustOptions[0].value);
      return;
    }
    if (selectedCustOptions.length === 0) {
      setTscpSelectedCustCode("");
      return;
    }
    setTscpSelectedCustCode((prev) => {
      if (prev && selectedCustOptions.some((option) => option.value === prev)) {
        return prev;
      }
      return "";
    });
  }, [tscpModalOpen, selectedCustOptions]);
  useEffect(() => {
    console.debug("[TSCP] state", {
      tscpModalOpen,
      tscpPrecheckModalOpen,
      tscpPrecheckRunning,
      tscpGenerating,
      precheckSessionId: tscpPrecheckSession?.sessionId || null,
      warehouseCode: tscpWarehouseCode,
      selectedPoCount: selectedPoNumbers.length,
      filteredPoCount: tscpFilteredPoNumbers.length,
      selectedCustCode: tscpSelectedCustCode || "",
    });
  }, [
    tscpModalOpen,
    tscpPrecheckModalOpen,
    tscpPrecheckRunning,
    tscpGenerating,
    tscpPrecheckSession,
    tscpWarehouseCode,
    selectedPoNumbers.length,
    tscpFilteredPoNumbers.length,
    tscpSelectedCustCode,
  ]);
  useEffect(() => {
    return () => {
      if (tscpPrecheckAbortRef.current) {
        tscpPrecheckAbortRef.current.abort();
        tscpPrecheckAbortRef.current = null;
      }
    };
  }, []);
  const holdFieldValueOptions = useMemo(() => {
    if (!holdFields.length || !dataset?.headers?.length || !dataset?.data?.length) return {};
    const map = {};
    holdFields.forEach((field) => {
      if (field.type !== "text") return;
      const headerKey = dataset.headers.find((header) => header.key === field.columnKey)?.key;
      if (!headerKey) return;
      const seen = new Set();
      const values = [];
      dataset.data.forEach((row) => {
        const raw = row?.[headerKey];
        const label = String(raw ?? "").trim();
        if (!label) return;
        const normalized = label.toLowerCase();
        if (seen.has(normalized)) return;
        seen.add(normalized);
        values.push(label);
      });
      values.sort((a, b) => a.localeCompare(b));
      if (values.length) {
        map[field.columnKey] = values;
      }
    });
    return map;
  }, [dataset?.data, dataset?.headers, holdFields]);
  const currentHoldField = useMemo(
    () => holdFields.find((field) => field.columnKey === holdForm.columnKey),
    [holdFields, holdForm.columnKey]
  );
  const accessScopedRows = useMemo(() => {
    if (!dataset?.data) return [];
    const columns = (dataset.headers || []).map((header) => header.key);
    return filterRowsByAccess(dataset.data, {
      columns,
      custKeys: ["cust_code", "customer", "customer_code"],
      whsKeys: ["whse_code", "warehouse", "warehouse_code"],
      allowBlank: true,
      accessVersion,
    });
  }, [dataset, accessVersion]);

  const inputRef = useRef(null);
  const dragRef = useRef({ col: "", startX: 0, startW: 0 });
  const headerCheckboxRef = useRef(null);

  useEffect(() => {
    persistWidths(widthsStorageKey, widths);
  }, [widths, widthsStorageKey]);
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "pos");
  const importDisabled = permissionLevel !== "Full";
  const isAdminRole = String(me?.role || "").trim().toLowerCase() === "admin";
  const canOverridePricing = isAdminRole && permissionLevel === "Full";

  useEffect(() => {
    let cancelled = false;
    const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
    const loadRemote = async () => {
      setPricingError("");
      setPricingSummary(null);
      setPricingLoading(false);
      setLoadingRemote(true);
      try {
        const resp = await fetch(`${API_BASE}/po-tracker`, { headers });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to load PO details dataset.");
        }
        if (cancelled) return;
        const payloadTokens = normalizeTscpTokens(payload?.tscpTokens || []);
        tscpTokensRef.current = payloadTokens;
        setTscpTokenVersion((version) => version + 1);
        if (payload.dataset) {
          const ensured = ensureAuditColumnsForDisplay(payload.dataset, { customerMap: customerLookupRef.current });
          const withTscp = annotateDatasetWithTscpColumn(ensured, payloadTokens);
          const annotated = annotateDatasetWithHold(withTscp, payload.holdRules || holdRulesRef.current);
          setDataset(annotated);
          setFilters(defaultFiltersFor(annotated.headers || []));
          setSelectedRows(new Set());
          setPage(1);
          filterHeaderRef.current = annotated.headers ? annotated.headers.map((header) => header.key).join("|") : "";
        }
        if (Array.isArray(payload.holdRules)) {
          setHoldRules(payload.holdRules);
        }
        if (Array.isArray(payload.holdFields)) {
          setHoldFields(payload.holdFields);
        }
        setCancelFollowupsByPoDetailId(payload.cancelFollowupsByPoDetailId || {});
        setCancelFollowupSummary(payload.cancelFollowupSummary || null);
        if (payload.meta) {
          setFileMeta((prev) => toMetaState(payload.meta, prev));
        }
        undoDatasetRef.current = null;
        undoMetaRef.current = null;
        setCanUndo(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load PO details dataset.");
        }
      } finally {
        if (!cancelled) setLoadingRemote(false);
      }
    };
    loadRemote();
    return () => {
      cancelled = true;
    };
  }, [me?.email, me?.role, annotateDatasetWithHold]);

  useEffect(() => {
    if (!dataset?.headers?.length || !drilldownParams) return;
    const nextFilters = clearFiltersFor(dataset.headers);
    let hasScopedFilter = false;
    const applyPoBaseFilter = shouldApplyMrpPoBaseFilter({
      dateField: drilldownParams.dateField,
      poBaseFilterToken: drilldownParams.poBaseFilterToken,
    });

    if (drilldownParams.dateField) {
      const headerLabel = DRILLDOWN_PO_DATE_FIELD_TO_LABEL[drilldownParams.dateField];
      const dateHeaderKey =
        headerLabel === "Create Date"
          ? createDateHeaderKey
          : headerLabel === "MABD"
            ? mabdHeaderKey
            : "";
      if (dateHeaderKey) {
        nextFilters[dateHeaderKey] = {
          from: drilldownParams.dateFrom || "",
          to: drilldownParams.dateTo || "",
        };
        hasScopedFilter = true;
      }
    }

    if (drilldownParams.walmartItemNo && walmartItemHeaderKey) {
      nextFilters[walmartItemHeaderKey] = [drilldownParams.walmartItemNo.toLowerCase()];
      hasScopedFilter = true;
    }
    if (drilldownParams.whseCode && whseCodeHeaderKey) {
      nextFilters[whseCodeHeaderKey] = [drilldownParams.whseCode.toLowerCase()];
      hasScopedFilter = true;
    }
    if (drilldownParams.shipPoint && shipPointHeaderKey) {
      nextFilters[shipPointHeaderKey] = [drilldownParams.shipPoint.toLowerCase()];
      hasScopedFilter = true;
    }

    if (applyPoBaseFilter) {
      nextFilters[TSCP_KEY] = ["yes"];
      if (lineStatusKey) {
        nextFilters[lineStatusKey] = ["approved"];
      }
      hasScopedFilter = true;
    }

    if (!hasScopedFilter) return;
    setFilters(nextFilters);
    setPage(1);
  }, [
    dataset?.headers,
    drilldownParams,
    createDateHeaderKey,
    mabdHeaderKey,
    walmartItemHeaderKey,
    whseCodeHeaderKey,
    shipPointHeaderKey,
    lineStatusKey,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadCustomers = async () => {
      try {
        const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
        const resp = await fetch(`${API_BASE}/customers`, { headers });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to load customers dataset.");
        }
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        const map = new Map();
        rows.forEach((row) => {
          const supplier = normalizeSupplierIdentifier(row?.wm_supplier_no);
          const code = String(row?.cust_code || "").trim();
          if (!supplier || !code) return;
          map.set(supplier, code);
        });
        if (!cancelled) {
          customerLookupRef.current = map;
          setCustomerLookupVersion((version) => version + 1);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Unable to load customers lookup", err);
        }
      }
    };
    loadCustomers();
    return () => {
      cancelled = true;
    };
  }, [me?.email, me?.role]);

  useEffect(() => {
    let cancelled = false;
    const buildMatchTokens = (...values) => {
      const tokens = [];
      values.forEach((value) => {
        const trimmed = String(value || "").trim();
        if (!trimmed) return;
        const upper = trimmed.toUpperCase();
        if (!tokens.includes(upper)) tokens.push(upper);
      });
      return tokens;
    };

    const loadWarehouses = async () => {
      setWarehousesLoading(true);
      setWarehouseLoadError("");
      try {
        const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
        const resp = await fetch(`${API_BASE}/warehouses`, { headers });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to load warehouses dataset.");
        }
        if (cancelled) return;
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        let options = rows
          .filter((row) => row && row.whse_code)
          .map((row) => ({
            code: String(row.whse_code || "").trim(),
            label: row.whse_name
              ? `${String(row.whse_code || "").trim()} - ${row.whse_name}`
              : String(row.whse_code || "").trim(),
            shipPoint: String(row.wm_ship_point || "").trim(),
            matchTokens: buildMatchTokens(row.whse_code, row.wms_code, row.wm_ship_point),
          }));
        options.sort((a, b) => a.code.localeCompare(b.code));
        if (shouldLimitWarehouses && activeWhsTokenSet) {
          options = options.filter((option) =>
            option.matchTokens?.some((token) => activeWhsTokenSet.has(token))
          );
        }
        if (!options.length) {
          setWarehouseLoadError(
            shouldLimitWarehouses
              ? "No warehouse access is available for your account."
              : "No warehouse records are configured."
          );
        } else {
          setWarehouseLoadError("");
        }
        setWarehouseOptions(
          options.map((option) => ({
            code: option.code,
            label: option.label,
            shipPoint: option.shipPoint,
          }))
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Unable to load warehouse list", err);
          setWarehouseOptions([]);
          setWarehouseLoadError(err?.message || "Unable to load warehouses dataset.");
        }
      } finally {
        if (!cancelled) {
          setWarehousesLoading(false);
        }
      }
    };
    loadWarehouses();
    return () => {
      cancelled = true;
    };
  }, [me?.email, me?.role, shouldLimitWarehouses, activeWhsTokenKey, activeWhsTokenSet]);

  useEffect(() => {
    if (!tscpWarehouseCode) return;
    if (!warehouseOptions.some((option) => option.code === tscpWarehouseCode)) {
      setTscpWarehouseCode("");
    }
  }, [warehouseOptions, tscpWarehouseCode]);

  useEffect(() => {
    if (!warehouseAssignCode) return;
    if (!warehouseOptions.some((option) => option.code === warehouseAssignCode)) {
      setWarehouseAssignCode("");
    }
  }, [warehouseOptions, warehouseAssignCode]);

  const persistRemote = useCallback(
    async (nextDataset, nextMeta, importContext = null) => {
      if (sandbox) return { ok: true };
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const payloadMeta = { ...(nextMeta || {}) };
      const updatedAt = payloadMeta.updatedAt instanceof Date
        ? payloadMeta.updatedAt.toISOString()
        : typeof payloadMeta.updatedAt === "string"
          ? payloadMeta.updatedAt
          : new Date().toISOString();
      payloadMeta.updatedAt = updatedAt;
      const resp = await fetch(`${API_BASE}/po-tracker`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          dataset: nextDataset,
          meta: payloadMeta,
          ...(importContext ? { importContext } : {}),
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to save PO details dataset.");
      }
      if (Array.isArray(payload?.tscpTokens)) {
        tscpTokensRef.current = normalizeTscpTokens(payload.tscpTokens);
        setTscpTokenVersion((version) => version + 1);
      }
      return payload;
    },
    [sandbox, me?.email, me?.role]
  );

  const reloadPoTrackerAfterSave = useCallback(async () => {
    if (sandbox) return;
    setLoadingRemote(true);
    try {
      const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
      const resp = await fetch(`${API_BASE}/po-tracker`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true || !payload?.dataset) {
        return;
      }
      const payloadTokens = normalizeTscpTokens(payload?.tscpTokens || []);
      tscpTokensRef.current = payloadTokens;
      setTscpTokenVersion((version) => version + 1);
      const ensured = ensureAuditColumnsForDisplay(payload.dataset, { customerMap: customerLookupRef.current });
      const withTscp = annotateDatasetWithTscpColumn(ensured, payloadTokens);
      const annotated = annotateDatasetWithHold(withTscp, payload.holdRules || holdRulesRef.current);
      setDataset(annotated);
      setFilters(defaultFiltersFor(annotated.headers || []));
      setSelectedRows(new Set());
      setPage(1);
      setCancelFollowupsByPoDetailId(payload.cancelFollowupsByPoDetailId || {});
      setCancelFollowupSummary(payload.cancelFollowupSummary || null);
      if (payload.meta) {
        setFileMeta((prev) => toMetaState(payload.meta, prev));
      }
      // Force a fresh pricing validation run after import/save reload.
      pricingRequestKeyRef.current = "";
      pricingCompletedKeyRef.current = "";
      setPricingRefreshKey((prev) => prev + 1);
    } finally {
      setLoadingRemote(false);
    }
  }, [sandbox, me?.email, me?.role, annotateDatasetWithHold]);

  const refreshPoTrackerFollowups = useCallback(
    async (followupId = null) => {
      if (sandbox) return null;
      const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
      const resp = await fetch(`${API_BASE}/po-tracker`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to refresh PO follow-ups.");
      }
      const nextByPoDetailId = payload.cancelFollowupsByPoDetailId || {};
      setCancelFollowupsByPoDetailId(nextByPoDetailId);
      setCancelFollowupSummary(payload.cancelFollowupSummary || null);
      if (followupId) {
        const nextFollowup =
          Object.values(nextByPoDetailId).find((item) => Number(item?.id) === Number(followupId)) || null;
        if (nextFollowup) {
          setCancelFollowupPanel((prev) =>
            prev.followupId === nextFollowup.id
              ? {
                  ...prev,
                  ...buildCancelFollowupPanelState(nextFollowup),
                  assignToMe: prev.assignToMe,
                }
              : prev
          );
        }
      }
      return nextByPoDetailId;
    },
    [sandbox, me?.email, me?.role]
  );

  const headerSignature = useMemo(
    () => (dataset?.headers ? dataset.headers.map((header) => header.key).join("|") : ""),
    [dataset?.headers]
  );
  const filterHeaderRef = useRef("");

  useEffect(() => {
    if (!dataset?.headers) return;
    setWidths((prev) => {
      let changed = false;
      const next = { ...prev };
      dataset.headers.forEach((header) => {
        if (!next[header.key]) {
          const hint = COLUMN_WIDTH_HINTS.get(canonicalizeHeaderLabel(header.label));
          const targetWidth =
            hint !== undefined ? hint : ((header.label?.length || 10) + 6) * 8;
          const approx = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, targetWidth));
          next[header.key] = approx;
          changed = true;
        }
      });
      if (!next[SELECT_COLUMN_KEY]) {
        next[SELECT_COLUMN_KEY] = SELECT_COLUMN_DEFAULT_WIDTH;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [headerSignature, dataset?.headers]);

  useEffect(() => {
    if (!dataset?.headers) return;
    if (filterHeaderRef.current === headerSignature) return;
    filterHeaderRef.current = headerSignature;
    setFilters(defaultFiltersFor(dataset.headers));
  }, [headerSignature, dataset?.headers]);

  useEffect(() => {
    if (!dataset) return;
    persistDataset(dataStorageKey, dataset);
  }, [dataset, dataStorageKey]);

  useEffect(() => {
    if (!dataset || !customerLookupVersion) return;
    setDataset((prev) => {
      if (!prev) return prev;
      const next = applyCustCodeColumn(prev, customerLookupRef.current);
      if (next === prev) return prev;
      const withTscp = annotateDatasetWithTscp(next);
      const annotated = annotateDatasetWithHold(withTscp);
      persistDataset(dataStorageKey, annotated);
      return annotated;
    });
  }, [dataset, customerLookupVersion, dataStorageKey, annotateDatasetWithHold, annotateDatasetWithTscp]);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [dataset]);

  useEffect(() => {
    if (!fileMeta) return;
    persistMeta(metaStorageKey, fileMeta);
  }, [fileMeta, metaStorageKey]);

  const importFileFromUpload = async (file) => {
    if (!file) return;
    if (importDisabled) {
      setError("Import is disabled for your role.");
      return;
    }
    setError("");
    const name = file.name || "PO Details.xlsx";
    setSaving(true);
    setLoadingRemote(true);
    try {
      undoDatasetRef.current = cloneDataset(dataset);
      undoMetaRef.current = cloneMeta(fileMeta);
      setCanUndo(false);
      const { rows, sheetName } = await loadWorkbookFromFile(file);
      const preparedRows = prepareRowsForImport(rows);
      const builtDataset = buildDataset(preparedRows);
      const mergedDataset = mergeDatasetWithHistory(builtDataset, dataset);
      const ensuredDataset = ensureAuditColumnsForDisplay(mergedDataset, { customerMap: customerLookupRef.current });
      const withTscp = annotateDatasetWithTscp(ensuredDataset);
      const nextDataset = annotateDatasetWithHold(withTscp);
      const meta = toMetaState({ name, sheetName, updatedAt: new Date() });
      setDataset(nextDataset);
      setFilters(defaultFiltersFor(nextDataset.headers || []));
      setSelectedRows(new Set());
      setPage(1);
      setFileMeta(meta);
      persistDataset(dataStorageKey, nextDataset);
      persistMeta(metaStorageKey, meta);
      const warning = await saveImportedPoDetails({
        persistRemote,
        reloadPoTrackerAfterSave,
        dataset: nextDataset,
        meta,
        sourceFilename: name,
      });
      setCanUndo(Boolean(undoDatasetRef.current));
      setError("");
      setImportWarning(warning);
    } catch (err) {
      console.error(err);
      setImportWarning("");
      setError(err.message || "Unable to import workbook.");
    } finally {
      setSaving(false);
      setLoadingRemote(false);
    }
  };

  const getVersionWarningForFile = useCallback(
    (file) => {
      if (!file || !currentVersionDate) return null;
      const updateDate = parseFilenameVersionDate(file.name || "");
      if (!updateDate) return null;
      const diff = diffVersionDates(updateDate, currentVersionDate);
      if (diff === null) return null;
      if (diff < 0) {
        return { type: "older", file, currentDate: currentVersionDate, updateDate };
      }
      if (diff > 1) {
        return { type: "skip", file, currentDate: currentVersionDate, updateDate };
      }
      return null;
    },
    [currentVersionDate]
  );

  const handleTriggerUpload = () => {
    if (importDisabled) {
      setError("Import is disabled for your role.");
      return;
    }
    if (saving) return;
    setError("");
    inputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    if (importDisabled) {
      event.target.value = "";
      setError("Import is disabled for your role.");
      return;
    }
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    const warningState = getVersionWarningForFile(file);
    if (warningState) {
      setVersionWarning(warningState);
      return;
    }
    await importFileFromUpload(file);
  };

  const handleCancelVersionWarning = () => {
    setVersionWarning(null);
  };

  const handleConfirmVersionWarning = () => {
    if (!versionWarning?.file) {
      setVersionWarning(null);
      return;
    }
    const pendingFile = versionWarning.file;
    setVersionWarning(null);
    void importFileFromUpload(pendingFile);
  };

  const handleUndoImport = async () => {
    if (!undoDatasetRef.current) return;
    setSaving(true);
    setLoadingRemote(true);
    try {
      const restoredDataset = cloneDataset(undoDatasetRef.current);
      const restoredMeta = cloneMeta(undoMetaRef.current);
      if (!restoredDataset) throw new Error("Nothing to undo.");
      const withTscp = annotateDatasetWithTscp(restoredDataset);
      const annotated = annotateDatasetWithHold(withTscp);
      setDataset(annotated);
      setFilters(defaultFiltersFor(annotated.headers || []));
      setSelectedRows(new Set());
      setPage(1);
      setFileMeta(restoredMeta || null);
      persistDataset(dataStorageKey, annotated);
      if (restoredMeta) {
        persistMeta(metaStorageKey, restoredMeta);
      } else {
        clearStoredMeta(metaStorageKey);
      }
      await persistRemote(annotated, restoredMeta || null);
      await reloadPoTrackerAfterSave();
      undoDatasetRef.current = null;
      undoMetaRef.current = null;
      setCanUndo(false);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to undo import.");
    } finally {
      setSaving(false);
      setLoadingRemote(false);
    }
  };

  const fetchHoldRulesList = useCallback(async () => {
    setLoadingHoldRules(true);
    setHoldRulesError("");
    try {
      const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
      const resp = await fetch(`${API_BASE}/po-tracker/hold-rules`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to load Hold POs rules.");
      }
      if (Array.isArray(payload.rules)) {
        setHoldRules(payload.rules);
      }
      if (Array.isArray(payload.fields)) {
        setHoldFields(payload.fields);
      }
    } catch (err) {
      console.error(err);
      setHoldRulesError(err?.message || "Unable to load Hold POs rules.");
    } finally {
      setLoadingHoldRules(false);
    }
  }, [me?.email, me?.role]);

  const handleOpenHoldModal = useCallback(() => {
    setHoldFormError("");
    setHoldRulesError("");
    setHoldModalOpen(true);
    void fetchHoldRulesList();
  }, [fetchHoldRulesList]);

  const handleCloseHoldModal = useCallback(() => {
    setHoldModalOpen(false);
    setHoldFormError("");
    setHoldRulesError("");
  }, []);

  const handleSaveHoldRule = useCallback(async () => {
    if (!holdForm.columnKey) {
      setHoldFormError("Please choose a field.");
      return;
    }
    const valueText = String(holdForm.value ?? "").trim();
    if (!valueText) {
      setHoldFormError("Please choose a value.");
      return;
    }
    setSavingHoldRule(true);
    setHoldFormError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/po-tracker/hold-rules`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          columnKey: holdForm.columnKey,
          columnLabel: currentHoldField?.label || holdForm.label || "",
          columnType: currentHoldField?.type || holdForm.type,
          operator: holdForm.operator || "=",
          value: valueText,
          applySamePo: holdForm.applySamePo,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to save Hold POs rule.");
      }
      if (Array.isArray(payload.rules)) {
        setHoldRules(payload.rules);
      }
      if (Array.isArray(payload.fields)) {
        setHoldFields(payload.fields);
      }
      setHoldForm((prev) => ({ ...prev, value: "" }));
    } catch (err) {
      console.error(err);
      setHoldFormError(err?.message || "Unable to save Hold POs rule.");
    } finally {
      setSavingHoldRule(false);
    }
  }, [holdForm, currentHoldField, me?.email, me?.role]);

  const handleDeleteHoldRule = useCallback(
    async (ruleId) => {
      if (!ruleId) return;
      setDeletingHoldRuleId(ruleId);
      setHoldRulesError("");
      try {
        const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
        const resp = await fetch(`${API_BASE}/po-tracker/hold-rules/${ruleId}`, {
          method: "DELETE",
          headers,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to delete Hold POs rule.");
        }
        if (Array.isArray(payload.rules)) {
          setHoldRules(payload.rules);
        }
        if (Array.isArray(payload.fields)) {
          setHoldFields(payload.fields);
        }
      } catch (err) {
        console.error(err);
        setHoldRulesError(err?.message || "Unable to delete Hold POs rule.");
      } finally {
        setDeletingHoldRuleId(null);
      }
    },
    [me?.email, me?.role]
  );

  const validateTscpInputs = useCallback(() => {
    if (!selectedPoNumbers.length) {
      setTscpError("Please select at least one PO before generating the TSCP Template.");
      return false;
    }
    if (tscpCustCodeRequired) {
      setTscpError("Selected POs contain multiple Cust Codes. Please export one customer at a time.");
      return false;
    }
    if (!tscpFilteredPoNumbers.length) {
      setTscpError("No PO numbers are available for the current selection.");
      return false;
    }
    if (!tscpWarehouseCode) {
      setTscpError("Please select a Warehouse Code.");
      return false;
    }
    if (tscpResolveLoading) {
      setTscpError("WM Ship Point is still resolving. Please wait a moment and try again.");
      return false;
    }
    if (tscpResolveError) {
      setTscpError(tscpResolveError);
      return false;
    }
    if (!tscpResolvedShipPoint) {
      setTscpError(
        "No active WM Ship Point mapping found for this customer and warehouse. Please configure it in Ship Point Management."
      );
      return false;
    }
    return true;
  }, [
    selectedPoNumbers.length,
    tscpCustCodeRequired,
    tscpFilteredPoNumbers.length,
    tscpResolveError,
    tscpResolveLoading,
    tscpResolvedShipPoint,
    tscpWarehouseCode,
  ]);

  const getTscpPricingGuardrailMessage = useCallback(() => {
    if (!selectedPoNumbers.length || !poNumberKey) return "";
    const sourceRows = accessScopedRows.length ? accessScopedRows : dataset?.data || [];
    if (!sourceRows.length) return "";
    const selectedPoSet = new Set(
      selectedPoNumbers
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
    );
    if (!selectedPoSet.size) return "";

    const blockedByPo = new Map();
    sourceRows.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const poNumber = String(row[poNumberKey] ?? "").trim();
      if (!poNumber || !selectedPoSet.has(poNumber)) return;
      const poDetailId = extractPoDetailId(row);
      if (!poDetailId) return;
      const pricingResult = pricingResults[poDetailId];
      if (!isPricingBlockedResult(pricingResult)) return;

      const walmartItemNo = walmartItemHeaderKey ? String(row[walmartItemHeaderKey] ?? "").trim() : "";
      const productCode = productCodeHeaderKey ? String(row[productCodeHeaderKey] ?? "").trim() : "";
      const details = [];
      if (walmartItemNo) details.push(`Walmart item ${walmartItemNo}`);
      if (productCode) details.push(`Product ${productCode}`);
      const lineLabel = details.length ? details.join(" / ") : `PO Detail ${poDetailId}`;

      if (!blockedByPo.has(poNumber)) blockedByPo.set(poNumber, []);
      blockedByPo.get(poNumber).push(lineLabel);
    });

    if (!blockedByPo.size) return "";
    const poMessages = Array.from(blockedByPo.entries()).map(([poNumber, lines]) => {
      const uniqueLines = Array.from(new Set(lines));
      const preview = uniqueLines.slice(0, 5).join(", ");
      const suffix = uniqueLines.length > 5 ? ` (+${uniqueLines.length - 5} more)` : "";
      return `${poNumber}: ${preview}${suffix}`;
    });
    const poLabel = poMessages.length > 1 ? "POs" : "PO";
    return (
      `Cannot generate TSCP Template because at least one line in the selected ${poLabel} is blocked ` +
      `by Pricing Validation. Please resolve pricing issues (fix price or apply admin override) for all ` +
      `lines in each PO before generating the template. Blocked ${poLabel}: ${poMessages.join(" | ")}`
    );
  }, [
    selectedPoNumbers,
    poNumberKey,
    accessScopedRows,
    dataset?.data,
    pricingResults,
    walmartItemHeaderKey,
    productCodeHeaderKey,
  ]);

  const handleOpenTscpModal = useCallback(() => {
    if (!selectedPoNumbers.length) return;
    console.debug("[TSCP] open modal requested", {
      selectedPoCount: selectedPoNumbers.length,
    });
    const guardrailMessage =
      (selectedCustOptions.length > 1
        ? "Selected POs contain multiple Cust Codes. Please export one customer at a time."
        : "") || getTscpPricingGuardrailMessage();
    if (guardrailMessage) {
      setTscpGuardrailError(guardrailMessage);
      return;
    }
    setTscpGuardrailError("");
    setTscpError("");
    setTscpResolvedShipPoint("");
    setTscpResolveError("");
    setTscpResolveLoading(false);
    setTscpWarehouseOverwrite(true);
    setTscpPrecheckSession(null);
    setTscpPrecheckResult(null);
    setTscpPrecheckModalOpen(false);
    setTscpModalOpen(true);
  }, [selectedPoNumbers.length, selectedCustOptions.length, getTscpPricingGuardrailMessage]);

  const handleCloseTscpModal = useCallback(() => {
    if (tscpGenerating) return;
    console.debug("[TSCP] close modal", {
      precheckRunning: tscpPrecheckRunning,
      precheckModalOpen: tscpPrecheckModalOpen,
      precheckSessionId: tscpPrecheckSession?.sessionId || null,
      warehouseCode: tscpWarehouseCode,
      filteredPoCount: tscpFilteredPoNumbers.length,
    });
    if (tscpPrecheckAbortRef.current) {
      console.debug("[TSCP] precheck abort", {
        requestId: tscpPrecheckRequestIdRef.current,
        sessionId: tscpPrecheckSession?.sessionId || null,
        reason: "close-modal",
        warehouseCode: tscpWarehouseCode,
        filteredPoCount: tscpFilteredPoNumbers.length,
      });
      tscpPrecheckAbortRef.current.abort();
      tscpPrecheckAbortRef.current = null;
    }
    tscpPrecheckRequestIdRef.current += 1;
    setTscpModalOpen(false);
    setTscpWarehouseCode("");
    setTscpWarehouseOverwrite(true);
    setTscpError("");
    setTscpResolvedShipPoint("");
    setTscpResolveError("");
    setTscpResolveLoading(false);
    setTscpSelectedCustCode("");
    setTscpPrecheckModalOpen(false);
    setTscpPrecheckResult(null);
    setTscpPrecheckSession(null);
    setTscpPrecheckRunning(false);
  }, [
    tscpGenerating,
    tscpFilteredPoNumbers.length,
    tscpPrecheckModalOpen,
    tscpPrecheckRunning,
    tscpPrecheckSession,
    tscpWarehouseCode,
  ]);

  useEffect(() => {
    if (!tscpGuardrailError) return;
    if (!selectedPoNumbers.length) {
      setTscpGuardrailError("");
    }
  }, [selectedPoNumbers.length, tscpGuardrailError]);

  useEffect(() => {
    if (!warehouseAssignModalOpen) return;
    if (!selectedPoNumbers.length) {
      setWarehouseAssignModalOpen(false);
      setWarehouseAssignError("");
      setWarehouseAssignConflicts([]);
      setWarehouseAssignOverwrite(false);
    }
  }, [warehouseAssignModalOpen, selectedPoNumbers.length]);

  const handleRunTscpPrecheck = useCallback(async () => {
    const requestId = tscpPrecheckRequestIdRef.current + 1;
    tscpPrecheckRequestIdRef.current = requestId;
    const startedAt = performance.now();
    const session = {
      sessionId: requestId,
      warehouseCode: tscpWarehouseCode,
      poNumbers: [...tscpFilteredPoNumbers],
      selectedCustCode: tscpSelectedCustCode || "",
      overwriteExistingAssignment: Boolean(tscpWarehouseOverwrite),
    };
    setTscpError("");
    console.debug("[TSCP] precheck start", {
      requestId,
      sessionId: session.sessionId,
      warehouseCode: session.warehouseCode,
      filteredPoCount: session.poNumbers.length,
      poNumbers: session.poNumbers,
    });
    if (!validateTscpInputs()) return;
    if (tscpPrecheckAbortRef.current) {
      tscpPrecheckAbortRef.current.abort();
      tscpPrecheckAbortRef.current = null;
    }
    const controller = new AbortController();
    tscpPrecheckAbortRef.current = controller;
    setTscpPrecheckRunning(true);
    setTscpPrecheckResult(null);
    setTscpPrecheckSession(null);
    setTscpPrecheckModalOpen(false);
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/api/tscp/template/precheck`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          warehouse_code: session.warehouseCode,
          po_numbers: session.poNumbers,
        }),
        signal: controller.signal,
      });
      const payload = await resp.json().catch(() => ({}));
      console.debug("[TSCP] precheck response", {
        requestId,
        sessionId: session.sessionId,
        status: resp.status,
        ok: resp.ok,
        elapsedMs: Math.round(performance.now() - startedAt),
        payloadStatus: payload?.status || "",
        snapshotId: payload?.inventory_snapshot_id || null,
        refreshed: payload?.inventory_refreshed ?? null,
        inventorySource: payload?.inventory_source || "",
        snapshotIsStale: payload?.snapshot_is_stale ?? null,
        backgroundRefreshStarted: payload?.background_refresh_started ?? null,
      });
      if (controller.signal.aborted || requestId !== tscpPrecheckRequestIdRef.current) {
        console.debug("[TSCP] precheck stale response ignored", {
          requestId,
          sessionId: session.sessionId,
          warehouseCode: session.warehouseCode,
          filteredPoCount: session.poNumbers.length,
        });
        return;
      }
      if (!resp.ok || payload?.ok === false) {
        const message =
          payload?.message ||
          payload?.error ||
          "Unable to run the TSCP Template Pre-Check.";
        throw new Error(message);
      }
      setTscpPrecheckResult(payload);
      setTscpPrecheckSession(session);
      setTscpPrecheckModalOpen(true);
      console.debug("[TSCP] precheck modal opened", {
        requestId,
        sessionId: session.sessionId,
        warehouseCode: payload?.warehouse_code || session.warehouseCode,
        summaryCount: Array.isArray(payload?.summary) ? payload.summary.length : 0,
      });
    } catch (err) {
      if (err?.name === "AbortError" || controller.signal.aborted) {
        console.debug("[TSCP] precheck aborted", {
          requestId,
          sessionId: session.sessionId,
          elapsedMs: Math.round(performance.now() - startedAt),
          warehouseCode: session.warehouseCode,
          filteredPoCount: session.poNumbers.length,
        });
        return;
      }
      console.error("[TSCP] precheck failed", {
        requestId,
        sessionId: session.sessionId,
        elapsedMs: Math.round(performance.now() - startedAt),
        error: err?.message || String(err),
      });
      setTscpError(err?.message || "Unable to run the TSCP Template Pre-Check.");
    } finally {
      console.debug("[TSCP] precheck finalize", {
        requestId,
        sessionId: session.sessionId,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      if (tscpPrecheckAbortRef.current === controller) {
        tscpPrecheckAbortRef.current = null;
      }
      if (requestId === tscpPrecheckRequestIdRef.current) {
        setTscpPrecheckRunning(false);
      }
    }
  }, [
    validateTscpInputs,
    me?.email,
    me?.role,
    tscpFilteredPoNumbers,
    tscpSelectedCustCode,
    tscpWarehouseCode,
    tscpWarehouseOverwrite,
  ]);

  const executeTscpTemplateDownload = useCallback(async (sessionOverride = null) => {
    const session = sessionOverride || tscpPrecheckSession;
    const requestId = tscpExportRequestIdRef.current + 1;
    tscpExportRequestIdRef.current = requestId;
    const startedAt = performance.now();
    if (!session?.warehouseCode || !Array.isArray(session?.poNumbers) || !session.poNumbers.length) {
      console.error("[TSCP] export blocked: missing frozen session", {
        requestId,
        sessionId: session?.sessionId || null,
        warehouseCode: session?.warehouseCode || "",
        filteredPoCount: Array.isArray(session?.poNumbers) ? session.poNumbers.length : 0,
      });
      setTscpError("Unable to generate TSCP Template.");
      return;
    }
    setTscpGenerating(true);
    setTscpError("");
    console.debug("[TSCP] export start", {
      requestId,
      sessionId: session.sessionId || null,
      warehouseCode: session.warehouseCode,
      warehouseOverwrite: session.overwriteExistingAssignment,
      filteredPoCount: session.poNumbers.length,
      poNumbers: session.poNumbers,
    });
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/po-tracker/tscp-template`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          warehouseCode: session.warehouseCode,
          poNumbers: session.poNumbers,
          warehouse_overwrite: session.overwriteExistingAssignment,
        }),
      });
      console.debug("[TSCP] export response headers", {
        requestId,
        sessionId: session.sessionId || null,
        status: resp.status,
        ok: resp.ok,
        elapsedMs: Math.round(performance.now() - startedAt),
        contentType: resp.headers.get("Content-Type") || "",
        contentDisposition: resp.headers.get("Content-Disposition") || "",
      });
      if (!resp.ok) {
        let message = "Unable to generate TSCP Template.";
        try {
          const payload = await resp.json();
          if (payload?.error === "PO_PRICING_BLOCKED") {
            const blocked = Array.isArray(payload?.blocked) ? payload.blocked : [];
            if (blocked.length) {
              const preview = blocked
                .slice(0, 5)
                .map((item) => {
                  const po = String(item?.po_number || "").trim();
                  const walmartItem = String(item?.walmart_item_no || "").trim();
                  const productCode = String(item?.product_code || "").trim();
                  const detail = walmartItem || productCode || String(item?.po_detail_id || "").trim();
                  return po && detail ? `${po} (${detail})` : po || detail || "Unknown line";
                })
                .join(", ");
              const suffix = blocked.length > 5 ? ` (+${blocked.length - 5} more)` : "";
              message = `${payload?.message || "Cannot generate TSCP Template because some PO lines are blocked by Pricing Validation."} Blocked lines: ${preview}${suffix}`;
            } else {
              message =
                payload?.message ||
                "Cannot generate TSCP Template because some PO lines are blocked by Pricing Validation.";
            }
          } else if (payload?.error === "po_not_found" && Array.isArray(payload?.missing) && payload.missing.length) {
            message = `Unable to find the following PO numbers: ${payload.missing.join(", ")}`;
          } else if (payload?.error === "ship_point_missing") {
            message = "This warehouse is missing WM Ship Point. Please update the Warehouse master first.";
          } else if (payload?.error === "warehouse_conflict") {
            const conflicts = Array.isArray(payload?.conflicts) ? payload.conflicts : [];
            if (conflicts.length) {
              const preview = conflicts
                .slice(0, 5)
                .map((item) => {
                  const po = String(item?.po_number || "").trim();
                  const existing = Array.isArray(item?.existing_whse_codes) ? item.existing_whse_codes.join("/") : "";
                  return existing ? `${po} (${existing})` : po;
                })
                .filter(Boolean)
                .join(", ");
              const suffix = conflicts.length > 5 ? ` (+${conflicts.length - 5} more)` : "";
              message = `Warehouse assignment conflicts detected: ${preview}${suffix}. Enable overwrite to continue.`;
            } else {
              message = payload?.message || "Warehouse assignment conflicts detected. Enable overwrite to continue.";
            }
          } else if (payload?.message) {
            message = payload.message;
          } else if (payload?.error) {
            message = payload.error;
          }
        } catch (err) {
          // ignore JSON parse error
        }
        setTscpError(message);
        return;
      }
      const blob = await resp.blob();
      const disposition = resp.headers.get("Content-Disposition") || "";
      const suggested = filenameFromDisposition(disposition);
      const fallback = `${TSCP_TEMPLATE_FILENAME_PREFIX}_${formatTimestampForTscpFile(new Date())}.xlsx`;
      const filename = suggested || fallback;
      console.debug("[TSCP] export blob ready", {
        requestId,
        sessionId: session.sessionId || null,
        elapsedMs: Math.round(performance.now() - startedAt),
        blobSize: blob?.size ?? null,
        filename,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      setTscpModalOpen(false);
      setTscpWarehouseCode("");
      setTscpWarehouseOverwrite(true);
      setTscpSelectedCustCode("");
      setTscpError("");
      setTscpPrecheckModalOpen(false);
      setTscpPrecheckResult(null);
      setTscpPrecheckSession(null);
    } catch (err) {
      console.error("[TSCP] export failed", {
        requestId,
        sessionId: session.sessionId || null,
        elapsedMs: Math.round(performance.now() - startedAt),
        error: err?.message || String(err),
      });
      setTscpError(err?.message || "Unable to generate TSCP Template.");
    } finally {
      console.debug("[TSCP] export finalize", {
        requestId,
        sessionId: session.sessionId || null,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      setTscpGenerating(false);
    }
  }, [me?.email, me?.role, tscpPrecheckSession]);

  const handleAbortTscpPrecheck = useCallback(() => {
    if (tscpGenerating) return;
    console.debug("[TSCP] precheck modal abort", {
      sessionId: tscpPrecheckSession?.sessionId || null,
      warehouseCode: tscpPrecheckSession?.warehouseCode || tscpWarehouseCode,
      filteredPoCount: Array.isArray(tscpPrecheckSession?.poNumbers)
        ? tscpPrecheckSession.poNumbers.length
        : tscpFilteredPoNumbers.length,
      hasResult: Boolean(tscpPrecheckResult),
    });
    setTscpPrecheckModalOpen(false);
    setTscpPrecheckResult(null);
    setTscpPrecheckSession(null);
  }, [tscpGenerating, tscpFilteredPoNumbers.length, tscpPrecheckResult, tscpPrecheckSession, tscpWarehouseCode]);

  const handleConfirmTscpPrecheck = useCallback(() => {
    if (tscpGenerating) return;
    const session = tscpPrecheckSession;
    console.debug("[TSCP] precheck continue", {
      sessionId: session?.sessionId || null,
      warehouseCode: session?.warehouseCode || tscpWarehouseCode,
      filteredPoCount: Array.isArray(session?.poNumbers) ? session.poNumbers.length : tscpFilteredPoNumbers.length,
      hasResult: Boolean(tscpPrecheckResult),
      resultStatus: tscpPrecheckResult?.status || "",
    });
    if (!session?.warehouseCode || !Array.isArray(session?.poNumbers) || !session.poNumbers.length) {
      console.error("[TSCP] precheck continue blocked: invalid frozen session", {
        sessionId: session?.sessionId || null,
        warehouseCode: session?.warehouseCode || "",
        filteredPoCount: Array.isArray(session?.poNumbers) ? session.poNumbers.length : 0,
      });
      setTscpError("Unable to generate TSCP Template.");
      return;
    }
    executeTscpTemplateDownload(session);
  }, [
    tscpGenerating,
    executeTscpTemplateDownload,
    tscpFilteredPoNumbers.length,
    tscpPrecheckResult,
    tscpPrecheckSession,
    tscpWarehouseCode,
  ]);

  const handleOpenWarehouseAssignModal = useCallback(() => {
    if (!selectedPoNumbers.length || !warehouseOptions.length) return;
    setWarehouseAssignModalOpen(true);
    setWarehouseAssignError("");
    setWarehouseAssignConflicts([]);
    setWarehouseAssignOverwrite(false);
    setWarehouseAssignCode((prev) => {
      if (prev && warehouseOptions.some((option) => option.code === prev)) return prev;
      return warehouseOptions[0]?.code || "";
    });
  }, [selectedPoNumbers.length, warehouseOptions]);

  const handleCloseWarehouseAssignModal = useCallback(() => {
    if (warehouseAssignSaving) return;
    setWarehouseAssignModalOpen(false);
    setWarehouseAssignError("");
    setWarehouseAssignConflicts([]);
    setWarehouseAssignOverwrite(false);
  }, [warehouseAssignSaving]);

  const handleApplyWarehouseAssignment = useCallback(async () => {
    if (sandbox) {
      setWarehouseAssignError("Warehouse assignment is unavailable in sandbox mode.");
      return;
    }
    if (!selectedPoNumbers.length) {
      setWarehouseAssignError("Please select at least one PO.");
      return;
    }
    if (!warehouseAssignCode) {
      setWarehouseAssignError("Please select a warehouse.");
      return;
    }
    setWarehouseAssignSaving(true);
    setWarehouseAssignError("");
    setWarehouseAssignConflicts([]);
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/po-tracker/warehouse-assignment`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          po_numbers: selectedPoNumbers,
          warehouse_code: warehouseAssignCode,
          overwrite: warehouseAssignOverwrite,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        if (resp.status === 409 && payload?.error === "warehouse_conflict") {
          setWarehouseAssignConflicts(Array.isArray(payload?.conflicts) ? payload.conflicts : []);
          setWarehouseAssignError(
            payload?.message ||
              "Some selected POs already have a different warehouse assignment. Enable overwrite to continue."
          );
          return;
        }
        throw new Error(payload?.message || payload?.error || "Unable to update warehouse assignment.");
      }
      setWarehouseAssignModalOpen(false);
      setWarehouseAssignError("");
      setWarehouseAssignConflicts([]);
      setWarehouseAssignOverwrite(false);
      await reloadPoTrackerAfterSave();
    } catch (err) {
      console.error(err);
      setWarehouseAssignError(err?.message || "Unable to update warehouse assignment.");
    } finally {
      setWarehouseAssignSaving(false);
    }
  }, [
    sandbox,
    selectedPoNumbers,
    warehouseAssignCode,
    warehouseAssignOverwrite,
    me?.email,
    me?.role,
    reloadPoTrackerAfterSave,
  ]);


  const openOverrideModal = useCallback(
    (poDetailId) => {
      if (!canOverridePricing || !poDetailId) return;
      setOverrideModal({ open: true, poDetailId, reason: "", error: "" });
    },
    [canOverridePricing]
  );

  const closeOverrideModal = useCallback(() => {
    if (overrideSaving) return;
    setOverrideModal({ open: false, poDetailId: null, reason: "", error: "" });
  }, [overrideSaving]);

  const handleOverrideReasonChange = (event) => {
    const value = event.target.value;
    setOverrideModal((prev) => ({ ...prev, reason: value, error: "" }));
  };

  const handleConfirmOverride = useCallback(async () => {
    if (!overrideModal.poDetailId) return;
    const reason = String(overrideModal.reason || "").trim();
    if (!reason) {
      setOverrideModal((prev) => ({ ...prev, error: "Reason is required." }));
      return;
    }
    setOverrideSaving(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}${PRICING_OVERRIDE_ENDPOINT}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ po_detail_id: overrideModal.poDetailId, reason }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        console.error("Pricing override failed", { status: resp.status, payload });
        throw new Error(payload?.message || payload?.error || "Unable to create override.");
      }
      setOverrideModal({ open: false, poDetailId: null, reason: "", error: "" });
      setPricingRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Pricing override request error", err);
      const rawMessage = err?.message || "Unable to create override.";
      const friendlyMessage =
        rawMessage === "save_failed" || rawMessage.toLowerCase().includes("save_failed")
          ? "Override failed. Please try again."
          : rawMessage;
      setOverrideModal((prev) => ({ ...prev, error: friendlyMessage }));
    } finally {
      setOverrideSaving(false);
    }
  }, [overrideModal.poDetailId, overrideModal.reason, me?.email, me?.role]);

  const openRevokeModal = useCallback(
    (poDetailId) => {
      if (!canOverridePricing || !poDetailId) return;
      setRevokeModal({ open: true, poDetailId, reason: "", error: "" });
    },
    [canOverridePricing]
  );

  const closeRevokeModal = useCallback(() => {
    if (revokeSaving) return;
    setRevokeModal({ open: false, poDetailId: null, reason: "", error: "" });
  }, [revokeSaving]);

  const handleRevokeReasonChange = (event) => {
    const value = event.target.value;
    setRevokeModal((prev) => ({ ...prev, reason: value, error: "" }));
  };

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeModal.poDetailId) return;
    const reason = String(revokeModal.reason || "").trim();
    if (!reason) {
      setRevokeModal((prev) => ({ ...prev, error: "Reason is required." }));
      return;
    }
    setRevokeSaving(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}${PRICING_OVERRIDE_REVOKE_ENDPOINT}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ po_detail_id: revokeModal.poDetailId, reason }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        console.error("Pricing override revoke failed", { status: resp.status, payload });
        throw new Error(payload?.message || payload?.error || "Unable to revoke override.");
      }
      setRevokeModal({ open: false, poDetailId: null, reason: "", error: "" });
      setPricingRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Pricing override revoke request error", err);
      const rawMessage = err?.message || "Unable to revoke override.";
      const friendlyMessage =
        rawMessage === "save_failed" || rawMessage.toLowerCase().includes("save_failed")
          ? "Override revoke failed. Please try again."
          : rawMessage;
      setRevokeModal((prev) => ({ ...prev, error: friendlyMessage }));
    } finally {
      setRevokeSaving(false);
    }
  }, [revokeModal.poDetailId, revokeModal.reason, me?.email, me?.role]);

  const toggleCancelFollowupPanel = useCallback((followup) => {
    if (!followup?.id) return;
    const nextPanelState = buildCancelFollowupPanelState(followup);
    setCancelFollowupIlsAction({ loading: "", message: "", tone: "" });
    setCancelFollowupProgressAction({ loading: "", message: "", tone: "" });
    setCancelFollowupCorrection(emptyCancelFollowupCorrectionState());
    setCancelFollowupReview({
      open: false,
      action: "",
      followup: null,
      refreshing: false,
      error: "",
      refreshedAt: "",
    });
    setCancelFollowupCloseState({
      open: false,
      status: nextPanelState.workflowMeta.manualCloseStatus || "DONE",
      reason: nextPanelState.workflowMeta.manualCloseReason || "",
      error: "",
    });
    setCancelFollowupPanel((prev) => {
      if (prev.followupId === followup.id) {
        return buildCancelFollowupPanelState(null);
      }
      return nextPanelState;
    });
  }, []);

  const closeCancelFollowupPanel = useCallback(() => {
    if (cancelFollowupSaving) return;
    setCancelFollowupIlsAction({ loading: "", message: "", tone: "" });
    setCancelFollowupProgressAction({ loading: "", message: "", tone: "" });
    setCancelFollowupCorrection(emptyCancelFollowupCorrectionState());
    setCancelFollowupReview({
      open: false,
      action: "",
      followup: null,
      refreshing: false,
      error: "",
      refreshedAt: "",
    });
    setCancelFollowupCloseState({
      open: false,
      status: "DONE",
      reason: "",
      error: "",
    });
    setCancelFollowupPanel(buildCancelFollowupPanelState(null));
  }, [cancelFollowupSaving]);

  const openCancelFollowupManualClose = useCallback(() => {
    setCancelFollowupCloseState((prev) => ({
      ...prev,
      open: true,
      status: getManualCloseDefaultStatus({
        manualCloseStatus: cancelFollowupPanel.workflowMeta.manualCloseStatus,
        preview: cancelFollowupCorrection.preview,
      }),
      error: "",
    }));
  }, [
    cancelFollowupCorrection.preview,
    cancelFollowupPanel.workflowMeta.manualCloseStatus,
  ]);

  const executeCancelFollowupIlsUpdate = useCallback(async (followup) => {
    if (!followup?.id) return;
    if (!followup?.ilsAction?.canUpdate) {
      setCancelFollowupIlsAction({
        loading: "",
        message: followup?.ilsAction?.updateDisabledReason || "Update iLS is not available for this follow-up.",
        tone: "error",
      });
      return;
    }

    setCancelFollowupIlsAction({ loading: "update", message: "", tone: "" });
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}${CANCEL_FOLLOWUP_ILS_UPDATE_ENDPOINT}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ followup_id: followup.id }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.message || payload?.error || "Unable to update iLS.");
      }
      const successCount = Number(payload?.successCount ?? payload?.succeeded?.length ?? 0);
      const failureCount = Number(payload?.failureCount ?? payload?.failed?.length ?? 0);
      await refreshPoTrackerFollowups(followup.id);
      setCancelFollowupIlsAction({
        loading: "",
        message:
          failureCount > 0
            ? `Update iLS finished with ${successCount} success and ${failureCount} failure.`
            : `Update iLS succeeded for ${successCount} outbound${successCount === 1 ? "" : "s"}.`,
        tone: failureCount > 0 ? "warning" : "success",
      });
    } catch (err) {
      console.error("Cancel follow-up Update iLS failed", err);
      setCancelFollowupIlsAction({
        loading: "",
        message: err?.message || "Unable to update iLS.",
        tone: "error",
      });
    }
  }, [me?.email, me?.role, refreshPoTrackerFollowups]);

  const executeCancelFollowupIlsCancel = useCallback(async (followup) => {
    if (!followup?.id) return;
    if (!followup?.ilsAction?.canCancel) {
      setCancelFollowupIlsAction({
        loading: "",
        message: followup?.ilsAction?.cancelDisabledReason || "iLS cancellation request is not available for this follow-up.",
        tone: "error",
      });
      return;
    }

    setCancelFollowupIlsAction({ loading: "cancel", message: "", tone: "" });
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}${CANCEL_FOLLOWUP_ILS_CANCEL_ENDPOINT}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ followup_id: followup.id }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload?.message || payload?.error || "Unable to send the iLS cancellation request.");
      }
      const successCount = Number(payload?.successCount ?? payload?.succeeded?.length ?? 0);
      const failureCount = Number(payload?.failureCount ?? payload?.failed?.length ?? 0);
      await refreshPoTrackerFollowups(followup.id);
      setCancelFollowupIlsAction({
        loading: "",
        message:
          failureCount > 0
            ? `iLS cancellation request finished with ${successCount} success and ${failureCount} failure.`
            : `iLS cancellation request was submitted for ${successCount} outbound${successCount === 1 ? "" : "s"}.`,
        tone: failureCount > 0 ? "warning" : "success",
      });
    } catch (err) {
      console.error("Cancel follow-up iLS cancel failed", err);
      setCancelFollowupIlsAction({
        loading: "",
        message: err?.message || "Unable to send the iLS cancellation request.",
        tone: "error",
      });
    }
  }, [me?.email, me?.role, refreshPoTrackerFollowups]);

  const closeCancelFollowupReview = useCallback(() => {
    if (cancelFollowupIlsAction.loading) return;
    setCancelFollowupReview({
      open: false,
      action: "",
      followup: null,
      refreshing: false,
      error: "",
      refreshedAt: "",
    });
  }, [cancelFollowupIlsAction.loading]);

  const openCancelFollowupReview = useCallback(
    async (action) => {
      const followup = cancelFollowupPanel.followup;
      if (!followup?.id) return;
      setCancelFollowupIlsAction({ loading: "", message: "", tone: "" });
      setCancelFollowupReview({
        open: true,
        action,
        followup: followup,
        refreshing: true,
        error: "",
        refreshedAt: "",
      });
      try {
        const nextByPoDetailId = await refreshPoTrackerFollowups(followup.id);
        const refreshedFollowup =
          Object.values(nextByPoDetailId || {}).find((item) => Number(item?.id) === Number(followup.id)) || followup;
        setCancelFollowupReview({
          open: true,
          action,
          followup: refreshedFollowup,
          refreshing: false,
          error: "",
          refreshedAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error("Cancel follow-up review refresh failed", err);
        setCancelFollowupReview({
          open: true,
          action,
          followup,
          refreshing: false,
          error: err?.message || "Unable to refresh linked Routing Status rows for review.",
          refreshedAt: "",
        });
      }
    },
    [cancelFollowupPanel.followup, refreshPoTrackerFollowups]
  );

  const confirmCancelFollowupReview = useCallback(async () => {
    if (!cancelFollowupReview.followup) return;
    if (cancelFollowupReview.action === "update") {
      await executeCancelFollowupIlsUpdate(cancelFollowupReview.followup);
    } else if (cancelFollowupReview.action === "cancel") {
      await executeCancelFollowupIlsCancel(cancelFollowupReview.followup);
    }
    setCancelFollowupReview((prev) => ({ ...prev, open: false, refreshing: false }));
  }, [cancelFollowupReview.action, cancelFollowupReview.followup, executeCancelFollowupIlsCancel, executeCancelFollowupIlsUpdate]);

  const buildCancelFollowupJsonHeaders = useCallback(() => {
    const headers = { "Content-Type": "application/json" };
    if (me?.email) headers["X-User-Email"] = me.email;
    if (me?.role) headers["X-User-Role"] = me.role || "";
    return headers;
  }, [me?.email, me?.role]);

  const submitCancelFollowupProgressUpdate = useCallback(
    async ({
      status,
      notes = cancelFollowupPanel.notes,
      workflowMeta = cancelFollowupPanel.workflowMeta,
      assignToMe = cancelFollowupPanel.assignToMe,
      loading = "save",
      successMessage = "Follow-up updated.",
    }) => {
      if (!cancelFollowupPanel.followup?.id) return null;
      const normalizedStatus = normalizeCancelFollowupStatus(status);
      if (!normalizedStatus) {
        setCancelFollowupPanel((prev) => ({ ...prev, error: "Status is required." }));
        return null;
      }
      setCancelFollowupSaving(true);
      setCancelFollowupProgressAction({ loading, message: "", tone: "" });
      setCancelFollowupPanel((prev) => ({ ...prev, error: "" }));
      try {
        const resp = await fetch(`${API_BASE}${CANCEL_FOLLOWUP_UPDATE_ENDPOINT}`, {
          method: "POST",
          headers: buildCancelFollowupJsonHeaders(),
          body: JSON.stringify({
            followup_id: cancelFollowupPanel.followup.id,
            status: normalizedStatus,
            notes: buildCancelFollowupNotes(notes, workflowMeta),
            assign_to_me: assignToMe,
          }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) {
          throw new Error(payload?.message || payload?.error || "Unable to update cancel follow-up.");
        }
        const nextByPoDetailId =
          (await refreshPoTrackerFollowups(cancelFollowupPanel.followup.id).catch(() => null)) || {};
        const updatedFollowup =
          Object.values(nextByPoDetailId).find(
            (item) => Number(item?.id) === Number(cancelFollowupPanel.followup.id)
          ) ||
          payload?.followup ||
          null;
        if (updatedFollowup) {
          const nextPanelState = buildCancelFollowupPanelState(updatedFollowup);
          setCancelFollowupPanel((prev) =>
            prev.followupId === updatedFollowup.id
              ? {
                  ...nextPanelState,
                  assignToMe: false,
                }
              : prev
          );
          setCancelFollowupCloseState((prev) => ({
            ...prev,
            status: nextPanelState.workflowMeta.manualCloseStatus || prev.status || "DONE",
            reason:
              nextPanelState.workflowMeta.manualCloseReason ||
              prev.reason,
            error: "",
          }));
        }
        setCancelFollowupProgressAction({
          loading: "",
          message: successMessage,
          tone: "success",
        });
        return updatedFollowup;
      } catch (err) {
        console.error("Cancel follow-up progress update failed", err);
        const message = err?.message || "Unable to update cancel follow-up.";
        setCancelFollowupPanel((prev) => ({ ...prev, error: message }));
        if (loading === "manual_close") {
          setCancelFollowupCloseState((prev) => ({ ...prev, error: message }));
        }
        setCancelFollowupProgressAction({
          loading: "",
          message,
          tone: "error",
        });
        return null;
      } finally {
        setCancelFollowupSaving(false);
      }
    },
    [
      buildCancelFollowupJsonHeaders,
      cancelFollowupPanel.assignToMe,
      cancelFollowupPanel.followup,
      cancelFollowupPanel.notes,
      cancelFollowupPanel.workflowMeta,
      refreshPoTrackerFollowups,
    ]
  );

  const handleCancelFollowupNotesSave = useCallback(async () => {
    await submitCancelFollowupProgressUpdate({
      status: cancelFollowupPanel.status,
      successMessage: "Notes and follow-up owner were saved.",
    });
  }, [cancelFollowupPanel.status, submitCancelFollowupProgressUpdate]);

  const markCancelFollowupWorkflowStepDone = useCallback(
    async (stepKey, successMessage) => {
      const nextWorkflowMeta = setCancelFollowupWorkflowStep(
        cancelFollowupPanel.workflowMeta,
        stepKey,
        me?.email
      );
      await submitCancelFollowupProgressUpdate({
        status: getActiveCancelFollowupProgressStatus(cancelFollowupPanel.status),
        workflowMeta: nextWorkflowMeta,
        successMessage,
        loading: stepKey,
      });
    },
    [
      cancelFollowupPanel.status,
      cancelFollowupPanel.workflowMeta,
      me?.email,
      submitCancelFollowupProgressUpdate,
    ]
  );

  const handleCancelFollowupRefreshLiveState = useCallback(async () => {
    if (!cancelFollowupPanel.followup?.id) return;
    setCancelFollowupProgressAction({ loading: "refresh_live", message: "", tone: "" });
    try {
      const routingRowIds = Array.from(
        new Set(
          [
            Number(cancelFollowupPanel.followup?.originLiveState?.routingStatusId || 0),
            Number(cancelFollowupPanel.followup?.replacementLiveState?.routingStatusId || 0),
            ...asArray(cancelFollowupPanel.followup?.routingLinks).map((link) =>
              Number(
                link?.originRoutingStatusId ||
                  link?.routingStatusId ||
                  link?.linkedRoutingStatusId ||
                  0
              )
            ),
          ]
            .filter((value) => Number.isInteger(value) && value > 0)
        )
      );
      if (routingRowIds.length) {
        const resp = await fetch(`${API_BASE}/routing-status/wms-status`, {
          method: "POST",
          headers: buildCancelFollowupJsonHeaders(),
          body: JSON.stringify({ routing_row_ids: routingRowIds }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) {
          throw new Error(payload?.message || payload?.error || "Unable to refresh current iLS status.");
        }
      }
      const nextByPoDetailId = await refreshPoTrackerFollowups(cancelFollowupPanel.followup.id);
      const refreshedFollowup =
        Object.values(nextByPoDetailId || {}).find(
          (item) => Number(item?.id) === Number(cancelFollowupPanel.followup.id)
        ) || null;
      if (refreshedFollowup) {
        const nextPanelState = buildCancelFollowupPanelState(refreshedFollowup);
        setCancelFollowupPanel((prev) =>
          prev.followupId === refreshedFollowup.id
            ? {
                ...nextPanelState,
                assignToMe: prev.assignToMe,
              }
            : prev
        );
      }
      setCancelFollowupProgressAction({
        loading: "",
        message: "Current iLS / live routing state was refreshed.",
        tone: "success",
      });
    } catch (err) {
      console.error("Refresh current live routing state failed", err);
      setCancelFollowupProgressAction({
        loading: "",
        message: err?.message || "Unable to refresh current iLS / live routing state.",
        tone: "error",
      });
    }
  }, [buildCancelFollowupJsonHeaders, cancelFollowupPanel.followup, refreshPoTrackerFollowups]);

  useEffect(() => {
    const followup = cancelFollowupPanel.followup;
    if (!followup?.id) {
      cancelFollowupAutoRefreshKeyRef.current = "";
      return;
    }
    const followupPath = resolveCancelFollowupPath(followup);
    if (followupPath.type !== "not_shipped") return;
    if (cancelFollowupProgressAction.loading === "refresh_live" || cancelFollowupReview.refreshing) return;

    const originLiveState = followup.originLiveState || {};
    const persistedCorrection = followup.routingCorrection || {};
    const replacementEvidence = firstText(
      followup.replacementLiveState?.outboundCode,
      persistedCorrection?.replacementOutboundCode
    );
    const originOutboundCode = firstText(originLiveState?.outboundCode, followup.outboundCode);
    const originWmsKey = normalizeWmsStatusKey(originLiveState?.currentWmsStatus);
    const alreadyAdvanced = CANCEL_FOLLOWUP_REQUESTED_WMS_KEYS.has(originWmsKey);
    const needsOriginRefresh = Boolean(
      replacementEvidence &&
        originOutboundCode &&
        !alreadyAdvanced
    );
    if (!needsOriginRefresh) return;

    const refreshKey = `${followup.id}:${originOutboundCode}:${replacementEvidence}:${originWmsKey || "NONE"}`;
    if (cancelFollowupAutoRefreshKeyRef.current === refreshKey) return;
    cancelFollowupAutoRefreshKeyRef.current = refreshKey;
    handleCancelFollowupRefreshLiveState().catch(() => null);
  }, [
    cancelFollowupPanel.followup,
    cancelFollowupProgressAction.loading,
    cancelFollowupReview.refreshing,
    handleCancelFollowupRefreshLiveState,
  ]);

  const handleCancelFollowupLineApproved = useCallback(async () => {
    if (!cancelFollowupPanel.followup?.id || !cancelFollowupPanel.followup?.poDetailId) return;
    if (!dataset || !lineStatusKey) {
      setCancelFollowupProgressAction({
        loading: "",
        message: "Line status cannot be updated because the current dataset is missing the Line status column.",
        tone: "error",
      });
      return;
    }
    const targetId = Number(cancelFollowupPanel.followup.poDetailId);
    const targetIndex = dataset.data.findIndex(
      (row) => Number(extractPoDetailId(row)) === targetId
    );
    if (targetIndex < 0) {
      setCancelFollowupProgressAction({
        loading: "",
        message: "The current PO Details row could not be found in the loaded dataset.",
        tone: "error",
      });
      return;
    }
    const currentRow = dataset.data[targetIndex];
    if (String(currentRow?.[lineStatusKey] || "").trim().toUpperCase() === "APPROVED") {
      setCancelFollowupProgressAction({
        loading: "",
        message: "Line status is already Approved.",
        tone: "success",
      });
      return;
    }

    setCancelFollowupSaving(true);
    setCancelFollowupProgressAction({ loading: "set_approved", message: "", tone: "" });
    try {
      const nextRows = dataset.data.map((row, index) =>
        index === targetIndex
          ? {
              ...row,
              [lineStatusKey]: "Approved",
            }
          : row
      );
      const nextDataset = { ...dataset, data: nextRows };
      const withTscp = annotateDatasetWithTscp(nextDataset);
      const annotated = annotateDatasetWithHold(withTscp);
      setDataset(annotated);
      persistDataset(dataStorageKey, annotated);
      if (fileMeta) persistMeta(metaStorageKey, fileMeta);
      await persistRemote(annotated, fileMeta);
      await reloadPoTrackerAfterSave();
      await refreshPoTrackerFollowups(cancelFollowupPanel.followup.id).catch(() => null);
      setCancelFollowupProgressAction({
        loading: "",
        message: "Line status was reset to Approved.",
        tone: "success",
      });
    } catch (err) {
      console.error("Reset line status to Approved failed", err);
      setCancelFollowupProgressAction({
        loading: "",
        message: err?.message || "Unable to set line status back to Approved.",
        tone: "error",
      });
    } finally {
      setCancelFollowupSaving(false);
    }
  }, [
    annotateDatasetWithHold,
    annotateDatasetWithTscp,
    cancelFollowupPanel.followup,
    dataStorageKey,
    dataset,
    fileMeta,
    lineStatusKey,
    metaStorageKey,
    persistRemote,
    reloadPoTrackerAfterSave,
    refreshPoTrackerFollowups,
  ]);

  const handleCancelFollowupNormalClose = useCallback(async () => {
    await submitCancelFollowupProgressUpdate({
      status: "DONE",
      successMessage: "Follow-up was closed.",
      loading: "close_followup",
    });
  }, [submitCancelFollowupProgressUpdate]);

  const handleCancelFollowupManualClose = useCallback(async () => {
    if (cancelFollowupSaving || cancelFollowupManualCloseSubmittingRef.current) return;
    const closeStatus = normalizeCancelFollowupStatus(cancelFollowupCloseState.status) || "DONE";
    const reason = String(cancelFollowupCloseState.reason || "").trim();
    const reasonError = manualCloseReasonError(reason);
    if (reasonError) {
      setCancelFollowupCloseState((prev) => ({ ...prev, error: reasonError }));
      return;
    }
    cancelFollowupManualCloseSubmittingRef.current = true;
    const nextWorkflowMeta = {
      ...sanitizeCancelFollowupWorkflowMeta(cancelFollowupPanel.workflowMeta),
      manualCloseReason: reason,
      manualCloseStatus: closeStatus,
    };
    try {
      const updated = await submitCancelFollowupProgressUpdate({
        status: closeStatus,
        workflowMeta: nextWorkflowMeta,
        successMessage: "Follow-up was closed manually.",
        loading: "manual_close",
      });
      if (updated) {
        setCancelFollowupCloseState((prev) => ({
          ...prev,
          open: false,
          error: "",
        }));
      }
    } finally {
      cancelFollowupManualCloseSubmittingRef.current = false;
    }
  }, [
    cancelFollowupCloseState.reason,
    cancelFollowupCloseState.status,
    cancelFollowupPanel.workflowMeta,
    cancelFollowupSaving,
    submitCancelFollowupProgressUpdate,
  ]);

  const runCancelFollowupCorrectionPreview = useCallback(async (followup) => {
    if (!followup?.id) return;
    setCancelFollowupCorrection({
      loading: "preview",
      message: "",
      tone: "",
      preview: null,
      activation: null,
      orchestration: null,
    });
    try {
      const resp = await fetch(`${API_BASE}${CANCEL_FOLLOWUP_CORRECTION_PREVIEW_ENDPOINT}`, {
        method: "POST",
        headers: buildCancelFollowupJsonHeaders(),
        body: JSON.stringify({ followup_id: followup.id }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        throw new Error(payload?.message || payload?.error || "Unable to preview correction.");
      }
      const preview = payload?.preview || null;
      const previewDescription = describeCorrectionPreview(preview);
      setCancelFollowupCorrection({
        loading: "",
        message: previewDescription.message,
        tone: previewDescription.tone,
        preview,
        activation: null,
        orchestration: null,
      });
    } catch (err) {
      console.error("Cancel follow-up correction preview failed", err);
      setCancelFollowupCorrection({
        loading: "",
        message: err?.message || "Unable to preview correction.",
        tone: "error",
        preview: null,
        activation: null,
        orchestration: null,
      });
    }
  }, [buildCancelFollowupJsonHeaders]);

  const runCancelFollowupCorrectionActivate = useCallback(async (followup) => {
    if (!followup?.id) return;
    const preview = cancelFollowupCorrection.preview;
    const eligible = Boolean(preview?.eligibleForReplacementOutboundCorrection) && !asArray(preview?.blockers).length;
    if (!eligible) {
      setCancelFollowupCorrection((prev) => ({
        ...prev,
        message: "Preview must be eligible before correction activation.",
        tone: "error",
      }));
      return;
    }
    setCancelFollowupCorrection((prev) => ({ ...prev, loading: "activate", message: "", tone: "", orchestration: null }));
    try {
      const resp = await fetch(`${API_BASE}${CANCEL_FOLLOWUP_CORRECTION_ACTIVATE_ENDPOINT}`, {
        method: "POST",
        headers: buildCancelFollowupJsonHeaders(),
        body: JSON.stringify({ followup_id: followup.id }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok === false) {
        setCancelFollowupCorrection((prev) => ({
          ...prev,
          loading: "",
          message: payload?.message || payload?.error || "Correction activation did not complete.",
          tone: "error",
          activation: payload?.result || prev.activation,
          orchestration: null,
        }));
        return;
      }
      await refreshPoTrackerFollowups(followup.id).catch(() => null);
      setCancelFollowupCorrection((prev) => ({
        ...prev,
        loading: "",
        message: "Correction activated. Review the replacement routing state before creating the replacement outbound.",
        tone: "success",
        activation: payload?.result || null,
        orchestration: null,
      }));
    } catch (err) {
      console.error("Cancel follow-up correction activation failed", err);
      setCancelFollowupCorrection((prev) => ({
        ...prev,
        loading: "",
        message: err?.message || "Unable to activate correction.",
        tone: "error",
      }));
    }
  }, [buildCancelFollowupJsonHeaders, cancelFollowupCorrection.preview, refreshPoTrackerFollowups]);

  const runCancelFollowupCorrectionOrchestrate = useCallback(async (followup) => {
    if (!followup?.id) return;
    const confirmed = window.confirm(
      "Create the replacement outbound and then cancel the old outbound? The old outbound is cancelled only after replacement create and local persistence succeed."
    );
    if (!confirmed) return;
    setCancelFollowupCorrection((prev) => ({ ...prev, loading: "orchestrate", message: "", tone: "" }));
    try {
      const resp = await fetch(`${API_BASE}${CANCEL_FOLLOWUP_CORRECTION_ORCHESTRATE_ENDPOINT}`, {
        method: "POST",
        headers: buildCancelFollowupJsonHeaders(),
        body: JSON.stringify({ followup_id: followup.id }),
      });
      const payload = await resp.json().catch(() => ({}));
      const succeeded = resp.ok && payload?.ok !== false;
      await refreshPoTrackerFollowups(followup.id).catch(() => null);
      setCancelFollowupCorrection((prev) => ({
        ...prev,
        loading: "",
        message: succeeded
          ? "Replacement outbound flow completed. Review the new outbound and old outbound cancel result below."
          : payload?.message || payload?.error || "Correction iLS orchestration did not complete.",
        tone: succeeded ? "success" : "error",
        orchestration: payload || null,
      }));
    } catch (err) {
      console.error("Cancel follow-up correction orchestration failed", err);
      setCancelFollowupCorrection((prev) => ({
        ...prev,
        loading: "",
        message: err?.message || "Unable to run correction iLS orchestration.",
        tone: "error",
      }));
    }
  }, [buildCancelFollowupJsonHeaders, refreshPoTrackerFollowups]);

  const handleDeleteSelected = async () => {
    if (!dataset || selectedRows.size === 0) return;
    const count = selectedRows.size;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${count} selected ${count === 1 ? "row" : "rows"}?`
    );
    if (!confirmed) return;
    setSaving(true);
    try {
      const remaining = dataset.data.filter((row) => !selectedRows.has(row._id));
      const nextDataset = { ...dataset, data: remaining };
      const withTscp = annotateDatasetWithTscp(nextDataset);
      const annotated = annotateDatasetWithHold(withTscp);
      setDataset(annotated);
      setSelectedRows(new Set());
      persistDataset(dataStorageKey, annotated);
      if (fileMeta) persistMeta(metaStorageKey, fileMeta);
      await persistRemote(annotated, fileMeta);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to delete selected rows.");
    } finally {
      setSaving(false);
    }
  };

  const handleDragging = useCallback((event) => {
    const { col, startX, startW } = dragRef.current;
    if (!col) return;
    const delta = event.clientX - startX;
    const nextWidth = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, startW + delta));
    setWidths((prev) => {
      if (prev[col] === nextWidth) return prev;
      return { ...prev, [col]: nextWidth };
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragRef.current = { col: "", startX: 0, startW: 0 };
    window.removeEventListener("mousemove", handleDragging);
    window.removeEventListener("mouseup", handleDragEnd);
  }, [handleDragging]);

  const onDragStart = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      col: key,
      startX: event.clientX,
      startW: widths[key] || DEFAULT_COL_WIDTH,
    };
    window.addEventListener("mousemove", handleDragging);
    window.addEventListener("mouseup", handleDragEnd);
  };

  useEffect(() => {
    return () => handleDragEnd();
  }, [handleDragEnd]);

  const hasActiveFilters = useMemo(() => {
    if (!dataset?.headers) return false;
    return dataset.headers.some((header) => {
      const mode = getFilterModeForHeader(header);
      const value = filters[header.key];
      if (mode === FILTER_MODES.date) {
        return Boolean(value?.from || value?.to);
      }
      if (mode === FILTER_MODES.multiSelect) {
        return Array.isArray(value) && value.length > 0;
      }
      return Boolean(String(value || "").trim());
    });
  }, [dataset?.headers, filters]);

  const accessibleRows = useMemo(() => {
    if (accessScopedRows.length || !dataset?.data?.length) return accessScopedRows;
    if (hasActiveFilters) return accessScopedRows;
    return dataset.data;
  }, [accessScopedRows, dataset?.data, hasActiveFilters]);
  const accessibleRowsWithEffectiveCancelledDate = useMemo(() => {
    if (!cancelledDateHeaderKey) return accessibleRows;
    let changed = false;
    const nextRows = accessibleRows.map((row) => {
      const poDetailId = extractPoDetailId(row);
      const cancelFollowup = poDetailId ? cancelFollowupsByPoDetailId[poDetailId] : null;
      const effectiveCancelledDate = firstText(row?.[cancelledDateHeaderKey], cancelFollowup?.cancelledDate);
      if (effectiveCancelledDate === String(row?.[cancelledDateHeaderKey] ?? "")) {
        return row;
      }
      changed = true;
      return {
        ...row,
        [cancelledDateHeaderKey]: effectiveCancelledDate,
      };
    });
    return changed ? nextRows : accessibleRows;
  }, [accessibleRows, cancelFollowupsByPoDetailId, cancelledDateHeaderKey]);

  const filteredRows = useMemo(() => {
    if (!dataset) return [];
    const result = applyFilters(accessibleRowsWithEffectiveCancelledDate, dataset.headers, filters);
    if (!hasActiveFilters && accessibleRowsWithEffectiveCancelledDate.length && result.length === 0) {
      return accessibleRowsWithEffectiveCancelledDate;
    }
    return result;
  }, [dataset, accessibleRowsWithEffectiveCancelledDate, filters, hasActiveFilters]);

  const sortedRows = useMemo(() => {
    if (!dataset) return [];
    return applySorting(filteredRows, dataset.headers, sortState);
  }, [dataset, filteredRows, sortState]);

  const poFilterTokens = useMemo(() => {
    if (!poNumberKey) return [];
    return parsePoFilterTokens(filters?.[poNumberKey] || "");
  }, [filters, poNumberKey]);

  const poFilterTokenKey = useMemo(
    () => poFilterTokens.map((token) => token.normalized).join("|"),
    [poFilterTokens]
  );

  useEffect(() => {
    setMissingPoBannerDismissed(false);
  }, [poFilterTokenKey]);

  const missingPoNumbers = useMemo(() => {
    if (!poNumberKey || poFilterTokens.length < 2) return [];
    const found = new Set();
    filteredRows.forEach((row) => {
      const value = String(row?.[poNumberKey] ?? "").trim().toLowerCase();
      if (value) found.add(value);
    });
    return poFilterTokens
      .filter((token) => !found.has(token.normalized))
      .map((token) => token.raw);
  }, [filteredRows, poFilterTokens, poNumberKey]);

  const showMissingPoBanner =
    poFilterTokens.length >= 2 && missingPoNumbers.length > 0 && !missingPoBannerDismissed;

  const resolvedRows = useMemo(() => {
    if (sortedRows.length || !dataset?.data?.length) return sortedRows;
    if (hasActiveFilters) return sortedRows;
    return dataset.data;
  }, [sortedRows, dataset?.data, hasActiveFilters]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    const activeFilterKeys =
      dataset?.headers
        ?.filter((header) => {
          const mode = getFilterModeForHeader(header);
          const value = filters[header.key];
          if (mode === FILTER_MODES.date) return Boolean(value?.from || value?.to);
          if (mode === FILTER_MODES.multiSelect) return Array.isArray(value) && value.length > 0;
          return Boolean(String(value || "").trim());
        })
        ?.map((header) => header.key) || [];
    console.log("[PO Details debug]", {
      dataset: dataset?.data?.length || 0,
      accessScoped: accessScopedRows.length,
      accessible: accessibleRows.length,
      filtered: filteredRows.length,
      sorted: sortedRows.length,
      resolved: resolvedRows.length,
      hasActiveFilters,
      activeFilterKeys,
      page,
      pageSize,
    });
  }, [
    dataset,
    accessScopedRows,
    accessibleRows,
    filteredRows,
    sortedRows,
    resolvedRows,
    hasActiveFilters,
    filters,
    page,
    pageSize,
  ]);

  const handleExport = useCallback(() => {
    if (!visibleHeaders.length || !resolvedRows.length) return;
    const headerLabels = visibleHeaders.map((header) => header.label);
    const rows = resolvedRows.map((record) =>
      visibleHeaders.map((header) =>
        header.key === FIRST_UPDATE_KEY || header.key === LAST_UPDATE_KEY
          ? formatPstTimestamp(record[header.key])
          : normaliseValueForExport(record[header.key], header.type)
      )
    );
    const worksheet = XLSX.utils.aoa_to_sheet([headerLabels, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, exportSheetName);
    const timestamp = formatTimestampForFilename(new Date());
    const filename = `${exportFilePrefix}-${timestamp}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [visibleHeaders, resolvedRows, exportSheetName, exportFilePrefix]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortState.key, sortState.direction, dataset]);

  const totalPages = useMemo(() => {
    if (!resolvedRows.length) return 1;
    if (pageSize === "All") return 1;
    return Math.max(1, Math.ceil(resolvedRows.length / pageSize));
  }, [resolvedRows, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => paginate(resolvedRows, page, pageSize), [resolvedRows, page, pageSize]);
  const startIdx = pageSize === "All" ? 0 : (page - 1) * pageSize;
  const endIdx = pageSize === "All" ? resolvedRows.length : Math.min(resolvedRows.length, startIdx + pageSize);

  const pricingTargetRows = useMemo(() => {
    return Array.isArray(pagedRows) ? pagedRows : [];
  }, [pagedRows]);

  const pricingEligibleIds = useMemo(() => {
    if (!pricingTargetRows.length) return [];
    const ids = new Set();
    pricingTargetRows.forEach((row) => {
      if (!isPricingEligibleRow(row)) return;
      const poDetailId = extractPoDetailId(row);
      if (Number.isInteger(poDetailId) && poDetailId > 0) {
        ids.add(poDetailId);
      }
    });
    return Array.from(ids).sort((a, b) => a - b);
  }, [pricingTargetRows]);

  const pricingNoEligible = !loadingRemote && !saving && pricingEligibleIds.length === 0;

  const pricingBannerText = useMemo(() => {
    if (pricingNoEligible) return "Pricing Validation: no eligible lines.";
    if (pricingError) return "Pricing Validation: failed.";
    if (!pricingSummary) return "Pricing Validation: checking...";
    const { checked, match, notFound, mismatch, overridden } = pricingSummary;
    return `Pricing Validation: checked ${checked} - ${match} match, ${notFound} not found, ${mismatch} mismatch (blocked), ${overridden} overridden.`;
  }, [pricingSummary, pricingNoEligible, pricingError]);

  const showPricingBanner = !loadingRemote && !saving && Array.isArray(dataset?.data) && dataset.data.length > 0;

  const pricingTargetKey = useMemo(() => pricingEligibleIds.join("|"), [pricingEligibleIds]);

  useEffect(() => {
    if (loadingRemote || !dataset?.data) {
      return;
    }
    if (!pricingEligibleIds.length) {
      console.log("Pricing validate eligibleIds count:", 0);
      if (pricingAbortRef.current) {
        pricingAbortRef.current.abort();
        pricingAbortRef.current = null;
      }
      pricingRequestKeyRef.current = "";
      pricingCompletedKeyRef.current = "";
      setPricingResults({});
      setPricingSummary(null);
      setPricingError("");
      setPricingLoading(false);
      return;
    }

    const requestKey = `${pricingRefreshKey}|${pricingTargetKey}`;
    if (pricingCompletedKeyRef.current === requestKey) return;
    pricingRequestKeyRef.current = requestKey;

    pricingRequestIdRef.current += 1;
    const requestId = pricingRequestIdRef.current;

    if (pricingAbortRef.current) {
      pricingAbortRef.current.abort();
    }
    const controller = new AbortController();
    pricingAbortRef.current = controller;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 20000);

    const headers = {
      "Content-Type": "application/json",
    };
    if (me?.email) headers["X-User-Email"] = me.email;
    if (me?.role) headers["X-User-Role"] = me.role || "";

    const fetchPricingValidation = async () => {
      setPricingLoading(true);
      setPricingError("");
      console.log("Pricing validate eligibleIds count:", pricingEligibleIds.length, "visibleRows:", pricingTargetRows.length);
      try {
        const resp = await fetch(`${API_BASE}${PRICING_VALIDATE_ENDPOINT}`, {
          method: "POST",
          headers,
          body: JSON.stringify({ po_detail_ids: pricingEligibleIds }),
          signal: controller.signal,
        });
        const rawText = await resp.text();
        let payload = {};
        try {
          payload = rawText ? JSON.parse(rawText) : {};
        } catch {
          payload = {};
        }
        if (!resp.ok || payload?.ok === false) {
          console.error("Pricing validation failed", {
            status: resp.status,
            message: payload?.error || payload?.message || rawText,
          });
          throw new Error(payload?.error || payload?.message || "Unable to validate pricing.");
        }
        if (requestId !== pricingRequestIdRef.current) return;
        const nextMap = {};
        const summary = {
          checked: 0,
          match: 0,
          notFound: 0,
          mismatch: 0,
          overridden: 0,
        };
        (payload?.results || []).forEach((item) => {
          if (!item || item.po_detail_id === undefined || item.po_detail_id === null) return;
          nextMap[item.po_detail_id] = item;
          summary.checked += 1;
          const status = String(item.status || "").toUpperCase();
          if (status === "MATCH") {
            summary.match += 1;
          } else if (status === "ALLOWED") {
            summary.overridden += 1;
          } else if (status === "MISMATCH" && !item.override_applied) {
            summary.mismatch += 1;
          } else {
            summary.notFound += 1;
          }
        });
        setPricingResults(nextMap);
        setPricingSummary(summary);
        pricingCompletedKeyRef.current = requestKey;
      } catch (err) {
        if (requestId !== pricingRequestIdRef.current) return;
        if (controller.signal.aborted && !timedOut) {
          // Request was superseded by a newer validation run; do not show transient errors.
          return;
        }
        if (controller.signal.aborted && timedOut) {
          setPricingError("Pricing validation request timed out.");
        } else {
          console.error("Pricing validation request error", err);
          setPricingError(err?.message || "Unable to validate pricing.");
        }
        pricingCompletedKeyRef.current = requestKey;
      } finally {
        window.clearTimeout(timeoutId);
        if (requestId === pricingRequestIdRef.current) {
          setPricingLoading(false);
        }
      }
    };

    fetchPricingValidation();
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [pricingEligibleIds, pricingTargetKey, pricingRefreshKey, me?.email, me?.role, pricingTargetRows.length, loadingRemote, dataset?.data]);

  const blockedRowIdSet = useMemo(() => {
    if (!filteredRows.length) return new Set();
    const blocked = new Set();
    filteredRows.forEach((row) => {
      const poDetailId = extractPoDetailId(row);
      if (!poDetailId) return;
      const result = pricingResults[poDetailId];
      if (isPricingBlockedResult(result)) {
        blocked.add(row._id);
      }
    });
    return blocked;
  }, [filteredRows, pricingResults]);

  useEffect(() => {
    if (!blockedRowIdSet.size) return;
    setSelectedRows((prev) => {
      const next = new Set(prev);
      let changed = false;
      blockedRowIdSet.forEach((id) => {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [blockedRowIdSet]);

  const visibleRowIds = useMemo(() => filteredRows.map((row) => row._id), [filteredRows]);
  const selectableRowIds = useMemo(
    () => visibleRowIds.filter((id) => !blockedRowIdSet.has(id)),
    [visibleRowIds, blockedRowIdSet]
  );
  const allVisibleSelected = useMemo(
    () => selectableRowIds.length > 0 && selectableRowIds.every((id) => selectedRows.has(id)),
    [selectableRowIds, selectedRows]
  );
  const someVisibleSelected = useMemo(
    () => selectableRowIds.some((id) => selectedRows.has(id)),
    [selectableRowIds, selectedRows]
  );

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = !allVisibleSelected && someVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const setDateRangeFilter = (key, range) => {
    setFilters((prev) => ({
      ...prev,
      [key]: {
        from: range.from || "",
        to: range.to || "",
      },
    }));
  };

  const resetColumnWidths = useCallback(() => {
    setWidths(() => {
      if (!dataset?.headers) return {};
      const defaults = {};
      dataset.headers.forEach((header) => {
        defaults[header.key] = Math.min(
          MAX_COL_WIDTH,
          Math.max(MIN_COL_WIDTH, ((header.label?.length || 10) + 6) * 8)
        );
      });
      defaults[SELECT_COLUMN_KEY] = SELECT_COLUMN_DEFAULT_WIDTH;
      return defaults;
    });
    clearStoredWidths(widthsStorageKey);
  }, [dataset?.headers, widthsStorageKey]);

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) => {
      if (blockedRowIdSet.has(id)) return prev;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        selectableRowIds.forEach((id) => next.delete(id));
      } else {
        selectableRowIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleResetFilters = useCallback(() => {
    if (!dataset?.headers) return;
    setFilters(clearFiltersFor(dataset.headers));
    setSelectedRows(new Set());
  }, [dataset?.headers]);

  const toggleSort = (key) => {
    setSortState((prev) => toggleSortState(prev, key));
  };

  const selectColumnWidth = widths[SELECT_COLUMN_KEY] || SELECT_COLUMN_DEFAULT_WIDTH;
  const stickyOffsets = useMemo(() => {
    if (!visibleHeaders.length) return {};
    const offsets = {};
    let offset = selectColumnWidth;
    visibleHeaders.forEach((header) => {
      const canonical = canonicalizeHeaderLabel(header.label);
      const width = widths[header.key] || DEFAULT_COL_WIDTH;
      if (STICKY_HEADER_SET.has(canonical)) {
        offsets[header.key] = offset;
      }
      offset += width;
    });
    return offsets;
  }, [visibleHeaders, widths, selectColumnWidth]);
  const cancelFollowupPanelWidth = useMemo(() => {
    if (!visibleHeaders.length) return null;
    let totalWidth = selectColumnWidth;
    let foundLineStatus = false;
    visibleHeaders.forEach((header) => {
      totalWidth += widths[header.key] || DEFAULT_COL_WIDTH;
      if (header.key === lineStatusKey) {
        foundLineStatus = true;
      }
    });
    return foundLineStatus ? totalWidth : null;
  }, [visibleHeaders, widths, selectColumnWidth, lineStatusKey]);
  const lineStatusVisibleIndex = useMemo(() => {
    if (!visibleHeaders.length || !lineStatusKey) return -1;
    return visibleHeaders.findIndex((header) => header.key === lineStatusKey);
  }, [visibleHeaders, lineStatusKey]);
  const cancelFollowupPanelColSpan = useMemo(() => {
    if (lineStatusVisibleIndex < 0) return visibleHeaders.length + 1;
    return lineStatusVisibleIndex + 2;
  }, [lineStatusVisibleIndex, visibleHeaders.length]);
  const cancelFollowupTrailingColSpan = useMemo(
    () => Math.max(0, visibleHeaders.length + 1 - cancelFollowupPanelColSpan),
    [visibleHeaders.length, cancelFollowupPanelColSpan]
  );
  const warningTitle =
    versionWarning?.type === "skip"
      ? "You are attempting to import a file whose version skips ahead of the current one!"
      : "You are attempting to import an older version than the current file!";
  const warningCurrentLabel = versionWarning ? formatVersionDate(versionWarning.currentDate) : "";
  const warningUpdateLabel = versionWarning ? formatVersionDate(versionWarning.updateDate) : "";
  const unresolvedCancelFollowupCount = Number(cancelFollowupSummary?.unresolvedCount || 0);
  const unresolvedCancelFollowupPoCount = Number(cancelFollowupSummary?.unresolvedPoCount || 0);
  const showCancelFollowupBanner = unresolvedCancelFollowupCount > 0;
  const unresolvedCancelFollowupPoNumbers = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    Object.values(cancelFollowupsByPoDetailId || {}).forEach((followup) => {
      const status = normalizeCancelFollowupStatus(followup?.status);
      const poNumber = String(followup?.poNumber || "").trim();
      if (!ACTIVE_CANCEL_FOLLOWUP_STATUSES.has(status) || !poNumber || seen.has(poNumber)) return;
      seen.add(poNumber);
      ordered.push(poNumber);
    });
    return ordered.sort((a, b) => a.localeCompare(b));
  }, [cancelFollowupsByPoDetailId]);
  const handleCancelFollowupBannerClick = useCallback(() => {
    if (!dataset?.headers?.length || !poNumberKey || !unresolvedCancelFollowupPoNumbers.length) return;
    const nextFilters = clearFiltersFor(dataset.headers);
    nextFilters[poNumberKey] = unresolvedCancelFollowupPoNumbers.join(", ");
    setFilters(nextFilters);
    setSelectedRows(new Set());
    setPage(1);
    setMissingPoBannerDismissed(false);
  }, [dataset?.headers, poNumberKey, unresolvedCancelFollowupPoNumbers]);

  return (
    <>
      {versionWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelVersionWarning} />
          <div className="relative z-50 w-full max-w-lg rounded-2xl border-2 border-amber-300 bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3">
              <AlertTriangle className="h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <h2 className="text-lg font-semibold text-amber-900">{warningTitle}</h2>
                <p className="text-sm text-amber-800">This action may overwrite the latest dataset.</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-700">Review the version dates below before continuing.</p>
            <div className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-slate-800">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-700">Current Version:</span>
                <span>{warningCurrentLabel || "Unknown"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-700">Update Version:</span>
                <span>{warningUpdateLabel || "Unknown"}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={handleCancelVersionWarning}
              >
                Abort
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={handleConfirmVersionWarning}
                disabled={saving}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {overrideModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={closeOverrideModal} />
          <div className="relative z-50 w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Admin Override</h2>
              <p className="mt-1 text-sm text-slate-600">
                Allow pricing mismatch for PO line{" "}
                <span className="font-semibold text-slate-900">#{overrideModal.poDetailId}</span>.
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Reason
                <textarea
                  value={overrideModal.reason}
                  onChange={handleOverrideReasonChange}
                  rows={3}
                  className="mt-1 w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Provide the admin override reason"
                  disabled={overrideSaving}
                />
              </label>
              {overrideModal.error && <p className="text-xs text-red-600">{overrideModal.error}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={closeOverrideModal}
                disabled={overrideSaving}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmOverride}
                disabled={overrideSaving}
                className="rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {overrideSaving ? "Saving..." : "Confirm Override"}
              </button>
            </div>
          </div>
        </div>
      )}
      {revokeModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={closeRevokeModal} />
          <div className="relative z-50 w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Revoke Override</h2>
              <p className="mt-1 text-sm text-slate-600">
                Remove pricing override for PO line{" "}
                <span className="font-semibold text-slate-900">#{revokeModal.poDetailId}</span>.
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Reason
                <textarea
                  value={revokeModal.reason}
                  onChange={handleRevokeReasonChange}
                  rows={3}
                  className="mt-1 w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Provide the reason for revoking the override"
                  disabled={revokeSaving}
                />
              </label>
              {revokeModal.error && <p className="text-xs text-red-600">{revokeModal.error}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={closeRevokeModal}
                disabled={revokeSaving}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRevoke}
                disabled={revokeSaving}
                className="rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {revokeSaving ? "Saving..." : "Confirm Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}
      {warehouseAssignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={handleCloseWarehouseAssignModal} />
          <div className="relative z-50 w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Set Warehouse</h2>
              <p className="mt-1 text-sm text-slate-600">
                Apply one warehouse to all selected PO lines.
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <p className="text-sm text-slate-700">
                Selected PO count: <span className="font-semibold">{selectedPoNumbers.length}</span>
              </p>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Warehouse
                <select
                  value={warehouseAssignCode}
                  onChange={(event) => setWarehouseAssignCode(event.target.value)}
                  disabled={warehouseAssignSaving || warehousesLoading || !warehouseOptions.length}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="">Select Warehouse</option>
                  {warehouseOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.code}/{option.shipPoint || "-"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={warehouseAssignOverwrite}
                  onChange={(event) => setWarehouseAssignOverwrite(event.target.checked)}
                  disabled={warehouseAssignSaving}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                Overwrite existing warehouse assignment when conflict exists
              </label>
              {warehouseAssignConflicts.length > 0 && (
                <div className="max-h-28 overflow-auto rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <p className="font-semibold">Conflicts found:</p>
                  <ul className="mt-1 list-disc pl-4">
                    {warehouseAssignConflicts.slice(0, 10).map((row) => (
                      <li key={row.po_number}>
                        {row.po_number} (existing: {(row.existing_whse_codes || []).join(", ") || "N/A"})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {warehouseAssignError && <p className="text-xs text-red-600">{warehouseAssignError}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseWarehouseAssignModal}
                disabled={warehouseAssignSaving}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyWarehouseAssignment}
                disabled={warehouseAssignSaving || !warehouseAssignCode}
                className="rounded bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {warehouseAssignSaving ? "Applying..." : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
      {tscpModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseTscpModal} />
          <div className="relative z-50 w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">{TSCP_TEMPLATE_BUTTON_LABEL}</h2>
              <p className="mt-1 text-sm text-slate-600">
                Select a warehouse to resolve the WM Ship Point for the TSCP Template export.
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Selected POs to export ({tscpSelectedPoCountLabel})
                </p>
                <div className="mt-1 max-h-32 overflow-auto rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {tscpFilteredPoNumbers.length ? (
                    <div className="flex flex-wrap gap-1">
                      {tscpFilteredPoNumbers.map((po) => (
                        <span key={po} className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                          {po}
                        </span>
                      ))}
                    </div>
                  ) : tscpCustCodeRequired && !tscpSelectedCustCode ? (
                    <p className="text-xs text-slate-500">Select a Cust Code to preview PO numbers.</p>
                  ) : tscpCustCodeRequired ? (
                    <p className="text-xs text-slate-500">No PO numbers match the selected Cust Code.</p>
                  ) : (
                    <p className="text-xs text-slate-500">No PO selected.</p>
                  )}
                </div>
              </div>
              {tscpCustCodeRequired && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Cust Code
                    <select
                      value={tscpSelectedCustCode}
                      onChange={(event) => setTscpSelectedCustCode(event.target.value)}
                      disabled={tscpGenerating}
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <option value="">Select Cust Code</option>
                      {selectedCustOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="mt-1 text-xs text-slate-500">
                    {tscpSelectedCustCode
                      ? `Only PO numbers for ${tscpSelectedCustLabel || tscpSelectedCustCode} will be exported.`
                      : "Multiple Cust Codes detected. Please pick one to continue."}
                  </p>
                </div>
              )}
              {!tscpCustCodeRequired && selectedCustOptions.length === 1 && (
                <div>
                  <p className="text-xs text-slate-600">
                    Cust Code: <span className="font-semibold text-slate-800">{selectedCustOptions[0].label}</span>
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Warehouse Code
                  <select
                    value={tscpWarehouseCode}
                    onChange={(event) => setTscpWarehouseCode(event.target.value)}
                    disabled={tscpGenerating || warehousesLoading || !warehouseOptions.length}
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="">Select Warehouse</option>
                    {warehouseOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {warehousesLoading && <p className="mt-1 text-xs text-slate-500">Loading warehouse list...</p>}
                {warehouseLoadError && (
                  <p className="mt-1 text-xs text-red-600">Unable to load warehouses: {warehouseLoadError}</p>
                )}
                {tscpSelectedWarehouseOption && (
                  <p className="mt-1 text-xs text-slate-600">
                    WM Ship Point:{" "}
                    {tscpResolveLoading ? (
                      <span className="font-semibold text-slate-500">Resolving...</span>
                    ) : tscpResolvedShipPoint ? (
                      <span className="font-semibold text-slate-800">{tscpResolvedShipPoint}</span>
                    ) : tscpResolveError ? (
                      <span className="font-semibold text-red-600">{tscpResolveError}</span>
                    ) : (
                      <span className="font-semibold text-amber-700">Not resolved</span>
                    )}
                  </p>
                )}
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={tscpWarehouseOverwrite}
                    onChange={(event) => setTscpWarehouseOverwrite(event.target.checked)}
                    disabled={tscpGenerating}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  Overwrite existing warehouse assignment on selected POs
                </label>
              </div>
              {tscpError && <p className="text-xs text-red-600">{tscpError}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseTscpModal}
                disabled={tscpGenerating}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRunTscpPrecheck}
                disabled={
                  tscpGenerating ||
                  tscpPrecheckRunning ||
                  tscpPrecheckModalOpen ||
                  !tscpFilteredPoNumbers.length
                }
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {tscpPrecheckRunning ? "Checking..." : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
      {tscpPrecheckModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={handleAbortTscpPrecheck} />
          <div className="relative z-50 w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">TSCP Template Pre-Check</h2>
              <p className="mt-1 text-sm text-slate-600">
                Warehouse:{" "}
                <span className="font-semibold text-slate-900">
                  {tscpPrecheckWarehouseLabel || "Unknown"}
                </span>
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              {tscpPrecheckResult && (
                <div
                  className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
                    tscpPrecheckStatus === "fail"
                      ? "border-red-200 bg-red-50"
                      : "border-emerald-200 bg-emerald-50"
                  }`}
                >
                  {tscpPrecheckStatus === "fail" ? (
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                  )}
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {tscpPrecheckMappingErrors.length
                        ? "Inventory check blocked by missing PO identity mapping."
                        : tscpPrecheckStatus === "fail"
                          ? "Insufficient inventory to fulfill the selected POs."
                        : "Inventory check passed. You can proceed to generate the template."}
                    </p>
                    <p className="text-xs text-slate-600">
                      Inventory availability is based on the inventory source shown below.
                    </p>
                  </div>
                </div>
              )}
              {(tscpPrecheckInventorySourceLabel || tscpPrecheckSnapshotMessage) && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Inventory Source
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{tscpPrecheckInventorySourceLabel}</p>
                  {tscpPrecheckSnapshotLabel && (
                    <p className="mt-1 text-xs text-slate-600">
                      Snapshot generated: {tscpPrecheckSnapshotLabel}
                      {tscpPrecheckSnapshotAge ? ` (age ${tscpPrecheckSnapshotAge})` : ""}
                    </p>
                  )}
                  {tscpPrecheckSnapshotMessage && (
                    <p className="mt-1 text-xs font-medium text-slate-600">{tscpPrecheckSnapshotMessage}</p>
                  )}
                </div>
              )}
              {tscpPrecheckSnapshotIsStale && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
                  <p>
                    This result is based on an older snapshot.
                    {tscpPrecheckBackgroundRefreshStarted
                      ? " A background refresh has been started."
                      : " A background refresh may still be pending."}
                  </p>
                </div>
              )}
              {!!tscpPrecheckMappingErrors.length && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 text-red-600" />
                  <p>
                    Inventory was not calculated for {tscpPrecheckMappingErrors.length} row group(s) because Cust
                    Code, Product Code, or Retail SKU mapping is missing. Resolve the mapping before continuing.
                  </p>
                </div>
              )}
              {!!tscpPrecheckNoApproved.length && (
                <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-slate-700">
                  <Info className="mt-0.5 h-4 w-4 text-blue-600" />
                  <p>
                    Some selected POs have no approved lines. Their quantities are treated as 0 in this check:{" "}
                    <span className="font-semibold text-slate-900">{tscpPrecheckNoApproved.join(", ")}</span>.
                  </p>
                </div>
              )}
              {tscpPrecheckSummaryRows.length ? (
                <div className="max-h-72 overflow-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Cust Code</th>
                        <th className="px-3 py-2 text-left">Product Code</th>
                        <th className="px-3 py-2 text-left">Retail SKU</th>
                        <th className="px-3 py-2 text-right">Net Avail</th>
                        <th className="px-3 py-2 text-right">PO Qty</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {tscpPrecheckSummaryRows.map((row, index) => {
                        const totalNumeric = Number(row?.total ?? 0);
                        const totalIsNegative = Number.isFinite(totalNumeric) && totalNumeric < 0;
                        return (
                          <tr key={`${row?.cust_code || "cust"}-${row?.product_code || "prod"}-${row?.retail_sku || "sku"}-${index}`}>
                            <td className="px-3 py-2 font-medium text-slate-900">{row?.cust_code || "—"}</td>
                            <td className="px-3 py-2 text-slate-900">{row?.product_code || "—"}</td>
                            <td className="px-3 py-2 text-slate-700">{row?.retail_sku || "—"}</td>
                            <td className="px-3 py-2 text-right text-slate-900">
                              {row?.mapping_error ? "Mapping missing" : formatPrecheckNumber(row?.net_avail ?? 0)}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-900">
                              {formatPrecheckNumber(row?.po_qty ?? 0)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">
                              <span
                                className={`inline-flex rounded px-2 py-0.5 ${
                                  totalIsNegative ? "bg-red-50 text-red-700" : "text-slate-900"
                                }`}
                              >
                                {row?.mapping_error ? "Not calculated" : formatPrecheckNumber(row?.total ?? 0)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  No approved SKU lines were found for the selected POs. You can continue to generate the template if
                  needed.
                </p>
              )}
              {tscpError && <p className="text-sm text-red-600">{tscpError}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={handleAbortTscpPrecheck}
                disabled={tscpGenerating}
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Abort
              </button>
              <button
                type="button"
                onClick={handleConfirmTscpPrecheck}
                disabled={tscpGenerating || tscpPrecheckMappingErrors.length > 0}
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {tscpGenerating ? "Generating..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
      {holdModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseHoldModal} />
          <div className="relative z-50 w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Hold POs</h2>
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                onClick={handleCloseHoldModal}
              >
                Close
              </button>
            </div>
            <div className="mt-4 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Active Conditions</h3>
                <div className="mt-2 max-h-72 overflow-auto rounded border border-slate-200">
                  {loadingHoldRules ? (
                    <p className="px-3 py-2 text-xs text-slate-500">Loading hold rules...</p>
                  ) : holdRules.length ? (
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-2 py-1 text-left">Field</th>
                          <th className="px-2 py-1 text-left">Operator</th>
                          <th className="px-2 py-1 text-left">Value</th>
                          <th className="px-2 py-1 text-left">Apply to same PO?</th>
                          <th className="px-2 py-1 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holdRules.map((rule) => (
                          <tr key={rule.id} className="odd:bg-white even:bg-slate-50">
                            <td className="px-2 py-1 text-slate-700">{rule.label || rule.columnKey}</td>
                            <td className="px-2 py-1 text-slate-700">{rule.operator}</td>
                            <td className="px-2 py-1 text-slate-600">{rule.value || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{rule.applySamePo ? "Yes" : "No"}</td>
                            <td className="px-2 py-1 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteHoldRule(rule.id)}
                                disabled={deletingHoldRuleId === rule.id}
                                className="rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {deletingHoldRuleId === rule.id ? "Removing..." : "Remove"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="px-3 py-2 text-xs text-slate-500">No hold rules configured.</p>
                  )}
                </div>
                {holdRulesError && <p className="mt-2 text-xs text-red-600">{holdRulesError}</p>}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Add Condition</h3>
                <div className="mt-2 space-y-3">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Field
                    <select
                      value={holdForm.columnKey}
                      onChange={(event) => {
                        const columnKey = event.target.value;
                        const field = holdFields.find((item) => item.columnKey === columnKey);
                        setHoldForm((prev) => ({
                          ...prev,
                          columnKey,
                          label: field?.label || "",
                          type: field?.type || "text",
                          operator: field?.type === "text" ? "=" : prev.operator,
                          value: "",
                        }));
                      }}
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {holdFields.map((field) => (
                        <option key={field.columnKey} value={field.columnKey}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Operator
                    <select
                      value={holdForm.operator}
                      onChange={(event) => setHoldForm((prev) => ({ ...prev, operator: event.target.value }))}
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={!currentHoldField || currentHoldField.type === "text"}
                    >
                      {(currentHoldField?.type === "text" ? ["="] : ["=", ">", "<"]).map((operator) => (
                        <option key={operator} value={operator}>
                          {operator}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Value
                    <div className="mt-1">
                      {currentHoldField?.type === "date" ? (
                        <input
                          type="date"
                          value={toISODateInputValue(holdForm.value)}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : currentHoldField?.type === "number" ? (
                        <input
                          type="number"
                          value={holdForm.value}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : currentHoldField?.type === "text" &&
                        (holdFieldValueOptions[currentHoldField.columnKey] || []).length ? (
                        <select
                          value={holdForm.value}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select value</option>
                          {holdFieldValueOptions[currentHoldField.columnKey].map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={holdForm.value}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter value"
                        />
                      )}
                    </div>
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <input
                      type="checkbox"
                      checked={holdForm.applySamePo}
                      onChange={(event) => setHoldForm((prev) => ({ ...prev, applySamePo: event.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    Apply to all line items with the same PO#?
                  </label>
                </div>
                {holdFormError && <p className="mt-2 text-xs text-red-600">{holdFormError}</p>}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                    onClick={handleCloseHoldModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveHoldRule}
                    disabled={savingHoldRule || !currentHoldField}
                    className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingHoldRule ? "Saving..." : "Save Rule"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ManualCloseFollowupDialog
        open={cancelFollowupCloseState.open}
        status={cancelFollowupCloseState.status}
        reason={cancelFollowupCloseState.reason}
        error={cancelFollowupCloseState.error}
        saving={
          cancelFollowupSaving && cancelFollowupProgressAction.loading === "manual_close"
        }
        onStatusChange={(status) =>
          setCancelFollowupCloseState((prev) => ({ ...prev, status, error: "" }))
        }
        onReasonChange={(reason) =>
          setCancelFollowupCloseState((prev) => ({ ...prev, reason, error: "" }))
        }
        onCancel={() =>
          setCancelFollowupCloseState((prev) => ({ ...prev, open: false, error: "" }))
        }
        onConfirm={handleCancelFollowupManualClose}
      />
      {cancelFollowupReview.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeCancelFollowupReview} />
          <div
            role="dialog"
            aria-modal="true"
            className="relative z-10 w-full max-w-5xl rounded-2xl bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {cancelFollowupReview.action === "cancel" ? "Review iLS Cancellation Request" : "Review Update iLS"}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Current linked Routing Status rows were refreshed for final review before sending the iLS action.
                </p>
              </div>
              <button
                type="button"
                onClick={closeCancelFollowupReview}
                disabled={Boolean(cancelFollowupIlsAction.loading)}
                className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Close
              </button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <p><span className="font-semibold text-slate-800">PO:</span> {cancelFollowupReview.followup?.poNumber || "-"}</p>
                <p className="mt-1"><span className="font-semibold text-slate-800">Line:</span> {cancelFollowupReview.followup?.lineNumber || "-"}</p>
                <p className="mt-1"><span className="font-semibold text-slate-800">Snapshot captured at:</span> {formatPrecheckTimestamp(cancelFollowupReview.followup?.snapshotCapturedAt || cancelFollowupReview.followup?.createdAt) || "-"}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <p><span className="font-semibold text-slate-800">Current rows refreshed at:</span> {formatPrecheckTimestamp(cancelFollowupReview.refreshedAt) || "-"}</p>
                <p className="mt-1"><span className="font-semibold text-slate-800">Current Routing Status:</span> {cancelFollowupReview.action === "cancel"
                  ? cancelFollowupReview.followup?.originLiveState?.currentRoutingStatus || "-"
                  : cancelFollowupReview.followup?.currentRoutingStatus || "-"}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-semibold text-slate-800">Current WMS:</span>
                  <WmsBadge value={cancelFollowupReview.action === "cancel"
                    ? cancelFollowupReview.followup?.originLiveState?.currentWmsStatus
                    : cancelFollowupReview.followup?.currentWms} />
                </div>
              </div>
            </div>
            {cancelFollowupReview.error ? (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {cancelFollowupReview.error}
              </div>
            ) : null}
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Linked Routing Rows
              </div>
              <div className="max-h-[360px] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Outbound</th>
                      <th className="px-3 py-2 text-left">Load ID</th>
                      <th className="px-3 py-2 text-left">Snapshot WMS</th>
                      <th className="px-3 py-2 text-left">Current Routing Status</th>
                      <th className="px-3 py-2 text-left">Current WMS</th>
                      <th className="px-3 py-2 text-left">Ready</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cancelFollowupReview.action === "cancel"
                      ? [
                          {
                            id: "origin-live-state",
                            outboundCode:
                              cancelFollowupReview.followup?.originLiveState?.outboundCode ||
                              cancelFollowupReview.followup?.outboundCode,
                            loadId: cancelFollowupReview.followup?.originLiveState?.currentLoadId,
                            wms: cancelFollowupReview.followup?.wms,
                            currentStatus: cancelFollowupReview.followup?.originLiveState?.currentRoutingStatus,
                            currentWms: cancelFollowupReview.followup?.originLiveState?.currentWmsStatus,
                            canCancelIls: cancelFollowupReview.followup?.ilsAction?.canCancel,
                            cancelDisabledReason: cancelFollowupReview.followup?.ilsAction?.cancelDisabledReason,
                          },
                        ]
                      : Array.isArray(cancelFollowupReview.followup?.routingLinks)
                        ? cancelFollowupReview.followup.routingLinks
                        : []
                    ).map((link) => {
                      const isReady =
                        cancelFollowupReview.action === "cancel" ? Boolean(link?.canCancelIls) : Boolean(link?.canUpdateIls);
                      const reason =
                        cancelFollowupReview.action === "cancel"
                          ? link?.cancelDisabledReason || ""
                          : link?.updateDisabledReason || "";
                      return (
                        <tr key={`review-${link.id}`} className="border-t border-slate-100 text-slate-700">
                          <td className="px-3 py-2">
                            {cancelFollowupReview.action === "cancel"
                              ? link.outboundCode || "-"
                              : link.currentOutboundCode || link.outboundCode || "-"}
                          </td>
                          <td className="px-3 py-2">{link.currentLoadId || link.loadId || "-"}</td>
                          <td className="px-3 py-2">
                            <WmsBadge value={link.wms} />
                          </td>
                          <td className="px-3 py-2">{link.currentStatus || "-"}</td>
                          <td className="px-3 py-2">
                            <WmsBadge value={link.currentWms} />
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                isReady ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                              }`}
                            >
                              {isReady ? "Ready" : "Blocked"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-500">{reason || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Snapshot fields above show what was captured when the follow-up was created. Current fields were refreshed just before this review.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={closeCancelFollowupReview}
                  disabled={Boolean(cancelFollowupIlsAction.loading)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={confirmCancelFollowupReview}
                  disabled={
                    cancelFollowupReview.refreshing ||
                    Boolean(cancelFollowupIlsAction.loading) ||
                    !(
                      cancelFollowupReview.action === "cancel"
                        ? cancelFollowupReview.followup?.ilsAction?.canCancel
                        : cancelFollowupReview.followup?.ilsAction?.canUpdate
                    )
                  }
                >
                  {cancelFollowupReview.refreshing
                    ? "Refreshing..."
                    : cancelFollowupIlsAction.loading === "update"
                      ? "Updating..."
                        : cancelFollowupIlsAction.loading === "cancel"
                        ? "Sending..."
                        : cancelFollowupReview.action === "cancel"
                          ? "Confirm Send Cancellation Request"
                          : "Confirm Update iLS"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-indigo-700">{pageLabel}</h1>
          <p className="text-sm text-slate-500">
            Import the latest PO details workbook or CSV, then filter, sort, and resize columns as needed.
          </p>
          {sandbox && (
            <div className="mt-2 w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
              {sandboxBanner}
            </div>
          )}
          {fileMeta && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Workbook:</span>
              <span className="max-w-[200px] truncate" title={fileMeta.name}>{fileMeta.name}</span>
              <span className="h-3 w-px bg-slate-300" />
              <span className="font-semibold text-slate-700">Sheet:</span>
              <span>{fileMeta.sheetName}</span>
              <span className="h-3 w-px bg-slate-300" />
              <span className="font-semibold text-slate-700">Loaded:</span>
              <span>{fileMeta.updatedAt.toLocaleString()}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <UserAccessSummary />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleTriggerUpload}
              disabled={importDisabled || saving}
              title={importDisabled ? "Import disabled for your role" : saving ? "Import disabled while saving" : ""}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Import
            </button>
            <button
              type="button"
              onClick={handleUndoImport}
              disabled={!canUndo || saving}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              title={canUndo ? "Revert to the dataset before the last import" : "Import first to enable undo"}
            >
              Undo Import
            </button>
            <button
              type="button"
              onClick={handleDeleteSelected}
              disabled={selectedRows.size === 0 || saving || !dataset}
              className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              title={
                selectedRows.size
                  ? `Delete ${selectedRows.size} selected ${selectedRows.size === 1 ? "row" : "rows"}`
                  : "Select rows to enable delete"
              }
            >
              Delete Selected
            </button>
            <button
              type="button"
              onClick={handleOpenTscpModal}
              disabled={selectedPoNumbers.length === 0}
              className="rounded-md border border-indigo-200 px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              title={
                selectedPoNumbers.length ? "Generate a TSCP Template for the selected POs" : "Select PO rows first"
              }
            >
              {TSCP_TEMPLATE_BUTTON_LABEL}
            </button>
            <button
              type="button"
              onClick={handleOpenWarehouseAssignModal}
              disabled={sandbox || selectedPoNumbers.length === 0 || warehousesLoading || !warehouseOptions.length}
              className="rounded-md border border-teal-200 px-3 py-2 text-sm font-medium text-teal-700 shadow-sm hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              title={
                selectedPoNumbers.length
                  ? "Apply a warehouse assignment to selected POs"
                  : "Select PO rows first"
              }
            >
              Set Warehouse
            </button>
            <button
              type="button"
              onClick={handleOpenHoldModal}
              className="rounded-md border border-amber-200 px-3 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
            >
              Hold POs
            </button>
            <MultiSelectDropdown
              values={visibleColumnKeys || []}
              options={columnOptions}
              onChange={handleVisibleColumnsChange}
              placeholder="Columns"
              buttonClassName="min-w-[150px] text-xs"
              disabled={!dataset?.headers?.length}
            />
            <button
              type="button"
              onClick={resetColumnWidths}
              disabled={!dataset?.headers}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Reset Widths
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={!dataset?.headers || !sortedRows.length}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              title={
                !dataset?.headers
                  ? "Import data before exporting"
                  : !sortedRows.length
                  ? "No rows match the current filters"
                  : "Export the current filtered dataset to Excel"
              }
            >
              Export
            </button>
            {importDisabled && (
              <span className="text-xs text-slate-500">Read only - import disabled</span>
            )}
            {!importDisabled && saving && (
              <span className="text-xs text-slate-500">Saving...</span>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {importWarning && (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {importWarning}
        </div>
      )}
      {tscpGuardrailError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {tscpGuardrailError}
        </div>
      )}

      {showCancelFollowupBanner && (
        <button
          type="button"
          onClick={handleCancelFollowupBannerClick}
          className="mb-3 flex w-full items-start gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-left text-sm text-rose-800 transition hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
          title="Filter PO Details to unresolved cancelled follow-up POs"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-500" />
          <div>
            <p className="font-semibold text-rose-900">Cancelled follow-up required</p>
            <p className="mt-1">
              {unresolvedCancelFollowupCount} unresolved cancelled line
              {unresolvedCancelFollowupCount === 1 ? "" : "s"} across {unresolvedCancelFollowupPoCount} PO
              {unresolvedCancelFollowupPoCount === 1 ? "" : "s"}. Click to filter those PO numbers, including
              non-cancelled lines under the same PO.
            </p>
          </div>
        </button>
      )}

      {showMissingPoBanner && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>
            Found {poFilterTokens.length - missingPoNumbers.length} of {poFilterTokens.length} PO numbers. Not found:{" "}
            {missingPoNumbers.join(", ")}
          </p>
          <button
            type="button"
            onClick={() => setMissingPoBannerDismissed(true)}
            className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded border border-amber-200 text-amber-700 hover:bg-amber-100"
            aria-label="Dismiss"
            title="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {showPricingBanner && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            pricingError ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-slate-50 text-slate-700"
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-800">{pricingBannerText}</span>
            {pricingLoading && <span className="text-xs text-slate-500">Refreshing...</span>}
          </div>
          {pricingError && <p className="mt-1 text-xs text-red-600">Pricing validation failed: {pricingError}</p>}
        </div>
      )}

      {dataset ? (
        <>
          <div
            ref={tableContainerRef}
            className="flex-1 overflow-auto rounded-lg border border-slate-300 shadow-md"
            onMouseDown={(event) => {
              const target = event.target;
              if (target && target.closest && target.closest(".po-copy-source")) {
                selectionModeRef.current = "PO_ONLY";
                return;
              }
              selectionModeRef.current = "NORMAL";
            }}
          >
            <table className="min-w-full border-collapse text-sm">
              <thead className="sticky top-0 z-30">
                <tr className="bg-indigo-900 text-indigo-50 text-xs uppercase tracking-wide">
                  <th
                    className="sticky top-0 bg-indigo-900 px-2 py-2 text-center font-semibold z-40"
                    style={{ width: selectColumnWidth, minWidth: selectColumnWidth, left: 0 }}
                  >
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      onChange={handleToggleSelectAll}
                      checked={allVisibleSelected && selectableRowIds.length > 0}
                      disabled={selectableRowIds.length === 0}
                    />
                    <span
                      title="Drag to resize"
                      className="ml-1 inline-flex h-6 w-2 cursor-col-resize select-none rounded bg-white/10 hover:bg-white/70"
                      onMouseDown={(event) => onDragStart(event, SELECT_COLUMN_KEY)}
                    />
                  </th>
                  {visibleHeaders.map((header) => {
                    const stickyLeft = stickyOffsets[header.key];
                    return (
                      <th
                        key={header.key}
                        style={{
                          width: widths[header.key],
                          minWidth: widths[header.key],
                          left: stickyLeft !== undefined ? stickyLeft : undefined,
                        }}
                        className={`sticky top-0 bg-indigo-900 px-4 py-2 text-left font-semibold ${
                          stickyLeft !== undefined ? "z-40 shadow-md" : ""
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleSort(header.key)}
                            className="flex items-center gap-2 text-left focus:outline-none"
                          >
                            <span className="block leading-tight">{renderHeaderLabel(header)}</span>
                            <span className="inline-flex h-5 w-5 items-center justify-center">
                              {sortState.key === header.key ? (
                                sortState.direction === 1 ? (
                                  <ChevronUp className="h-4 w-4 text-white" aria-hidden="true" />
                                ) : sortState.direction === -1 ? (
                                  <ChevronDown className="h-4 w-4 text-white" aria-hidden="true" />
                                ) : (
                                  <ChevronsUpDown className="h-4 w-4 text-slate-300" aria-hidden="true" />
                                )
                              ) : (
                                <ChevronsUpDown className="h-4 w-4 text-slate-300" aria-hidden="true" />
                              )}
                            </span>
                          </button>
                          <span
                            title="Drag to resize"
                            className="ml-1 inline-flex h-6 w-2 cursor-col-resize select-none rounded bg-white/10 hover:bg-white/70"
                            onMouseDown={(event) => onDragStart(event, header.key)}
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="bg-indigo-50 text-xs text-indigo-700">
                  <th
                    className="sticky top-[40px] bg-indigo-50 px-2 py-2 text-center z-30"
                    style={{ width: selectColumnWidth, minWidth: selectColumnWidth, left: 0 }}
                  >
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      disabled={!dataset?.headers}
                      title="Reset filters"
                      className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full border transition ${
                        hasActiveFilters
                          ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                          : "border-slate-300 bg-slate-200 text-slate-600 hover:bg-slate-300"
                      } ${!dataset?.headers ? "cursor-not-allowed opacity-60" : ""}`}
                    >
                      <RotateCw className="h-4 w-4" />
                    </button>
                  </th>
                  {visibleHeaders.map((header) => {
                    const widthStyle = { width: widths[header.key], minWidth: widths[header.key] };
                    const mode = getFilterModeForHeader(header);
                    const stickyLeft = stickyOffsets[header.key];
                    const filterClass = `sticky top-[40px] bg-indigo-50 px-3 py-2 align-top ${
                      stickyLeft !== undefined ? "z-30" : ""
                    }`;
                    const filterStyle = {
                      ...widthStyle,
                      left: stickyLeft !== undefined ? stickyLeft : undefined,
                    };
                    if (mode === FILTER_MODES.date) {
                      const currentValue = filters[header.key] || { from: "", to: "" };
                      const pickerValue = {
                        from: toISODateInputValue(currentValue.from),
                        to: toISODateInputValue(currentValue.to),
                      };
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <DateRangeFilter
                            value={pickerValue}
                            onChange={({ from, to }) =>
                              setDateRangeFilter(header.key, { from: from || "", to: to || "" })
                            }
                            placeholder="Select range"
                          />
                        </th>
                      );
                    }
                    if (mode === FILTER_MODES.multiSelect) {
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <MultiSelectDropdown
                            values={filters[header.key] || []}
                            options={multiSelectOptions[header.key] || []}
                            onChange={(nextValues) => updateFilter(header.key, nextValues)}
                          />
                        </th>
                      );
                    }
                    return (
                      <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                        <input
                          type="text"
                          placeholder={mode === FILTER_MODES.multiText ? "PO# (space/comma separated)" : "Search"}
                          value={filters[header.key] || ""}
                          onChange={(event) => updateFilter(header.key, event.target.value)}
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pagedRows.length ? (
                  pagedRows.map((record) => {
                    const statusValue = record[STATUS_KEY];
                    const normalizedStatus = String(statusValue || "").trim().toLowerCase();
                    const statusClass = STATUS_ROW_CLASSES[statusValue] || "";
                    const lineStatusValue = lineStatusKey
                      ? String(record[lineStatusKey] ?? "").trim().toLowerCase()
                      : "";
                    const highlightCancelled = lineStatusValue === "cancelled" && normalizedStatus !== "closed";
                    const poDetailId = extractPoDetailId(record);
                    const cancelFollowup = poDetailId ? cancelFollowupsByPoDetailId[poDetailId] : null;
                    const cancelFollowupStatus = normalizeCancelFollowupStatus(cancelFollowup?.status);
                    const hasActiveCancelFollowup = ACTIVE_CANCEL_FOLLOWUP_STATUSES.has(cancelFollowupStatus);
                    const isCancelFollowupExpanded =
                      Boolean(cancelFollowup?.id) && cancelFollowupPanel.followupId === cancelFollowup.id;
                    const panelFollowup = isCancelFollowupExpanded
                      ? cancelFollowupPanel.followup || cancelFollowup
                      : cancelFollowup;
                    const currentRowLineStatus = firstText(
                      lineStatusKey ? record[lineStatusKey] : "",
                      panelFollowup?.currentLineStatus,
                      panelFollowup?.lineStatus
                    );
                    const historicalCancelledDate = firstText(
                      panelFollowup?.cancelledDate,
                      cancelFollowup?.cancelledDate
                    );
                    const correctionPreview = isCancelFollowupExpanded ? cancelFollowupCorrection.preview : null;
                    const correctionPreviewRows = asArray(correctionPreview?.previewRows);
                    const correctionPreviewDescription = describeCorrectionPreview(correctionPreview);
                    const correctionPreviewEligible = correctionPreviewDescription.eligible;
                    const correctionPreviewReplacementNotRequired =
                      correctionPreviewDescription.replacementNotRequired;
                    const correctionActivation = isCancelFollowupExpanded ? cancelFollowupCorrection.activation : null;
                    const correctionActivationRows = [
                      ...asArray(correctionActivation?.activated),
                      ...asArray(correctionActivation?.alreadyActivated),
                    ];
                    const correctionOrchestration = isCancelFollowupExpanded ? cancelFollowupCorrection.orchestration : null;
                    const persistedRoutingCorrection = panelFollowup?.routingCorrection || null;
                    const correctionOrchestrationResults = asArray(correctionOrchestration?.results);
                    const correctionActivated = Boolean(
                      correctionActivationRows.length > 0 ||
                        persistedRoutingCorrection?.replacementRoutingStatusId ||
                        persistedRoutingCorrection?.replacementRoutingUid
                    );
                    const replacementOutboundCode = firstText(
                      ...correctionOrchestrationResults.map((entry) =>
                        firstText(
                          entry?.newOutboundCode,
                          entry?.correction?.replacementOutboundCode
                        )
                      ),
                      persistedRoutingCorrection?.replacementOutboundCode
                    );
                    const replacementOutboundCreated = Boolean(replacementOutboundCode);
                    const correctionProgressExists = Boolean(
                      correctionPreview ||
                        persistedRoutingCorrection?.id ||
                        correctionActivationRows.length ||
                        correctionOrchestrationResults.length
                    );
                    const correctionOriginalCustomerOrder = firstText(
                      correctionPreview?.originalCustomerOrder,
                      correctionPreviewRows[0]?.originalCustomerOrder
                    );
                    const correctionReplacementCustomerOrder = firstText(
                      correctionPreview?.replacementCustomerOrder,
                      correctionPreviewRows[0]?.replacementCustomerOrder
                    );
                    const originLiveState = panelFollowup?.originLiveState || {};
                    const replacementLiveState = panelFollowup?.replacementLiveState || {};
                    const panelWorkflowMeta = isCancelFollowupExpanded
                      ? cancelFollowupPanel.workflowMeta
                      : parseCancelFollowupNotes(panelFollowup?.notes || "").workflowMeta;
                    const followupPath = resolveCancelFollowupPath(panelFollowup);
                    const originLiveWmsKey = normalizeWmsStatusKey(originLiveState?.currentWmsStatus);
                    const originCancellationRequested = CANCEL_FOLLOWUP_REQUESTED_WMS_KEYS.has(originLiveWmsKey);
                    const originCancelledConfirmed = originLiveWmsKey === "CANCELLED";
                    const notifyCarrierStep = getCancelFollowupWorkflowStep(panelWorkflowMeta, "notifyCarrier");
                    const carrierCancelledStep = getCancelFollowupWorkflowStep(panelWorkflowMeta, "carrierCancelled");
                    const notifyManagerStep = getCancelFollowupWorkflowStep(panelWorkflowMeta, "notifyManager");
                    const lineApprovedDone =
                      String(currentRowLineStatus || "").trim().toUpperCase() === "APPROVED";
                    const followupResolved = ["DONE", "NOT_REQUIRED"].includes(
                      normalizeCancelFollowupStatus(panelFollowup?.status)
                    );
                    const previewStepCompleted = Boolean(
                      correctionProgressExists ||
                        replacementOutboundCreated ||
                        originCancellationRequested ||
                        originCancelledConfirmed ||
                        notifyCarrierStep ||
                        carrierCancelledStep ||
                        notifyManagerStep ||
                        lineApprovedDone ||
                        followupResolved
                    );
                    const createReplacementStepCompleted = Boolean(
                      replacementOutboundCreated ||
                        originCancellationRequested ||
                        originCancelledConfirmed ||
                        notifyCarrierStep ||
                        carrierCancelledStep ||
                        followupResolved
                    );
                    const sendCancelStepCompleted = Boolean(
                      originCancellationRequested ||
                        originCancelledConfirmed ||
                        notifyCarrierStep ||
                        carrierCancelledStep ||
                        followupResolved
                    );
                    const confirmCancelledStepCompleted = Boolean(
                      originCancelledConfirmed ||
                        notifyCarrierStep ||
                        carrierCancelledStep ||
                        followupResolved
                    );
                    const notifyCarrierStepCompleted = Boolean(
                      notifyCarrierStep || carrierCancelledStep || followupResolved
                    );
                    const carrierCancelledStepCompleted = Boolean(
                      carrierCancelledStep || followupResolved
                    );
                    const shippedNotifyManagerCompleted = Boolean(
                      notifyManagerStep || lineApprovedDone || followupResolved
                    );
                    const shippedApprovedCompleted = Boolean(lineApprovedDone || followupResolved);
                    const busyWithFollowupAction =
                      cancelFollowupSaving ||
                      Boolean(cancelFollowupIlsAction.loading) ||
                      Boolean(cancelFollowupCorrection.loading) ||
                      cancelFollowupReview.refreshing;
                    const formatStepMeta = (step) =>
                      step?.doneAt
                        ? `Confirmed ${formatPrecheckTimestamp(step.doneAt)}${
                            step?.doneBy ? ` by ${step.doneBy}` : ""
                          }`
                        : "";
                    const lineStatusUnavailable =
                      !String(currentRowLineStatus || "").trim();
                    const shippedLineStepBlockedReason =
                      !dataset || !lineStatusKey
                        ? "Current dataset does not expose a writable Line status column."
                        : lineStatusUnavailable
                          ? "Current line status is unavailable."
                          : "";
                    const currentPathNote =
                      followupPath.type === "not_shipped"
                        ? !originLiveState?.liveStateAvailable
                          ? "Original outbound live state is unresolved. Step 3 and Step 4 will wait for the original outbound status until Refresh iLS Status can verify it."
                          : replacementOutboundCreated && !replacementLiveState?.liveStateAvailable
                            ? "Replacement outbound is persisted locally, but its live Routing Status row is not resolved yet."
                            : followupPath.usedSnapshotFallback
                              ? "Current live routing state could not be resolved. The path badge is using the follow-up snapshot WMS as a fallback."
                              : ""
                        : followupPath.usedSnapshotFallback
                          ? "Current live routing state could not be resolved. The path badge is using the follow-up snapshot WMS as a fallback."
                          : !followupPath.liveAvailable
                            ? "Current live routing state is not available yet. Use Refresh iLS Status to re-check."
                            : "";
                    const shippedStepStates = resolveCancelFollowupSequentialStepStates([
                      {
                        completed: shippedNotifyManagerCompleted,
                        available: true,
                      },
                      {
                        completed: shippedApprovedCompleted,
                        available: shippedNotifyManagerCompleted,
                        blocked: Boolean(shippedLineStepBlockedReason),
                      },
                      {
                        completed: followupResolved,
                        available: shippedApprovedCompleted,
                      },
                    ]);
                    const nonShippedStepStates = resolveCancelFollowupSequentialStepStates([
                      {
                        completed: previewStepCompleted,
                        available: true,
                      },
                      {
                        completed: createReplacementStepCompleted,
                        available: previewStepCompleted,
                        blocked:
                          previewStepCompleted &&
                          Boolean(correctionPreview) &&
                          !correctionPreviewEligible &&
                          !createReplacementStepCompleted,
                      },
                      {
                        completed: sendCancelStepCompleted,
                        available: createReplacementStepCompleted,
                        blocked: createReplacementStepCompleted && !panelFollowup?.ilsAction?.canCancel,
                      },
                      {
                        completed: confirmCancelledStepCompleted,
                        available: sendCancelStepCompleted,
                        blocked: sendCancelStepCompleted && !originLiveState?.liveStateAvailable,
                      },
                      {
                        completed: notifyCarrierStepCompleted,
                        available: confirmCancelledStepCompleted,
                      },
                      {
                        completed: carrierCancelledStepCompleted,
                        available: notifyCarrierStepCompleted,
                      },
                      {
                        completed: followupResolved,
                        available: carrierCancelledStepCompleted,
                      },
                    ]);
                    const stationFlow =
                      followupPath.type === "shipped"
                        ? [
                            {
                              key: "notify_manager",
                              title: "Notify WM Replenishment Manager",
                              state: shippedStepStates[0],
                              manual: true,
                              description: "Operator confirmation that the shipped-order escalation was sent.",
                              meta: formatStepMeta(notifyManagerStep),
                              action: notifyManagerStep
                                ? null
                                : {
                                    label: "Mark Done",
                                    onClick: () =>
                                      markCancelFollowupWorkflowStepDone(
                                        "notifyManager",
                                        "WM Replenishment Manager notification was marked done."
                                      ),
                                    disabled: busyWithFollowupAction,
                                  },
                            },
                            {
                              key: "set_approved",
                              title: "Set Line Status back to Approved",
                              state: shippedStepStates[1],
                              manual: false,
                              description: "Write the current PO Details row back to Approved through the existing save path.",
                              meta: lineApprovedDone ? "Current PO line is already Approved." : "",
                              reason: lineApprovedDone ? "" : shippedLineStepBlockedReason,
                              action: lineApprovedDone
                                ? null
                                : {
                                    label:
                                      cancelFollowupProgressAction.loading === "set_approved"
                                        ? "Saving..."
                                        : "Set to Approved",
                                    onClick: handleCancelFollowupLineApproved,
                                    disabled:
                                      busyWithFollowupAction ||
                                      !notifyManagerStep ||
                                      Boolean(shippedLineStepBlockedReason),
                                  },
                            },
                            {
                              key: "close",
                              title: "Close",
                              state: shippedStepStates[2],
                              manual: false,
                              description: "Close the follow-up after the shipped-order recovery steps are confirmed.",
                              meta:
                                followupResolved && panelWorkflowMeta.manualCloseReason
                                  ? `Manual close reason: ${panelWorkflowMeta.manualCloseReason}`
                                  : "",
                              action: followupResolved
                                ? null
                                : {
                                    label:
                                      cancelFollowupProgressAction.loading === "close_followup"
                                        ? "Closing..."
                                        : "Close Follow-up",
                                    onClick: handleCancelFollowupNormalClose,
                                    disabled: busyWithFollowupAction || !shippedApprovedCompleted,
                                  },
                            },
                          ]
                        : correctionPreviewReplacementNotRequired
                          ? [
                              {
                                key: "preview",
                                title: "Preview",
                                state: "completed",
                                manual: false,
                                description: "Review the cancellation context against the current outbound allocation.",
                                meta: "System preview is complete.",
                                action: null,
                              },
                              {
                                key: "no_replacement",
                                title: "No Replacement Outbound Required",
                                state: "completed",
                                manual: false,
                                description: correctionPreviewDescription.message,
                                meta: "Do not create or cancel an iLS outbound for this result. Manual Close remains a separate follow-up action and does not release correction protection.",
                                action: null,
                              },
                            ]
                          : [
                            {
                              key: "preview",
                              title: "Preview",
                              state: nonShippedStepStates[0],
                              manual: false,
                              description: "Review the cancellation context before preparing and creating the replacement outbound.",
                              meta: correctionPreview
                                ? correctionPreviewEligible
                                  ? "System preview is ready."
                                  : "System preview returned blockers or warnings."
                                : persistedRoutingCorrection?.id
                                  ? "A persisted correction record already exists for this follow-up."
                                  : "",
                              action:
                                replacementOutboundCreated || originCancellationRequested || originCancelledConfirmed
                                  ? null
                                  : {
                                      label:
                                        cancelFollowupCorrection.loading === "preview"
                                            ? "Previewing..."
                                            : "Preview",
                                        onClick: () => runCancelFollowupCorrectionPreview(panelFollowup),
                                        disabled: busyWithFollowupAction,
                                      },
                            },
                            {
                              key: "create_replacement",
                              title: "Create New Outbound in iLS",
                              state: nonShippedStepStates[1],
                              manual: false,
                              description:
                                "Activate the replacement routing if needed, then create the new outbound in iLS. The old outbound is cancelled only after the new outbound code is persisted locally.",
                              meta: replacementOutboundCreated
                                ? `Replacement outbound ${replacementOutboundCode} is persisted locally.${replacementLiveState?.currentWmsStatus ? ` Current WMS: ${replacementLiveState.currentWmsStatus}.` : ""}`
                                : correctionActivated
                                  ? "Replacement routing is ready. Create the new outbound in iLS next."
                                  : correctionPreview
                                    ? correctionPreviewEligible
                                      ? "Preview is eligible. Activate correction before the iLS create step."
                                      : "Preview blockers must be resolved before the new outbound can be created."
                                    : "",
                              reason:
                                !createReplacementStepCompleted &&
                                previewStepCompleted &&
                                Boolean(correctionPreview) &&
                                !correctionPreviewEligible
                                  ? "Preview blockers must be resolved before the replacement outbound can be created."
                                  : "",
                              action:
                                replacementOutboundCreated || originCancellationRequested || originCancelledConfirmed
                                  ? null
                                  : !correctionActivated
                                    ? {
                                        label:
                                          cancelFollowupCorrection.loading === "activate"
                                            ? "Activating..."
                                            : "Activate Correction",
                                        onClick: () => runCancelFollowupCorrectionActivate(panelFollowup),
                                        disabled:
                                          busyWithFollowupAction ||
                                          !previewStepCompleted ||
                                          !correctionPreviewEligible,
                                      }
                                    : {
                                        label:
                                          cancelFollowupCorrection.loading === "orchestrate"
                                            ? "Running..."
                                            : "Create New Outbound in iLS",
                                        onClick: () => runCancelFollowupCorrectionOrchestrate(panelFollowup),
                                        disabled: busyWithFollowupAction || !previewStepCompleted,
                                      },
                            },
                            {
                              key: "send_cancel",
                              title: "Send Cancellation Request to iLS",
                              state: nonShippedStepStates[2],
                              manual: false,
                              description: "Submit the old outbound cancellation request only after the replacement outbound has been created and persisted locally.",
                              meta:
                                originCancellationRequested && !originCancelledConfirmed
                                  ? `Original outbound live WMS is ${originLiveState?.currentWmsStatus || "in a cancel-request state"}.`
                                  : "",
                              reason:
                                !sendCancelStepCompleted && createReplacementStepCompleted && !panelFollowup?.ilsAction?.canCancel
                                  ? panelFollowup?.ilsAction?.cancelDisabledReason || "Cancellation request is unavailable."
                                  : "",
                              action:
                                originCancellationRequested
                                  ? null
                                  : {
                                      label:
                                        cancelFollowupIlsAction.loading === "cancel"
                                          ? "Sending..."
                                          : "Send Cancellation Request to iLS",
                                      onClick: () => openCancelFollowupReview("cancel"),
                                      disabled:
                                        busyWithFollowupAction ||
                                        !createReplacementStepCompleted ||
                                        !panelFollowup?.ilsAction?.canCancel,
                                    },
                            },
                            {
                              key: "confirm_cancelled",
                              title: "Confirm Cancelled in iLS",
                              state: nonShippedStepStates[3],
                              manual: false,
                              description: "Refresh the original outbound live routing state and confirm WMS reaches Cancelled.",
                              meta: originCancelledConfirmed
                                ? "Original outbound live WMS is Cancelled."
                                : !originLiveState?.liveStateAvailable
                                  ? ""
                                  : "Refresh after the cancellation request until live WMS reaches Cancelled.",
                              reason:
                                !confirmCancelledStepCompleted && sendCancelStepCompleted && !originLiveState?.liveStateAvailable
                                  ? "Original outbound live state is unresolved."
                                  : !confirmCancelledStepCompleted && sendCancelStepCompleted
                                    ? "iLS status is not yet Cancelled."
                                    : "",
                              action: originCancelledConfirmed
                                ? null
                                : {
                                    label:
                                      cancelFollowupProgressAction.loading === "refresh_live"
                                        ? "Refreshing..."
                                        : "Refresh iLS Status",
                                    onClick: handleCancelFollowupRefreshLiveState,
                                    disabled: busyWithFollowupAction || !sendCancelStepCompleted,
                                  },
                            },
                            {
                              key: "notify_carrier",
                              title: "Notify Carrier",
                              state: nonShippedStepStates[4],
                              manual: true,
                              description: "Operator confirmation that carrier notification was sent.",
                              meta: formatStepMeta(notifyCarrierStep),
                              action: notifyCarrierStep
                                ? null
                                : {
                                    label: "Mark Done",
                                    onClick: () =>
                                      markCancelFollowupWorkflowStepDone(
                                        "notifyCarrier",
                                        "Carrier notification was marked done."
                                      ),
                                    disabled: busyWithFollowupAction || !confirmCancelledStepCompleted,
                                  },
                            },
                            {
                              key: "carrier_cancelled",
                              title: "Carrier Cancelled / Cases Updated",
                              state: nonShippedStepStates[5],
                              manual: true,
                              description: "Operator confirmation that the carrier and case counts were updated.",
                              meta: formatStepMeta(carrierCancelledStep),
                              action: carrierCancelledStep
                                ? null
                                : {
                                    label: "Mark Done",
                                    onClick: () =>
                                      markCancelFollowupWorkflowStepDone(
                                        "carrierCancelled",
                                        "Carrier cancellation / case update was marked done."
                                      ),
                                    disabled: busyWithFollowupAction || !notifyCarrierStep,
                                  },
                            },
                            {
                              key: "close",
                              title: "Close",
                              state: nonShippedStepStates[6],
                              manual: false,
                              description: "Close the follow-up after the cancellation workflow is complete.",
                              meta:
                                followupResolved && panelWorkflowMeta.manualCloseReason
                                  ? `Manual close reason: ${panelWorkflowMeta.manualCloseReason}`
                                  : "",
                              action: followupResolved
                                ? null
                                : {
                                    label:
                                      cancelFollowupProgressAction.loading === "close_followup"
                                        ? "Closing..."
                                        : "Close Follow-up",
                                    onClick: handleCancelFollowupNormalClose,
                                    disabled: busyWithFollowupAction || !carrierCancelledStepCompleted,
                                  },
                            },
                          ];
                    const resolvedRowBaseClass =
                      highlightCancelled
                        ? "bg-red-100 text-red-900"
                        : hasActiveCancelFollowup
                          ? "bg-rose-50 text-slate-900"
                          : statusClass || "odd:bg-white even:bg-slate-50";
                    const rowClassName = ["border-t hover:bg-blue-50", resolvedRowBaseClass]
                      .filter(Boolean)
                      .join(" ");
                    const pricingResult = poDetailId ? pricingResults[poDetailId] : null;
                    const isPricingBlocked = isPricingBlockedResult(pricingResult);
                    const pricingTooltip = pricingResult ? buildPricingTooltip(pricingResult) : "";
                    const canOverrideRow = Boolean(isPricingBlocked && canOverridePricing && poDetailId);
                    const canRevokeRow = Boolean(
                      pricingResult &&
                        pricingResult.status === "ALLOWED" &&
                        pricingResult.override_applied &&
                        canOverridePricing &&
                        poDetailId
                    );
                    return (
                      <React.Fragment key={record._id}>
                        <tr className={rowClassName}>
                        <td
                          className="sticky left-0 z-20 px-2 py-2 text-center bg-inherit"
                          style={{ width: selectColumnWidth, minWidth: selectColumnWidth }}
                          title={pricingTooltip || undefined}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {cancelFollowup ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleCancelFollowupPanel(cancelFollowup);
                                }}
                                className={`inline-flex h-5 w-5 items-center justify-center rounded transition ${
                                  hasActiveCancelFollowup
                                    ? "text-rose-600 hover:bg-rose-100 hover:text-rose-700"
                                    : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                }`}
                                title={isCancelFollowupExpanded ? "Collapse cancelled follow-up" : "Expand cancelled follow-up"}
                              >
                                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                              </button>
                            ) : (
                              <span className="inline-flex h-5 w-5 items-center justify-center opacity-0">
                                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                              </span>
                            )}
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedRows.has(record._id)}
                              onChange={() => toggleRowSelection(record._id)}
                              disabled={isPricingBlocked}
                              title={pricingTooltip || undefined}
                            />
                          </div>
                          {canOverrideRow && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openOverrideModal(poDetailId);
                              }}
                              className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 hover:text-amber-800"
                              title="Allow mismatch with reason"
                            >
                              <Stamp className="h-3 w-3" aria-hidden="true" />
                              Override
                            </button>
                          )}
                          {canRevokeRow && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openRevokeModal(poDetailId);
                              }}
                              className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 hover:text-red-800"
                              title="Revoke override with reason"
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                              Revoke
                            </button>
                          )}
                        </td>
                        {visibleHeaders.map((header) => {
                          const diffMeta = record.__diff?.[header.key];
                          const isStatusCell = header.key === STATUS_KEY;
                          const isOnHoldCell = header.key === ON_HOLD_KEY;
                          const isTscpCell = header.key === TSCP_KEY;
                          const isPoCell = header.key === poNumberKey;
                          const isCancelledDateCell =
                            canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(CANCELLED_DATE_LABEL);
                          const isCancelFollowupCell =
                            canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(CANCEL_FOLLOWUP_STATUS_LABEL);
                          const isCancelWmsCell =
                            canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(CANCEL_WMS_LABEL);
                          const cancelledDateDisplayValue = isCancelledDateCell
                            ? firstText(record[header.key], cancelFollowup?.cancelledDate)
                            : record[header.key];
                          const tooltipValue =
                            diffMeta && Object.prototype.hasOwnProperty.call(diffMeta, "previous")
                              ? diffMeta.previous ?? ""
                              : cancelledDateDisplayValue ?? "";
                          const stickyLeft = stickyOffsets[header.key];
                          const cellClass = [
                            "px-4 py-2 text-sm text-slate-700",
                            diffMeta ? CHANGED_CELL_CLASS : "",
                            stickyLeft !== undefined ? "sticky z-10 bg-inherit" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <td
                              key={`${record._id}-${header.key}`}
                              style={{
                                width: widths[header.key],
                                minWidth: widths[header.key],
                                left: stickyLeft !== undefined ? stickyLeft : undefined,
                              }}
                              className={cellClass}
                              title={tooltipValue}
                            >
                              {isStatusCell ? (
                                <StatusBadge value={record[header.key]} />
                              ) : isOnHoldCell ? (
                                <OnHoldBadge value={record[header.key]} />
                              ) : isTscpCell ? (
                                <TscpBadge value={record[header.key]} />
                              ) : isCancelFollowupCell ? (
                                <CancelFollowupBadge value={record[header.key]} />
                              ) : isCancelWmsCell ? (
                                <WmsBadge value={record[header.key]} />
                              ) : isPoCell ? (
                                <span className="po-text-select po-copy-source" data-po={record[header.key]}>
                                  {renderCellValue(record[header.key], header)}
                                </span>
                              ) : (
                                renderCellValue(cancelledDateDisplayValue, header)
                              )}
                            </td>
                          );
                        })}
                        </tr>
                        {isCancelFollowupExpanded && panelFollowup && (
                          <tr className="border-t border-rose-200">
                            <td
                              colSpan={cancelFollowupPanelColSpan}
                              className="bg-rose-50/70 px-4 py-4 align-top"
                            >
                              <div
                                className="space-y-5"
                                style={{
                                  width: "100%",
                                  maxWidth: cancelFollowupPanelWidth || "100%",
                                }}
                              >
                                <div className="rounded-xl border border-rose-200 bg-white px-4 py-4">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-500" />
                                      <div>
                                        <p className="text-base font-semibold text-slate-900">
                                          Cancelled Follow-up
                                        </p>
                                        <p className="mt-1 text-sm text-slate-600">
                                          PO {panelFollowup.poNumber || "-"}, Line {panelFollowup.lineNumber || "-"} now follows a station-based progress route for operators.
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <CancelFollowupBadge value={panelFollowup.status} />
                                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                                        {followupPath.badgeLabel}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">PO#</p>
                                      <p className="mt-1 font-semibold text-slate-900">{panelFollowup.poNumber || "-"}</p>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Line#</p>
                                      <p className="mt-1 font-semibold text-slate-900">{panelFollowup.lineNumber || "-"}</p>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Original Cancelled Date</p>
                                      <p className="mt-1 font-semibold text-slate-900">{historicalCancelledDate || "-"}</p>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current Line Status</p>
                                      <p className="mt-1 font-semibold text-slate-900">{currentRowLineStatus || "-"}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Original OB Code</p>
                                      <p className="mt-1 font-semibold text-slate-900">
                                        {originLiveState?.outboundCode || panelFollowup.outboundCode || "-"}
                                      </p>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">WMS Status when Cancelled</p>
                                      <p className="mt-1 font-semibold text-slate-900">{panelFollowup.statusWhenCancelled || "-"}</p>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current WMS</p>
                                      <div className="mt-1">
                                        <WmsBadge value={originLiveState?.currentWmsStatus} />
                                      </div>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Routing Status Now</p>
                                      <p className="mt-1 font-semibold text-slate-900">{originLiveState?.currentRoutingStatus || "-"}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Replaced OB Code</p>
                                      <p className="mt-1 font-semibold text-slate-900">
                                        {replacementLiveState?.outboundCode || replacementOutboundCode || "-"}
                                      </p>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current WMS</p>
                                      <div className="mt-1">
                                        <WmsBadge value={replacementLiveState?.currentWmsStatus} />
                                      </div>
                                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current Routing</p>
                                      <p className="mt-1 font-semibold text-slate-900">{replacementLiveState?.currentRoutingStatus || "-"}</p>
                                    </div>
                                  </div>

                                  {currentPathNote ? (
                                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                      <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                      <p>{currentPathNote}</p>
                                    </div>
                                  ) : null}
                                </div>

                                <div className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-semibold text-slate-900">Station Flow</p>
                                      <p className="mt-1 text-xs text-slate-600">
                                        Only the relevant current station exposes an action. Completed and waiting steps stay visible as route context.
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={openCancelFollowupManualClose}
                                      className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                      {followupResolved ? "View Manual Close" : "Manual Close"}
                                    </button>
                                  </div>

                                  <div className="mt-4">
                                    <FollowupStationFlow stations={stationFlow} />
                                  </div>
                                </div>

                                <CollapsibleSection
                                  title="Snapshot at Follow-up Creation"
                                  subtitle="Reference values captured when the cancelled follow-up was first created."
                                  defaultOpen={false}
                                >
                                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Snapshot Captured At</p>
                                      <p className="mt-1 font-semibold text-slate-900">
                                        {formatPrecheckTimestamp(panelFollowup.snapshotCapturedAt || panelFollowup.createdAt) || "-"}
                                      </p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">TSCP</p>
                                      <p className="mt-1 font-semibold text-slate-900">{panelFollowup.tscp || "-"}</p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Snapshot WMS</p>
                                      <div className="mt-1">
                                        <WmsBadge value={panelFollowup.wms} />
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Owner</p>
                                      <p className="mt-1 font-semibold text-slate-900">{panelFollowup.ownerEmail || "-"}</p>
                                    </div>
                                  </div>
                                  <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                                    <div className="max-h-52 overflow-auto">
                                      <table className="min-w-full text-xs">
                                        <thead className="bg-slate-50 text-slate-500">
                                          <tr>
                                            <th className="px-3 py-2 text-left">Outbound</th>
                                            <th className="px-3 py-2 text-left">Load ID</th>
                                            <th className="px-3 py-2 text-left">WMS</th>
                                            <th className="px-3 py-2 text-left">Shipped</th>
                                            <th className="px-3 py-2 text-left">Carrier PU</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {asArray(panelFollowup.routingLinks).length ? (
                                            panelFollowup.routingLinks.map((link) => (
                                              <tr key={`snapshot-${link.id}`} className="border-t border-slate-100 text-slate-700">
                                                <td className="px-3 py-2">{link.outboundCode || "-"}</td>
                                                <td className="px-3 py-2">{link.loadId || "-"}</td>
                                                <td className="px-3 py-2"><WmsBadge value={link.wms} /></td>
                                                <td className="px-3 py-2">{link.shippedDate || "-"}</td>
                                                <td className="px-3 py-2">{link.carrierPuDate || "-"}</td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan={5} className="px-3 py-4 text-center text-slate-500">No snapshot routing rows.</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </CollapsibleSection>

                                <CollapsibleSection
                                  title="Notes / Audit"
                                  subtitle="Operator notes, owner assignment, audit timestamps, and optional preview details."
                                  defaultOpen={false}
                                >
                                  <div className="space-y-4">
                                    {cancelFollowupProgressAction.message ? (
                                      <div
                                        className={`rounded border px-3 py-2 text-xs ${
                                          cancelFollowupProgressAction.tone === "success"
                                            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                            : cancelFollowupProgressAction.tone === "warning"
                                              ? "border-amber-200 bg-amber-50 text-amber-800"
                                              : "border-red-200 bg-red-50 text-red-800"
                                        }`}
                                      >
                                        {cancelFollowupProgressAction.message}
                                      </div>
                                    ) : null}
                                    {cancelFollowupIlsAction.message ? (
                                      <div
                                        className={`rounded border px-3 py-2 text-xs ${
                                          cancelFollowupIlsAction.tone === "success"
                                            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                            : cancelFollowupIlsAction.tone === "warning"
                                              ? "border-amber-200 bg-amber-50 text-amber-800"
                                              : "border-red-200 bg-red-50 text-red-800"
                                        }`}
                                      >
                                        {cancelFollowupIlsAction.message}
                                      </div>
                                    ) : null}
                                    {cancelFollowupPanel.error ? (
                                      <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                                        {cancelFollowupPanel.error}
                                      </div>
                                    ) : null}

                                    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                                      <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                          Operator Notes
                                          <textarea
                                            value={cancelFollowupPanel.notes}
                                            onChange={(event) =>
                                              setCancelFollowupPanel((prev) => ({
                                                ...prev,
                                                notes: event.target.value,
                                                error: "",
                                              }))
                                            }
                                            rows={5}
                                            disabled={cancelFollowupSaving}
                                            className="mt-1 w-full resize-none rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            placeholder="Add operator notes, exceptions, or audit context."
                                          />
                                        </label>
                                      </div>
                                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                                        <p><span className="font-semibold text-slate-900">Acknowledged:</span> {formatPrecheckTimestamp(panelFollowup.acknowledgedAt) || "-"}</p>
                                        <p><span className="font-semibold text-slate-900">Acknowledged By:</span> {panelFollowup.acknowledgedBy || "-"}</p>
                                        <p><span className="font-semibold text-slate-900">Resolved:</span> {formatPrecheckTimestamp(panelFollowup.resolvedAt) || "-"}</p>
                                        <p><span className="font-semibold text-slate-900">Resolved By:</span> {panelFollowup.resolvedBy || "-"}</p>
                                        {panelWorkflowMeta.manualCloseReason ? (
                                          <p><span className="font-semibold text-slate-900">Manual Close Reason:</span> {panelWorkflowMeta.manualCloseReason}</p>
                                        ) : null}
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                        <input
                                          type="checkbox"
                                          checked={cancelFollowupPanel.assignToMe}
                                          onChange={(event) =>
                                            setCancelFollowupPanel((prev) => ({
                                              ...prev,
                                              assignToMe: event.target.checked,
                                              error: "",
                                            }))
                                          }
                                          disabled={cancelFollowupSaving || !me?.email}
                                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        Assign to me ({me?.email || "no signed-in email"})
                                      </label>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={handleCancelFollowupNotesSave}
                                          disabled={cancelFollowupSaving}
                                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                          {cancelFollowupSaving && cancelFollowupProgressAction.loading === "save"
                                            ? "Saving..."
                                            : "Save Notes / Owner"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={closeCancelFollowupPanel}
                                          disabled={cancelFollowupSaving || Boolean(cancelFollowupIlsAction.loading)}
                                          className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                          Collapse
                                        </button>
                                      </div>
                                    </div>

                                    {(correctionPreview || correctionActivation || correctionOrchestration) && (
                                      <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                          <div>
                                            <p className="text-sm font-semibold text-slate-900">Preview / Correction Detail</p>
                                            <p className="mt-1 text-xs text-slate-600">
                                              System detail stays collapsible so the operator route remains the main focus.
                                            </p>
                                          </div>
                                          <div className="flex flex-wrap items-center gap-2">
                                            {correctionPreviewEligible && (
                                              <button
                                                type="button"
                                                onClick={() => runCancelFollowupCorrectionActivate(panelFollowup)}
                                                disabled={
                                                  cancelFollowupSaving ||
                                                  Boolean(cancelFollowupIlsAction.loading) ||
                                                  Boolean(cancelFollowupCorrection.loading) ||
                                                  correctionActivated
                                                }
                                                className="rounded border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                                              >
                                                {cancelFollowupCorrection.loading === "activate"
                                                  ? "Activating..."
                                                  : correctionActivated
                                                    ? "Activated"
                                                    : "Activate Correction"}
                                              </button>
                                            )}
                                            {correctionActivated && (
                                              <button
                                                type="button"
                                                onClick={() => runCancelFollowupCorrectionOrchestrate(panelFollowup)}
                                                disabled={
                                                  cancelFollowupSaving ||
                                                  Boolean(cancelFollowupIlsAction.loading) ||
                                                  Boolean(cancelFollowupCorrection.loading)
                                                }
                                                className="rounded border border-purple-200 bg-white px-3 py-2 text-xs font-semibold text-purple-800 hover:bg-purple-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                                              >
                                                {cancelFollowupCorrection.loading === "orchestrate"
                                                  ? "Running..."
                                                  : "Create New Outbound / Cancel Old"}
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        {cancelFollowupCorrection.message && (
                                          <div
                                            className={`rounded border px-3 py-2 text-xs ${
                                              cancelFollowupCorrection.tone === "success"
                                                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                                : cancelFollowupCorrection.tone === "warning"
                                                  ? "border-amber-200 bg-amber-50 text-amber-800"
                                                  : "border-red-200 bg-red-50 text-red-800"
                                            }`}
                                          >
                                            {cancelFollowupCorrection.message}
                                          </div>
                                        )}

                                        {correctionPreview && (
                                          <div className="space-y-3">
                                            <div className="grid gap-3 md:grid-cols-3">
                                              <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                                                <p className="font-semibold uppercase tracking-wide text-slate-500">Old Outbound</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-900">
                                                  {firstText(correctionPreviewRows[0]?.originalOutboundCode, panelFollowup?.outboundCode) || "-"}
                                                </p>
                                                <p className="mt-1 text-slate-600">Original Customer Order: {correctionOriginalCustomerOrder || "-"}</p>
                                              </div>
                                              <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                                                <p className="font-semibold uppercase tracking-wide text-slate-500">Replacement Customer Order</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-900">{correctionReplacementCustomerOrder || "-"}</p>
                                              </div>
                                              <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs">
                                                <p className="font-semibold uppercase tracking-wide text-slate-500">Preview Eligibility</p>
                                                <p className={`mt-1 text-sm font-semibold ${correctionPreviewEligible || correctionPreviewReplacementNotRequired ? "text-emerald-700" : "text-amber-700"}`}>
                                                  {correctionPreviewDescription.statusLabel}
                                                </p>
                                              </div>
                                            </div>
                                            <CorrectionMessages title="Preview blockers" items={correctionPreview.blockers} tone="error" />
                                            <CorrectionMessages title="Preview warnings" items={correctionPreview.warnings} tone="warning" />
                                            <div className="grid gap-3 md:grid-cols-2">
                                              <CorrectionAllocationTable title="Affected Cancelled SKU(s)" rows={asArray(correctionPreview.affectedSkus).map((sku) => ({ sku }))} />
                                              <CorrectionAllocationTable title="Remaining Allocation(s)" rows={correctionPreviewRows.flatMap((row) => asArray(row?.remainingAllocations))} />
                                            </div>
                                          </div>
                                        )}

                                        {correctionActivation && (
                                          <div className="space-y-3 rounded border border-emerald-200 bg-emerald-50/60 p-3">
                                            <p className="text-sm font-semibold text-emerald-900">Activation Result</p>
                                            <CorrectionMessages title="Activation blockers" items={correctionActivation.blockers} tone="error" />
                                            <CorrectionMessages title="Activation warnings" items={correctionActivation.warnings} tone="warning" />
                                            {correctionActivationRows.map((entry, index) => (
                                              <div key={entry?.correction?.id || index} className="rounded border border-emerald-200 bg-white p-3">
                                                <div className="grid gap-3 text-xs text-slate-700 md:grid-cols-2">
                                                  <div>
                                                    <p className="font-semibold uppercase tracking-wide text-slate-500">Old Routing</p>
                                                    <p className="mt-1"><span className="font-semibold">ID:</span> {entry?.oldRouting?.routingStatusId || "-"}</p>
                                                    <p><span className="font-semibold">UID:</span> {entry?.oldRouting?.routingUid || "-"}</p>
                                                  </div>
                                                  <div>
                                                    <p className="font-semibold uppercase tracking-wide text-slate-500">Replacement Routing</p>
                                                    <p className="mt-1"><span className="font-semibold">ID:</span> {entry?.newRouting?.routingStatusId || "-"}</p>
                                                    <p><span className="font-semibold">UID:</span> {entry?.newRouting?.routingUid || "-"}</p>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {correctionOrchestration && (
                                          <div className="space-y-3 rounded border border-purple-200 bg-purple-50/60 p-3">
                                            <p className="text-sm font-semibold text-purple-900">iLS Orchestration Result</p>
                                            <CorrectionMessages title="Orchestration blockers" items={correctionOrchestration.blockers} tone="error" />
                                            <CorrectionMessages title="Orchestration warnings" items={correctionOrchestration.warnings} tone="warning" />
                                            {asArray(correctionOrchestration.results).map((result, index) => (
                                              <div key={result?.correction?.id || index} className="rounded border border-purple-200 bg-white p-3 text-xs text-slate-700">
                                                <p><span className="font-semibold">Replacement Outbound:</span> {result?.newOutboundCode || result?.ilsCreateResult?.outboundCode || "-"}</p>
                                                <p className="mt-1"><span className="font-semibold">Old Outbound:</span> {result?.oldOutboundCode || result?.oldOutboundCancelResult?.outboundCode || "-"}</p>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </CollapsibleSection>
                              </div>
                            </td>
                            {cancelFollowupTrailingColSpan > 0 && (
                              <td
                                colSpan={cancelFollowupTrailingColSpan}
                                className="bg-transparent p-0 align-top"
                              />
                            )}
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={visibleHeaders.length + 1} className="px-4 py-8 text-center text-sm text-slate-500">
                      No data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(event) => {
                  const value = event.target.value;
                  setPageSize(value === "All" ? "All" : parseInt(value, 10));
                  setPage(1);
                }}
                className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="tabular-nums text-xs text-slate-500">
                {sortedRows.length === 0 ? 0 : startIdx + 1} - {endIdx} of {sortedRows.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="tabular-nums">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  aria-label="Last page"
                >
                  <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : loadingRemote ? (
        <div className="flex-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
          <p className="text-sm font-medium">Loading PO details data...</p>
          <p className="mt-1 text-xs">Fetching the latest workbook from the server.</p>
        </div>
      ) : (
        <div className="flex-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
          <p className="text-sm font-medium">No PO details data loaded yet.</p>
          <p className="mt-1 text-xs">
            Use the <span className="font-semibold text-slate-700">Import</span> button above to select a workbook or CSV file.
          </p>
        </div>
      )}

    </div>
    </>
  );
}
